import { toApiUrl } from '../../utils/apiBase';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import { callAPI, unsafeAPI } from '../../hooks/api';
import { ConfirmDialog, ConfirmDialogProps } from '../../components/ConfirmDialog';
import IdentityRecovery, { can_recover_identity, IdentityRecoveryClient } from '../../components/IdentityRecovery';
import { ConfirmInputDialog, ConfirmInputDialogProps } from '../../components/ConfirmInputDialog';
import {
	AlertTriangle,
	Copy,
	SearchIcon,
	Unlock,
	Trash,
	Search,
	Split,
	ShieldOff,
	ShieldCheck,
	CalendarSync,
	ScanFace,
	Eye,
	Upload,
	ExternalLink,
	Info,
	Plus
} from 'lucide-react';
import {
	validate_bank_account_number,
	validate_email,
	validate_identity_number,
	validate_phone_number
} from '../../utils/validate';
import { Ban, Lock, Pencil, RefreshCcw, BanknoteArrowDown } from 'lucide-react';
import { Button } from '../../shadcn/Button';
import { cn } from '../../shadcn/utils';
import { ucfirst, format_identification_with_prefix, remove_prefix_from_identification } from '../../utils/formatters';
import { Dialog } from '../../components/Dialog';
import Input from '../../components/Input';
import Select from '../../components/Select';
import TwoFactorAuth from '../../components/2FA';
import Spinner from '../../components/Spinner';
import Firebase from '../../firebase_setup';
import CustomerFinancingOverrides from './components/CustomerFinancingOverrides';

// API Response interfaces
interface APIResponse {
	success?: boolean;
	status?: string;
	data?: string;
}

interface FraudTransferForm {
	imei: string;
	identification_number_1: string;
	identification_number_2: string;
}

// Enhanced interfaces with proper typing
interface ClientData {
	user: User;
	devices: DeviceWithRelations[];
}

interface DeviceWithRelations extends Device {
	payments: Payment[];
	transactions: Transaction[];
}

interface BlacklistData {
	reason?: string | null;
	comment?: string | null;
	created_at?: string | null;
}

interface User {
	points: number;
	effective_points?: number;
	centro: string;
	welcome_message_sent: boolean;
	photo_id_url: string;
	zipcode: string;
	first_name: string;
	percent_down_approved: string;
	max_loan_approved_3months: number;
	max_loan_approved_3months_secondary: number;
	estado: string;
	direccion: string;
	push_token: string;
	identification_number: string;
	available_loan_approved: number;
	available_loan_approved_secondary: number;
	country: string;
	employment_data: EmploymentData;
	municipio: string;
	store_user: string | null;
	guarantor_name: string | null;
	created_at: string;
	identification_id_url: string;
	financial_data: FinancialData;
	interest_rate: number;
	sex: string;
	seller_store: string | null;
	ontime_payments_ids: string[];
	registration_status: string;
	identification_type?: string;
	phone_number: string;
	level: string | null;
	date_of_birth: string;
	age: number;
	user_registration_device_brand: string;
	cne_identified: string | null | boolean;
	parroquia: string;
	available_loan_approved_krece_plus: number;
	city: string;
	marital_status: string | null;
	devices_paid: string[];
	user_uuid: string;
	max_loan_approved_6months: number;
	max_loan_approved_6months_krece_plus: number;
	referral_code: string;
	education_level: string;
	last_name: string;
	is_test: boolean;
	max_loan_approved_3months_krece_plus: number;
	user_registration_device_model: string;
	late_payments_ids: string[];
	is_active: boolean;
	blacklisted?: boolean;
	blacklist_data?: BlacklistData | null;
	address: string | null;
	client_type: string | null;
	guarantor_phone_number: string | null;
	email: string;
	recargas_phone_numbers?: string[];
	odoo_partner_id?: number;
	recargas_fee_paid?: boolean;
	device_uid?: string;
	last_successful_liveness?: string;
	last_liveness_session_photo_url?: string;
	personal_loan_credit_line?: number;
	available_personal_loan_credit_line?: number;
	shared_credit_line_is_active?: boolean;
	raw_level?: string;
}

interface EmploymentData {
	monthly_income_dollars: number | string;
	current_position: string | null;
}

interface FinancialData {
	house_owner: string | null;
	simcard_payment_plan: string | null;
	rent_payment: string | null;
	bank_account: string | null;
	savings: number | string;
}

interface InsuranceActiveClaim {
	claim_doc_id?: string;
	siniestro_id?: string;
	tipo?: string;
	status?: string;
	created_at?: string;
	updated_at?: string;
}

interface InsuranceInformation {
	nuPoliza?: string | number;
	nuCertificado?: string | number;
	cdRamo?: string | number;
	cdSucursal?: string | number;
	mensaje?: string;
	created?: string;
	updated?: string;
	valid_until?: string;
	status?: string;
	is_active?: boolean;
	active_claim?: InsuranceActiveClaim;
}

const getBlacklistTooltip = (blacklistData?: BlacklistData | null) => {
	if (!blacklistData) return undefined;

	const details = [
		blacklistData.reason ? `Razón: ${blacklistData.reason}` : null,
		blacklistData.comment ? `Comentario: ${blacklistData.comment}` : null
	].filter(Boolean);

	return details.length > 0 ? details.join('\n') : undefined;
};

interface Device {
	operator?: string;
	product_description?: string;
	device_model?: string;
	sub_store: string;
	created_at: string;
	self_financed: boolean;
	device_payments_left: number;
	is_locked?: boolean;
	nuovopay_device_id: number | string;
	user: string;
	loan_payment_cycle: number;
	country: string;
	non_taxable_fees: number;
	online_purchase: boolean;
	total_interest: number;
	date_paid_by_krece: string | null;
	date_bought: string;
	device_loan_duration: number;
	payments_amount: number;
	device_manufacturer?: string;
	device_paid_by_krece: boolean;
	discount: number | null;
	payments_amount_financed: number;
	device_price: number;
	is_test: boolean;
	device_commercial_name: string;
	anexo?: string;
	taxable_fees: number;
	krece_lux?: boolean;
	seller_store: string | null;
	device_imei_no?: string;
	device_type: string;
	is_enrolled: boolean;
	store_user: string | null;
	new_chip_acquired_with_device?: boolean;
	device_management_provider?: string;
	is_released?: boolean;
	interest: number;
	amount_financed: number;
	store_financed: boolean;
	phone_range?: string;
	payment_cycle_days: number;
	device_confirmation_id: number | string;
	device_serial_no?: string;
	is_active: boolean;
	promotional_code: string | null;
	id: string;
	carta_de_saldo_sent?: boolean;
	last_payment_date?: string;
	phone_number?: string;
	approval_code?: boolean;
	is_stolen?: boolean;
	device_deleted?: boolean;
	payout_method?: 'bank_transfer' | 'cash_popular' | string;
	personal_loan_motive?: string;
	wire_reference?: string;
	bank_account_number?: string | null;
	bank_name?: string | null;
	recharge_id?: string;
	insurance_information?: InsuranceInformation | null;
}

const INSURABLE_DEVICE_TYPES = ['phone', 'tablet', 'laptop', 'iphone'];

const hasInsuranceInformation = (insurance?: InsuranceInformation | null): insurance is InsuranceInformation =>
	Boolean(insurance && Object.keys(insurance).length > 0);

const isInsurableDevice = (device: Device) => INSURABLE_DEVICE_TYPES.includes(device.device_type?.toLowerCase() || '');

const formatInsuranceValue = (value?: string | number | boolean | null) => {
	if (value === undefined || value === null || value === '') return '—';
	if (typeof value === 'boolean') return value ? 'Sí' : 'No';
	return String(value);
};

const formatInsuranceDate = (value?: string | null) => {
	if (!value) return '—';

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleDateString();
};

const isInsuranceExpired = (validUntil?: string) => {
	if (!validUntil) return false;

	const date = new Date(validUntil);
	if (Number.isNaN(date.getTime())) return false;
	if (/^\d{4}-\d{2}-\d{2}$/.test(validUntil)) date.setHours(23, 59, 59, 999);
	return date < new Date();
};

const getInsuranceStatusMeta = (insurance: InsuranceInformation) => {
	const normalizedStatus = String(insurance.status || '').toUpperCase();
	const isExpiredByDate = isInsuranceExpired(insurance.valid_until);

	if (normalizedStatus === 'ACTIVE' && !isExpiredByDate) {
		return {
			label: 'Activo',
			className: 'bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-400'
		};
	}

	if (normalizedStatus === 'EXPIRED' || isExpiredByDate) {
		return {
			label: 'Vencido',
			className: 'bg-yellow-500/10 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400'
		};
	}

	return {
		label: normalizedStatus || 'Seguro',
		className: 'bg-blue-500/10 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400'
	};
};

const renderInsuranceSummaryBadge = (device: Device) => {
	if (!isInsurableDevice(device) || !hasInsuranceInformation(device.insurance_information)) return null;

	const statusMeta = getInsuranceStatusMeta(device.insurance_information);
	return (
		<span
			title={`Seguro ${statusMeta.label}`}
			className={`ml-2 inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-medium ${statusMeta.className}`}
		>
			<ShieldCheck className="size-3.5" />
			Seguro
		</span>
	);
};

const renderDeletedDeviceBadge = (device: Device) => {
	if (!device.device_deleted) return null;

	return (
		<span
			title="Este equipo fue eliminado"
			className="ml-2 inline-flex shrink-0 items-center gap-1 rounded bg-red-500/10 px-2 py-1 text-xs font-medium text-red-700 dark:bg-red-500/20 dark:text-red-400"
		>
			<AlertTriangle className="size-3.5" />
			Eliminado
		</span>
	);
};

const renderInsuranceDetails = (device: Device) => {
	const insurance = device.insurance_information;
	if (!isInsurableDevice(device) || !hasInsuranceInformation(insurance)) return null;

	const statusMeta = getInsuranceStatusMeta(insurance);
	const policyRows = [
		{ label: 'Aseguradora', value: 'Seguros Caracas' },
		{ label: 'Número de póliza', value: formatInsuranceValue(insurance.nuPoliza) },
		{ label: 'Número de certificado', value: formatInsuranceValue(insurance.nuCertificado) },
		{ label: 'Estado', value: statusMeta.label },
		{ label: 'Vigente hasta', value: formatInsuranceDate(insurance.valid_until) },
		{ label: 'Fecha de emisión', value: formatInsuranceDate(insurance.created) },
		{ label: 'Última actualización', value: formatInsuranceDate(insurance.updated) },
		{ label: 'Ramo', value: formatInsuranceValue(insurance.cdRamo) },
		{ label: 'Sucursal', value: formatInsuranceValue(insurance.cdSucursal) },
		{ label: 'Mensaje', value: formatInsuranceValue(insurance.mensaje) }
	];

	if (insurance.is_active !== undefined) {
		policyRows.push({ label: 'Cobertura activa', value: formatInsuranceValue(insurance.is_active) });
	}

	const activeClaimRows = insurance.active_claim
		? [
				{ label: 'Siniestro', value: formatInsuranceValue(insurance.active_claim.siniestro_id) },
				{ label: 'Tipo', value: formatInsuranceValue(insurance.active_claim.tipo) },
				{ label: 'Documento', value: formatInsuranceValue(insurance.active_claim.claim_doc_id) },
				{ label: 'Creado', value: formatInsuranceDate(insurance.active_claim.created_at) },
				{ label: 'Actualizado', value: formatInsuranceDate(insurance.active_claim.updated_at) }
			]
		: [];

	return (
		<div className="mb-4 border-b border-neutral-800 pb-4">
			<div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
				<div className="flex items-center gap-3">
					<div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-500/10">
						<ShieldCheck className="h-5 w-5 text-blue-700 dark:text-blue-400" />
					</div>
					<div>
						<h4 className="font-medium text-neutral-100">Seguro del equipo</h4>
						<p className="text-sm text-neutral-400">Detalles de la póliza de Seguros Caracas</p>
					</div>
				</div>
				<span className={`w-fit rounded px-2 py-1 text-xs font-medium ${statusMeta.className}`}>{statusMeta.label}</span>
			</div>

			<div className="grid grid-cols-1 gap-1.5 text-sm md:grid-cols-2 md:gap-4">
				{policyRows.map((row) => (
					<div key={row.label} className="flex justify-between gap-4 rounded-md bg-neutral-800/30 px-3 py-2">
						<span className="shrink-0 text-neutral-300">{row.label}</span>
						<span className="break-all text-right text-neutral-100">{row.value}</span>
					</div>
				))}
			</div>

			{insurance.active_claim && (
				<div className="mt-4 rounded-lg border border-blue-500/20 bg-blue-500/10 p-3">
					<div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
						<p className="text-sm font-medium text-blue-700 dark:text-blue-400">Reclamo activo</p>
						<span className="w-fit rounded bg-blue-500/20 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300">
							{formatInsuranceValue(insurance.active_claim.status)}
						</span>
					</div>
					<div className="grid grid-cols-1 gap-1.5 text-sm md:grid-cols-2 md:gap-3">
						{activeClaimRows.map((row) => (
							<div key={row.label} className="flex justify-between gap-4 rounded bg-neutral-900/30 px-3 py-2">
								<span className="shrink-0 text-neutral-300">{row.label}</span>
								<span className="break-all text-right text-neutral-100">{row.value}</span>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

interface Payment {
	taxable_fees: number;
	krece_fixed_fee: number;
	paid: boolean;
	interest: number;
	paymentMethod: boolean;
	amountPaid: number;
	cobranza_owner: string | null;
	country: string;
	paid_to_store: boolean;
	total_interest: number;
	krece_percent_fee: number;
	confirmationNumber: string;
	created_at: string;
	sub_store: string | null;
	amount_owed_to_store: number;
	is_active: boolean;
	dueDate: string;
	user: string;
	screenshot: boolean;
	store_financed: boolean;
	actualAmount: number;
	self_financed: boolean;
	device: string;
	non_taxable_fees: number;
	related_store: string | null;
	is_test: boolean;
	checked_by_internal_team: boolean;
	paymentPostponedTimes: number;
	paymentPostponed: boolean;
	id: string;
	second_reminder_sent?: boolean;
	last_reminder_date?: string;
	in_app_message_sent_count?: number;
	amount_confirmed?: number;
	amountPaidBs?: number;
	most_recent_paid_date?: string;
	related_transaction?: string;
	manual_verification_denied?: boolean;
}

interface Transaction {
	id: string;
	user: string;
	amount: number;
	date: string;
	paid_to: string;
	related_payment?: string;
	related_store?: string | null;
	store_name?: string;
	store_user?: string | null;
	sub_store?: string | null;
	confirmation_number?: string;
	collected_by_krece?: boolean;
	is_test: boolean;
	bank_id?: string | null;
	bank_account_number?: string | null;
	bank_name?: string | null;
	reference?: string;
	reference_date?: string;
	amount_bs?: number;
}

interface PendingRecharge {
	id: string;
	amount: number;
	device_data?: { amount_financed?: number; [key: string]: unknown };
	created_at: string;
	is_active: boolean;
	operator: string;
	phone_number: string;
	status: string;
	recharge_id?: string;
	email: string;
	[key: string]: unknown;
}

interface PendingLoan {
	id: string;
	amount_usd: number;
	amount_bs: number;
	bank_account_data?: {
		bank_account_holder_name: string;
		bank_account_identification_number: string;
		bank_account_identification_type: string;
		bank_account_number: string;
		bank_account_type: string;
		bank_name: string;
	};
	user: string;
	status: string;
	is_active: boolean;
	disbursement_type: string;
	loan_duration: number;
	created_at: string;
	[key: string]: unknown;
}

// Raw API response interfaces
interface RawClientData {
	user: User;
	devices: Device[];
	payments: Payment[];
	transactions: Transaction[];
	pending_recharges?: PendingRecharge[];
	pending_loans?: PendingLoan[];
	has_more?: Record<string, boolean>;
}

export default function ManageCustomers() {
	const { internalCountry, userData, CountrySettings } = useContext(GlobalContext);
	const uidPrefix = CountrySettings?.uid_prefix || '';
	const currency_code = CountrySettings?.currency_code || '';

	const [dialogMessage, setDialogMessage] = useState('');
	const [confirmDialogProps, setConfirmDialogProps] = useState<null | ConfirmDialogProps>(null);
	const [editDialogProps, setEditDialogProps] = useState<null | ConfirmInputDialogProps>(null);
	const [twoFactorContext, setTwoFactorContext] = useState<null | {
		mode: 'email' | 'phone';
		email?: string;
		phone?: string;
		onPassed?: () => Promise<true | string>;
	}>(null);
	const [twoFactorActionLoading, setTwoFactorActionLoading] = useState(false);
	const [splitCuotaDeviceID, setSplitCuotaDeviceID] = useState<string | null>(null);
	const [splitPaymentLoading, setSplitPaymentLoading] = useState(false);
	const [fraudTransferOpen, setFraudTransferOpen] = useState(false);
	const [fraudTransferLoading, setFraudTransferLoading] = useState(false);
	const [fraudTransferError, setFraudTransferError] = useState('');
	const [fraudTransferForm, setFraudTransferForm] = useState<FraudTransferForm>({
		imei: '',
		identification_number_1: '',
		identification_number_2: ''
	});

	const [identificationValue, setIdentificationValue] = useState('');
	const [identificationType, setIdentificationType] = useState('identification_number');

	const [loading, setLoading] = useState(false);
	const [checkingDeviceStatus, setCheckingDeviceStatus] = useState(false);

	const [clientData, setClientData] = useState<ClientData | null | undefined>(undefined);
	const [identityRecoveryClient, setIdentityRecoveryClient] = useState<IdentityRecoveryClient | null>(null);
	const recoveryCountry = internalCountry.get() || userData?.country || '';
	useEffect(() => setIdentityRecoveryClient(null), [recoveryCountry, userData?.email]);
	const [unrelatedTransactions, setUnrelatedTransactions] = useState<Transaction[]>([]);
	const [livenessQrUrl, setLivenessQrUrl] = useState<string | null>(null);
	const [livenessGenerating, setLivenessGenerating] = useState(false);

	// Pagination state
	const [hasMore, setHasMore] = useState<Record<string, boolean>>({});
	const [loadingMore, setLoadingMore] = useState<string | null>(null);
	const [pendingRecharges, setPendingRecharges] = useState<PendingRecharge[]>([]);
	const [pendingLoans, setPendingLoans] = useState<PendingLoan[]>([]);
	const rawPaymentsRef = useRef<Payment[]>([]);
	const rawTransactionsRef = useRef<Transaction[]>([]);

	// Photo dialog state
	const [photoDialog, setPhotoDialog] = useState<null | { type: 'selfie' | 'documento'; currentUrl: string }>(null);
	const [photoUpdateLoading, setPhotoUpdateLoading] = useState(false);
	const [photoUploadProgress, setPhotoUploadProgress] = useState(0);
	const [selfieFile, setSelfieFile] = useState<File | null>(null);
	const [docFile, setDocFile] = useState<File | null>(null);
	const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
	const [docPreview, setDocPreview] = useState<string | null>(null);
	const [photoDocIdType, setPhotoDocIdType] = useState('');
	const [photoDocCountry, setPhotoDocCountry] = useState('');
	const [pendingDialog, setPendingDialog] = useState<'recharges' | 'loans' | null>(null);

	// Crear Recarga modal state
	const [crearRecargaOpen, setCrearRecargaOpen] = useState(false);
	const [crearRecargaLoading, setCrearRecargaLoading] = useState(false);

	// Sync Kashio state
	const [syncKashioLoading, setSyncKashioLoading] = useState(false);
	const [crearRecargaForm, setCrearRecargaForm] = useState({
		phone_number: '',
		amount: '',
		date: '',
		xid: ''
	});

	// Biometric authentication state (3 hours validity)
	const isUserBiometricallyAuthenticated = useMemo(() => {
		if (['admin', 'manager'].includes(userData?.access_type || '')) return true;

		const lastAuth = clientData?.user?.last_successful_liveness;
		if (!lastAuth) return false;

		// Convert ISO 8601 string to unix timestamp if needed
		const lastAuthTimestamp = typeof lastAuth === 'string' ? Math.floor(new Date(lastAuth).getTime() / 1000) : lastAuth;

		return Boolean(lastAuthTimestamp && Date.now() / 1000 - lastAuthTimestamp < 10800);
	}, [clientData?.user?.last_successful_liveness, userData?.access_type]);

	const canFraudTransfer = ['admin', 'manager'].includes(userData?.access_type || '');
	const activeCountry = internalCountry.get() || clientData?.user?.country || userData?.country || '';

	const openFraudTransferDialog = () => {
		setFraudTransferForm({
			imei: '',
			identification_number_1: remove_prefix_from_identification(clientData?.user?.identification_number || identificationValue),
			identification_number_2: ''
		});
		setFraudTransferError('');
		setFraudTransferOpen(true);
	};

	const closeFraudTransferDialog = () => {
		if (fraudTransferLoading) return;
		setFraudTransferOpen(false);
		setFraudTransferError('');
	};

	const validateFraudTransferIdentification = (identificationNumber: string) => {
		const value = identificationNumber.trim();
		return validate_identity_number(value, 'cedula', activeCountry) || validate_identity_number(value, 'dni', activeCountry);
	};

	const validateImageFile = (file: File): boolean => {
		const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
		if (!allowedTypes.includes(file.type)) {
			setDialogMessage('Tipo de archivo no válido. Solo se permiten imágenes JPEG, PNG o WebP.');
			return false;
		}
		if (file.size > 5 * 1024 * 1024) {
			setDialogMessage('El archivo es demasiado grande. El tamaño máximo es 5MB.');
			return false;
		}
		return true;
	};

	const uploadToFirebase = (file: File, path: string): Promise<string> => {
		return new Promise((resolve, reject) => {
			const storage = Firebase.storage();
			const storageRef = storage.ref();
			const fileRef = storageRef.child(path);
			const uploadTask = fileRef.put(file, {
				cacheControl: 'public,max-age=31536000,immutable'
			});

			uploadTask.on(
				'state_changed',
				(snapshot) => {
					const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
					setPhotoUploadProgress(progress);
				},
				(error) => {
					console.error('Upload error:', error);
					reject(error);
				},
				async () => {
					const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
					resolve(downloadURL);
				}
			);
		});
	};

	const handlePhotoUpdate = async () => {
		if (!clientData) return;
		const userId = clientData.user.email;
		const storagePath = clientData.user.user_uuid;

		if (photoDialog?.type === 'selfie') {
			if (!selfieFile) {
				setDialogMessage('Debes seleccionar una foto de selfie.');
				return;
			}
			if (!validateImageFile(selfieFile)) return;

			setPhotoUpdateLoading(true);
			try {
				const selfieUrl = await uploadToFirebase(selfieFile, `user_photos/${storagePath}/${Date.now()}_${selfieFile.name}`);
				const response = (await unsafeAPI(
					'POST',
					toApiUrl('update_seventh/'),
					{
						action_type: 'update_user_selfie',
						user_id: userId,
						user_selfie_photo: selfieUrl
					},
					userData,
					internalCountry.get()
				)) as APIResponse;

				if (response?.success || response?.status === 'success') {
					setDialogMessage('Selfie actualizado exitosamente.');
					closePhotoDialog();
				} else {
					setDialogMessage(
						response?.status?.length ? 'Error: ' + response.status : response?.data || 'Error al actualizar el selfie.'
					);
				}
			} catch {
				setDialogMessage('Error al subir la imagen. Por favor, intente nuevamente.');
			} finally {
				setPhotoUpdateLoading(false);
				setPhotoUploadProgress(0);
			}
		} else if (photoDialog?.type === 'documento') {
			if (!selfieFile || !docFile) {
				setDialogMessage('Debes seleccionar ambas fotos: selfie y documento.');
				return;
			}
			if (!validateImageFile(selfieFile) || !validateImageFile(docFile)) return;
			if (!photoDocIdType) {
				setDialogMessage('Debes seleccionar el tipo de documento.');
				return;
			}

			setPhotoUpdateLoading(true);
			try {
				const [selfieUrl, docUrl] = await Promise.all([
					uploadToFirebase(selfieFile, `user_photos/${storagePath}/${Date.now()}_selfie_${selfieFile.name}`),
					uploadToFirebase(docFile, `user_photos/${storagePath}/${Date.now()}_doc_${docFile.name}`)
				]);
				const response = (await unsafeAPI(
					'POST',
					toApiUrl('update_seventh/'),
					{
						action_type: 'update_user_identification',
						user_id: userId,
						user_selfie_photo: selfieUrl,
						user_identification_photo: docUrl,
						identification_type: photoDocIdType,
						document_country: photoDocCountry
					},
					userData,
					internalCountry.get()
				)) as APIResponse;

				if (response?.success || response?.status === 'success') {
					setDialogMessage('Documento actualizado exitosamente.');
					closePhotoDialog();
				} else {
					setDialogMessage(
						response?.status?.length ? 'Error: ' + response.status : response?.data || 'Error al actualizar el documento.'
					);
				}
			} catch {
				setDialogMessage('Error al subir las imágenes. Por favor, intente nuevamente.');
			} finally {
				setPhotoUpdateLoading(false);
				setPhotoUploadProgress(0);
			}
		}
	};

	const closePhotoDialog = () => {
		if (selfiePreview) URL.revokeObjectURL(selfiePreview);
		if (docPreview) URL.revokeObjectURL(docPreview);
		setPhotoDialog(null);
		setSelfieFile(null);
		setDocFile(null);
		setSelfiePreview(null);
		setDocPreview(null);
		setPhotoDocIdType('');
		setPhotoDocCountry('');
		setPhotoUploadProgress(0);
		setPhotoUpdateLoading(false);
	};

	const showAuthRequiredDialog = () =>
		setDialogMessage('Para realizar esta acción, primero debes autenticar la identidad del cliente con verificación biométrica.');

	const searchClientData = async () => {
		if (!identificationType || !identificationValue) {
			setDialogMessage('Por favor rellene todos los datos');
			return;
		}

		setLoading(true);
		setLivenessQrUrl(null);
		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('get_user_data/'),
				{
					KeyType: identificationType,
					KeyValue:
						identificationType === 'identification_number'
							? format_identification_with_prefix(identificationValue, uidPrefix)
							: identificationValue.trim()
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true && response?.data) {
				// Replace any NaN values with 0 before parsing
				const cleanedData = response.data.replace(/NaN/g, '0');
				const responseData = JSON.parse(cleanedData) as RawClientData;
				const clientLevel =
					responseData?.user?.level === 'level_1'
						? 'Azul'
						: responseData?.user?.level === 'level_2'
							? 'Plata'
							: responseData?.user?.level === 'level_3'
								? 'Oro'
								: responseData?.user?.level === 'level_4'
									? 'Platino'
									: null;
				const transformedData: ClientData = {
					user: {
						...responseData.user,
						level: clientLevel,
						raw_level: responseData.user.level || undefined
					},
					devices: responseData.devices.map((device) => ({
						...device,
						payments: responseData.payments.filter((payment) => payment.device === device.id),
						transactions: responseData.transactions.filter((transaction) =>
							responseData.payments.some((payment) => payment.device === device.id && payment.id === transaction.related_payment)
						)
					}))
				};

				console.log(transformedData);

				// Store raw data in refs for re-association on "load more"
				rawPaymentsRef.current = responseData.payments;
				rawTransactionsRef.current = responseData.transactions;

				setClientData(transformedData);
				setUnrelatedTransactions(
					responseData.transactions.filter(
						(transaction) => !responseData.payments.some((payment) => payment.id === transaction.related_payment)
					)
				);
				setPendingRecharges(responseData.pending_recharges || []);
				setPendingLoans(responseData.pending_loans || []);
				setHasMore(responseData.has_more || {});
			} else {
				setClientData(null);
				setPendingRecharges([]);
				setPendingLoans([]);
				setHasMore({});
			}
		} catch (err) {
			setClientData(null);
			setDialogMessage((err as Error).message);
		} finally {
			setLoading(false);
		}
	};

	const DEVICE_TYPE_GROUPS: Record<string, string[]> = {
		equipos: ['phone', 'tablet', 'laptop'],
		iphone: ['iphone'],
		recarga: ['recarga'],
		loan: ['loan'],
		other: ['other']
	};

	const loadMoreDevices = async (deviceType: string) => {
		if (!clientData) return;
		setLoadingMore(deviceType);

		const typeList = DEVICE_TYPE_GROUPS[deviceType] || [];
		const devicesOfType = clientData.devices.filter((d) => typeList.includes(d.device_type?.toLowerCase()));
		const lastDevice = devicesOfType[devicesOfType.length - 1];

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('get_user_data/'),
				{
					KeyType: identificationType,
					KeyValue:
						identificationType === 'identification_number'
							? format_identification_with_prefix(identificationValue, uidPrefix)
							: identificationValue.trim(),
					device_type_filter: deviceType,
					last_device_id: lastDevice?.id,
					page_size: 5
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success && response?.data) {
				const cleanedData = response.data.replace(/NaN/g, '0');
				const moreData = JSON.parse(cleanedData) as { devices: Device[]; has_more: boolean };

				const payments = rawPaymentsRef.current;
				const transactions = rawTransactionsRef.current;

				const newDevicesWithRelations: DeviceWithRelations[] = moreData.devices.map((device) => ({
					...device,
					payments: payments.filter((p) => p.device === device.id),
					transactions: transactions.filter((t) =>
						payments.some((p) => p.device === device.id && p.id === t.related_payment)
					)
				}));

				setClientData((prev) =>
					prev
						? {
								...prev,
								devices: [...prev.devices, ...newDevicesWithRelations]
							}
						: prev
				);
				setHasMore((prev) => ({ ...prev, [deviceType]: moreData.has_more }));
			}
		} catch (err) {
			setDialogMessage((err as Error).message);
		} finally {
			setLoadingMore(null);
		}
	};

	const loadMorePending = async (pendingType: 'pending_recharges' | 'pending_loans') => {
		setLoadingMore(pendingType);

		const currentItems = pendingType === 'pending_recharges' ? pendingRecharges : pendingLoans;
		const lastItem = currentItems[currentItems.length - 1];

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('get_user_data/'),
				{
					KeyType: identificationType,
					KeyValue:
						identificationType === 'identification_number'
							? format_identification_with_prefix(identificationValue, uidPrefix)
							: identificationValue.trim(),
					pending_type_filter: pendingType,
					last_pending_id: lastItem?.id,
					page_size: 5
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success && response?.data) {
				const cleanedData = response.data.replace(/NaN/g, '0');
				const moreData = JSON.parse(cleanedData);

				if (pendingType === 'pending_recharges') {
					setPendingRecharges((prev) => [...prev, ...(moreData.pending_recharges || [])]);
				} else {
					setPendingLoans((prev) => [...prev, ...(moreData.pending_loans || [])]);
				}
				setHasMore((prev) => ({ ...prev, [pendingType]: moreData.has_more }));
			}
		} catch (err) {
			setDialogMessage((err as Error).message);
		} finally {
			setLoadingMore(null);
		}
	};

	const unlockDevice = async (device_id: string): Promise<true | string> => {
		if (typeof device_id !== 'string') return 'Por favor ingrese un ID de dispositivo válido.';

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('unlock_phone/'),
				{
					device_id
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setClientData((prev) => {
					if (!prev) return prev;
					return {
						...prev,
						devices: prev.devices.map((device) => (device.id === device_id ? { ...device, is_locked: false } : device))
					};
				});
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const freeDevice = async (device_id: string, reason: string): Promise<true | string> => {
		if (typeof device_id !== 'string') return 'Por favor ingrese un ID de dispositivo válido.';
		if (!reason || reason.trim() === '') return 'Por favor, selecciona una razón para la liberación.';

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('release_device/'),
				{
					device_id,
					reason: reason.trim()
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setClientData((prev) => {
					if (!prev) return prev;
					return {
						...prev,
						devices: prev.devices.map((device) => (device.id === device_id ? { ...device, is_released: true } : device))
					};
				});
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const reportStolenDevice = async (device_id: string, is_stolen: boolean): Promise<true | string> => {
		if (typeof device_id !== 'string') return 'Por favor ingrese un ID de dispositivo válido.';

		const [response, error] = await callAPI(
			'https://api.krece.app/report_device_stolen/',
			'POST',
			{ device_id, is_stolen },
			userData,
			internalCountry.get()
		);

		if (error) return 'Error: ' + error;
		if (!response?.success) return 'Ocurrió un problema. Intente más tarde.';

		setClientData((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				devices: prev.devices.map((device) => (device.id === device_id ? { ...device, is_stolen } : device))
			};
		});

		return true;
	};

	const openStolenDeviceDialog = (device: Device) => {
		const markAsStolen = !device.is_stolen;
		setConfirmDialogProps({
			title: markAsStolen ? 'Reportar como robado' : 'Reportar como encontrado',
			message: markAsStolen
				? '¿Está seguro que desea reportar este equipo como robado?'
				: '¿Está seguro que desea reportar este equipo como encontrado? El usuario debe haber completado un liveness exitoso en los últimos 5 minutos.',
			onConfirm: async () => reportStolenDevice(device.id, markAsStolen),
			onClose: () => setConfirmDialogProps(null)
		});
	};

	const transferFraudDevice = async () => {
		const imei = fraudTransferForm.imei.trim();
		const identificationNumber1 = fraudTransferForm.identification_number_1.trim();
		const identificationNumber2 = fraudTransferForm.identification_number_2.trim();

		if (!activeCountry) {
			setFraudTransferError('Selecciona un país antes de transferir el equipo.');
			return;
		}
		if (!/^\d{15}$/.test(imei)) {
			setFraudTransferError('El IMEI debe contener exactamente 15 dígitos.');
			return;
		}
		if (!validateFraudTransferIdentification(identificationNumber1)) {
			setFraudTransferError('El número de identificación original no es válido para el país seleccionado.');
			return;
		}
		if (!validateFraudTransferIdentification(identificationNumber2)) {
			setFraudTransferError('El número de identificación del nuevo dueño no es válido para el país seleccionado.');
			return;
		}
		if (identificationNumber1 === identificationNumber2) {
			setFraudTransferError('Los dos usuarios deben ser diferentes.');
			return;
		}

		setFraudTransferLoading(true);
		setFraudTransferError('');
		const [response, error] = await callAPI(
			'https://api.krece.app/fraud_transfer_device/',
			'POST',
			{
				imei,
				identification_number_1: format_identification_with_prefix(identificationNumber1, uidPrefix),
				identification_number_2: format_identification_with_prefix(identificationNumber2, uidPrefix)
			},
			userData,
			activeCountry
		);
		setFraudTransferLoading(false);

		if (error || !response?.success) {
			setFraudTransferError(error || response?.status || 'Ocurrió un problema, intente más tarde.');
			return;
		}

		setFraudTransferOpen(false);
		setDialogMessage(
			`Dispositivo transferido exitosamente. Device ID: ${response.device_id}. Ghost device ID: ${response.ghost_device_id}. ` +
				`Pagos transferidos: ${response.payments_transferred}. Recordatorios transferidos: ${response.reminders_transferred}.`
		);
		await searchClientData();
	};

	const checkDeviceStatus = async (device_imei_no: string) => {
		setCheckingDeviceStatus(true);

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('administration/devicestatus/'),
				{ device_imei_no },
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true && response?.status?.length) {
				setDialogMessage(response.status);
			} else {
				setDialogMessage(response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.');
			}
		} catch (err) {
			setDialogMessage('Error: ' + (err as Error).message);
		} finally {
			setCheckingDeviceStatus(false);
		}
	};

	const changeCreditLine = async (new_value: string, type: 'primary' | 'secondary'): Promise<true | string> => {
		if (typeof new_value !== 'string' || !new_value?.trim() || Number.isNaN(Number(new_value)))
			return 'Por favor ingrese un número válido.';

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('change_linea_de_compra/'),
				{
					identification_number: clientData!.user.identification_number,
					nueva_linea_de_compra: parseInt(new_value?.trim()),
					type
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true && response?.status?.length) {
				// setClientData((prev) => {
				// 	if (!prev) return prev;
				// 	return {
				// 		...prev,
				// 		user: {
				// 			...prev.user,
				// 			max_loan_approved_3months: parseInt(new_value?.trim())
				// 		}
				// 	};
				// });
				await searchClientData();
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const changeName = async (new_value: string): Promise<true | string> => {
		if (typeof new_value !== 'string' || !new_value?.trim() || new_value?.trim()?.length < 3)
			return 'El nombre debe tener al menos 3 caracteres.';

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('change_user_name/'),
				{
					identification_number: clientData!.user.identification_number,
					first_name: new_value.toUpperCase().trim()
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setClientData((prev) => {
					if (!prev) return prev;
					return {
						...prev,
						user: {
							...prev.user,
							first_name: new_value.toUpperCase().trim()
						}
					};
				});
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const changeLastName = async (new_value: string): Promise<true | string> => {
		if (typeof new_value !== 'string' || !new_value?.trim() || new_value?.trim()?.length < 3)
			return 'Los apellidos deben tener al menos 3 caracteres.';

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('change_user_name/'),
				{
					identification_number: clientData!.user.identification_number,
					last_name: new_value.toUpperCase().trim()
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setClientData((prev) => {
					if (!prev) return prev;
					return {
						...prev,
						user: {
							...prev.user,
							last_name: new_value.toUpperCase().trim()
						}
					};
				});
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const changeIdentificationNumber = async (new_value: string): Promise<true | string> => {
		if (!isUserBiometricallyAuthenticated) {
			showAuthRequiredDialog();
			return 'Usuario no autenticado biométricamente';
		}
		if (
			typeof new_value !== 'string' ||
			(!validate_identity_number(new_value?.trim() || '', 'cedula', clientData?.user?.country!) &&
				!validate_identity_number(new_value?.trim() || '', 'dni', clientData?.user?.country!))
		)
			return 'El número de cédula o DNI no es válido.';

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('change_identification_number/'),
				{
					identification_number: clientData!.user.identification_number,
					identification_number_new: format_identification_with_prefix(new_value.trim(), uidPrefix)
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setClientData((prev) => {
					if (!prev) return prev;
					return {
						...prev,
						user: {
							...prev.user,
							identification_number: new_value.trim()
						}
					};
				});
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const changePhoneNumber = async (new_value: string): Promise<true | string> => {
		if (!isUserBiometricallyAuthenticated) {
			showAuthRequiredDialog();
			return 'Usuario no autenticado biométricamente';
		}
		if (!validate_phone_number(new_value?.trim() || '', clientData?.user?.country || '')) {
			return 'Error: El número de teléfono no es válido.';
		}

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('edit_phone_number/'),
				{
					identification_number: clientData!.user.identification_number,
					incorrect_phone_number: clientData!.user.phone_number,
					new_phone_number: new_value.trim()
				},
				userData,
				internalCountry.get()
			)) as any;

			if (response?.success === true) {
				setDialogMessage(response?.status || 'Se actualizó el número de teléfono exitosamente.');
				await searchClientData();
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrió un problema, intente de nuevo más tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const changeCommercialName = async (device_id: string, new_value: string): Promise<true | string> => {
		if (typeof new_value !== 'string' || !new_value?.trim() || new_value?.trim()?.length < 4)
			return 'El nombre  debe tener al menos 4 caracteres.';

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('change_device_commercial_name/'),
				{
					device_id,
					device_commercial_name: new_value.trim()
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setClientData((prev) => {
					if (!prev) return prev;
					return {
						...prev,
						devices: prev.devices.map((device) =>
							device.id === device_id ? { ...device, device_commercial_name: new_value.trim() } : device
						)
					};
				});
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const changeSalePrice = async (device_id: string, new_value: string): Promise<true | string> => {
		if (typeof new_value !== 'string' || !new_value?.trim() || Number.isNaN(Number(new_value)))
			return 'Por favor ingrese un número válido.';

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('change_device_selling_price/'),
				{
					device_id,
					device_price: parseInt(new_value.trim())
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setClientData((prev) => {
					if (!prev) return prev;
					return {
						...prev,
						devices: prev.devices.map((device) =>
							device.id === device_id ? { ...device, device_price: parseInt(new_value.trim()) } : device
						)
					};
				});
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const deleteTransaction = async (transaction_id: string): Promise<true | string> => {
		if (typeof transaction_id !== 'string') return 'Por favor ingrese un ID de transacción válido.';

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('delete_payment/'),
				{
					transaction_id
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setClientData((prev) => {
					if (!prev) return prev;
					return {
						...prev,
						devices: prev.devices.map((device) => {
							const transactions = device.transactions.filter((transaction) => transaction.id !== transaction_id);
							return { ...device, transactions };
						})
					};
				});
				setUnrelatedTransactions((prev) => prev?.filter((transaction) => transaction.id !== transaction_id));
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const deleteLoan = async (device_id: string): Promise<true | string> => {
		if (typeof device_id !== 'string') return 'Por favor ingrese un ID de dispositivo válido.';

		try {
			const apiResponse = await unsafeAPI(
				'POST',
				'https://api.krece.app/administration/deletedevice/',
				{
					identification_number: clientData!.user.identification_number,
					store_user: userData?.email || '',
					country: internalCountry.get() || '',
					device_id: device_id
				},
				userData,
				internalCountry.get()
			);
			const response = apiResponse as any;

			if (response?.device_deleted === true) {
				setClientData((prev) => {
					if (!prev) return prev;
					return {
						...prev,
						devices: prev.devices.filter((device) => device.id !== device_id)
					};
				});
				return true;
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrió un problema, intente más tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}
	};

	const deleteAccount = async (): Promise<true | string> => {
		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('delete_user/'),
				{
					identification_number: clientData!.user.identification_number
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setDialogMessage('Cuenta eliminada exitosamente, el navegador se recargará en 2 segundos...');
				setTimeout(() => {
					window.location.reload();
				}, 2000);
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const blacklistAccount = async (reason: string, comment?: string): Promise<true | string> => {
		// Validate that reason is provided
		if (!reason || reason.trim() === '') {
			return 'Por favor, selecciona una razón para el bloqueo.';
		}

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('black_list_user/'),
				{
					identification_number: clientData!.user.identification_number,
					reason: reason?.trim(),
					comment: comment?.trim() || undefined
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setDialogMessage('El cliente fue agregado a la lista de clientes vetados por Krece.');
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const removeBlacklistAccount = async (): Promise<true | string> => {
		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('delete_black_list_user/'),
				{
					identification_number: clientData!.user.identification_number
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setDialogMessage(response?.status || 'El cliente fue removido de la lista de clientes vetados por Krece.');
				await searchClientData();
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const createMissingRecharge = async (): Promise<true | string> => {
		const { phone_number, amount, date, xid } = crearRecargaForm;
		const identification_number = clientData?.user?.identification_number;
		const country = internalCountry.get();
		const isRD = country === 'rd';

		if (!identification_number) return 'No se encontró la cédula del cliente.';
		if (!phone_number || !amount || !date) return 'Todos los campos son requeridos.';
		if (isRD && !xid) return 'El XID es requerido para República Dominicana.';

		const parsedAmount = parseFloat(amount);
		if (isNaN(parsedAmount) || parsedAmount <= 0) return 'El monto debe ser un número válido mayor a 0.';

		try {
			setCrearRecargaLoading(true);
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('administration/create_missing_recharge/'),
				{
					identification_number,
					phone_number,
					amount: parsedAmount,
					date,
					...(isRD && { xid })
				},
				userData,
				country
			)) as { success?: boolean; status?: string; message?: string };

			if (response?.success === true) {
				setDialogMessage(response?.message || 'Recarga creada exitosamente.');
				setCrearRecargaOpen(false);
				setCrearRecargaForm({ phone_number: '', amount: '', date: '', xid: '' });
				await searchClientData();
				return true;
			} else {
				return response?.status || response?.message || 'Ocurrió un problema, intente más tarde.';
			}
		} catch (err: any) {
			const status = err?.response?.status || err?.status;
			if (status === 409) return 'Esta recarga ya existe en el sistema.';
			if (status === 404) return 'Usuario no encontrado o no se encontró recarga exitosa en el proveedor.';
			return 'Error: ' + (err?.message || 'Ocurrió un problema inesperado.');
		} finally {
			setCrearRecargaLoading(false);
		}
	};

	const syncKashio = async () => {
		if (!clientData?.user?.email) return;
		try {
			setSyncKashioLoading(true);
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('apply_kashio_webhook_logs_for_user/'),
				{ user_email: clientData.user.email },
				userData,
				internalCountry.get()
			)) as { success?: boolean; status?: string; message?: string };

			setDialogMessage(response?.status || response?.message || 'Sync completado.');
		} catch (err: any) {
			setDialogMessage('Error: ' + (err?.message || 'Ocurrió un problema inesperado.'));
		} finally {
			setSyncKashioLoading(false);
		}
	};

	const blacklistPreemptive = async (identification_number: string, reason: string, comment?: string): Promise<true | string> => {
		if (!identification_number || identification_number.trim() === '') {
			return 'Por favor, ingresa un número de identificación.';
		}
		if (!reason || reason.trim() === '') {
			return 'Por favor, selecciona una razón para el bloqueo.';
		}

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('black_list_user/'),
				{
					identification_number: format_identification_with_prefix(identification_number.trim(), uidPrefix),
					reason: reason?.trim(),
					comment: comment?.trim() || undefined
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setDialogMessage('El número de identificación fue agregado a la lista de clientes vetados por Krece.');
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const resetPassword = async (): Promise<true | string> => {
		if (!isUserBiometricallyAuthenticated) {
			showAuthRequiredDialog();
			return 'Usuario no autenticado biométricamente';
		}
		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('change_user_password/'),
				{
					email: clientData!.user.email
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setDialogMessage(response?.status || 'Se reseteó la contraseña exitosamente.');
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const recalculateLevel = async (): Promise<true | string> => {
		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('users/updateuserlevel/'),
				{
					identification_number: clientData!.user.identification_number
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setDialogMessage(response?.status || 'Se recalculó el nivel exitosamente.');
				searchClientData();
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const updateAmountPaid = async (payment_id: string, amount: string): Promise<true | string> => {
		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('updateamountpaid/'),
				{
					payment: payment_id,
					amount_paid: amount
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setDialogMessage(response?.status || 'Se actualizó el monto pagado exitosamente.');
				searchClientData();
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const recalculatePayment = async (payment_id: string) => {
		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('recalculate_payment/'),
				{ payment_id },
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setDialogMessage(response?.status || 'El monto del pago fue recalculado exitosamente.');
				searchClientData();
			} else {
				setDialogMessage(response?.status?.length ? 'Error: ' + response.status : 'Ocurrió un problema, intente más tarde.');
			}
		} catch (err) {
			setDialogMessage('Error: ' + (err as Error).message);
		}
	};

	const updateActualAmount = async (device_id: string, amount: string): Promise<true | string> => {
		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('updateactualamount/'),
				{
					device: device_id,
					actual_amount: amount
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setDialogMessage(response?.status || 'Se actualizó el monto exitosamente.');
				searchClientData();
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const refundTransaction = async (
		transaction_id: string,
		id_document: string,
		account_number: string,
		refund_amount: number
	): Promise<true | string> => {
		if (typeof transaction_id !== 'string') return 'Por favor ingrese un ID de transacción válido.';
		if (typeof id_document !== 'string' || !id_document?.trim()) return 'Por favor ingrese un número de cédula válido.';
		if (typeof account_number !== 'string' || !account_number?.trim()) return 'Por favor ingrese un número de cuenta válido.';
		if (typeof refund_amount !== 'number' || refund_amount <= 0) return 'Por favor ingrese un monto de reembolso válido.';

		// Validate cedula number
		if (
			!validate_identity_number(id_document.trim(), 'cedula', clientData?.user?.country!) &&
			!validate_identity_number(id_document.trim(), 'dni', clientData?.user?.country!) &&
			!validate_identity_number(id_document.trim(), 'rif', clientData?.user?.country!)
		) {
			return 'El número de cédula o DNI no es válido.';
		}

		// Validate account number
		if (!validate_bank_account_number(account_number.trim(), clientData?.user?.country!)) {
			return 'El número de cuenta no es válido.';
		}

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('process_refund/'),
				{
					id_document: id_document.trim(),
					transaction_id,
					account_number: account_number.trim(),
					refund_amount
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				// @ts-ignore
				const reference = response?.data?.reference;
				setDialogMessage(
					(response?.status || 'Reembolso procesado exitosamente.') + (reference ? ` Referencia: ${reference}` : '')
				);
			} else {
				return response?.status?.length ? response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const changePaymentDate15and30 = async (device_id: string) => {
		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('update_payments_to_15_30/'),
				{
					device_id
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setDialogMessage(response?.status || 'Fechas de pago cambiadas exitosamente.');
				searchClientData();
			} else {
				return response?.status?.length ? response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const changeDeviceIdentifier = async (device_id: string, identifier: string, device_type: string) => {
		const trimmedIdentifier = identifier.trim();
		if (!trimmedIdentifier) {
			return `Error: ${device_type?.toLowerCase() === 'laptop' ? 'El serial' : 'El IMEI'} es obligatorio.`;
		}

		if (['phone', 'iphone', 'tablet'].includes(device_type?.toLowerCase())) {
			const imeiRegex = /^\d{15}$/;
			if (!imeiRegex.test(trimmedIdentifier)) {
				return 'Error: El IMEI debe contener exactamente 15 dígitos numéricos.';
			}
		}

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('change_imei/'),
				{
					device_id: device_id,
					target_imei: trimmedIdentifier
				},
				userData,
				internalCountry.get()
			)) as APIResponse;

			if (response?.success === true) {
				setDialogMessage(response?.status || `Se actualizó ${device_type?.toLowerCase() === 'laptop' ? 'el serial' : 'el IMEI'} exitosamente.`);
				searchClientData();
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	const changeEmail = async (email: string, new_email: string) => {
		if (!isUserBiometricallyAuthenticated) {
			showAuthRequiredDialog();
			return 'Usuario no autenticado biométricamente';
		}
		if (!validate_email(new_email)) {
			return 'Error: El email no es válido.';
		}

		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('administration/change_user_email/'),
				{
					email: email.trim().toLowerCase(),
					new_email: new_email.trim().toLowerCase()
				},
				userData,
				internalCountry.get()
			)) as any;

			if (response?.success === true) {
				const new_password = response?.data?.new_password;
				setDialogMessage(`Se actualizó el email exitosamente. La nueva contraseña es: ${new_password}`);
				searchClientData();
			} else {
				return response?.status?.length ? 'Error: ' + response.status : 'Ocurrio un problema intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	return (
		<div className="mx-auto w-full max-w-7xl p-4 md:p-[1.25rem]">
			{dialogMessage && (
				<Dialog title={''} onClose={() => setDialogMessage('')}>
					<p>{dialogMessage}</p>
				</Dialog>
			)}
			{photoDialog && (
				<Dialog
					title={photoDialog.type === 'selfie' ? 'Selfie del Cliente' : 'Documento del Cliente'}
					onClose={closePhotoDialog}
					cerrarButton={false}
				>
					<div className="flex flex-col gap-4">
						{/* Current photo URL */}
						<div>
							<p className="mb-1 text-xs text-neutral-400">URL actual</p>
							<div className="flex items-center gap-2 rounded-md bg-neutral-800/50 px-3 py-2">
								<span className="min-w-0 flex-1 truncate text-xs text-neutral-300">
									{photoDialog.currentUrl}
								</span>
								<button
									onClick={() => navigator.clipboard.writeText(photoDialog.currentUrl)}
									className="shrink-0 rounded p-1 text-neutral-400 hover:bg-neutral-700 hover:text-neutral-200"
									title="Copiar URL"
								>
									<Copy className="size-3.5" />
								</button>
								<a
									href={photoDialog.currentUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="shrink-0 rounded p-1 text-neutral-400 hover:bg-neutral-700 hover:text-neutral-200"
									title="Abrir en nueva pestaña"
								>
									<ExternalLink className="size-3.5" />
								</a>
							</div>
						</div>

						<hr className="border-neutral-700/50" />

						{/* Update section */}
						<div className="flex flex-col gap-3">
							<p className="text-sm font-medium text-neutral-300">Actualizar foto</p>

							{/* Selfie upload - always shown */}
							<div>
								<p className="mb-1 text-xs text-neutral-400">
									{photoDialog.type === 'selfie' ? 'Nueva selfie' : 'Nueva selfie'}
								</p>
								<label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-neutral-600 px-3 py-2 text-xs text-neutral-300 transition-colors hover:border-neutral-500 hover:bg-neutral-800/30">
									<Upload className="size-4" />
									{selfieFile ? selfieFile.name : 'Seleccionar imagen de selfie'}
									<input
										type="file"
										accept="image/jpeg,image/png,image/webp"
										className="hidden"
										onChange={(e) => {
											const file = e.target.files?.[0];
											if (file) {
												if (selfiePreview) URL.revokeObjectURL(selfiePreview);
												setSelfieFile(file);
												setSelfiePreview(URL.createObjectURL(file));
											}
										}}
									/>
								</label>
								{selfiePreview && (
									<img
										src={selfiePreview}
										alt="Vista previa selfie"
										className="mt-2 max-h-32 rounded-md object-contain"
									/>
								)}
							</div>

							{/* Document upload - only for documento type */}
							{photoDialog.type === 'documento' && (
								<>
									<div>
										<p className="mb-1 text-xs text-neutral-400">Nueva foto de documento</p>
										<label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-neutral-600 px-3 py-2 text-xs text-neutral-300 transition-colors hover:border-neutral-500 hover:bg-neutral-800/30">
											<Upload className="size-4" />
											{docFile ? docFile.name : 'Seleccionar imagen de documento'}
											<input
												type="file"
												accept="image/jpeg,image/png,image/webp"
												className="hidden"
												onChange={(e) => {
													const file = e.target.files?.[0];
													if (file) {
														if (docPreview) URL.revokeObjectURL(docPreview);
														setDocFile(file);
														setDocPreview(URL.createObjectURL(file));
													}
												}}
											/>
										</label>
										{docPreview && (
											<img
												src={docPreview}
												alt="Vista previa documento"
												className="mt-2 max-h-32 rounded-md object-contain"
											/>
										)}
									</div>

									<div>
										<p className="mb-1 text-xs text-neutral-400">Tipo de documento</p>
										<Select
											placeholder="Seleccionar tipo"
											defaultValue={photoDocIdType || undefined}
											onSelectedChange={(val) => setPhotoDocIdType(String(val || ''))}
											className="!h-8 !text-xs"
											options={
												CountrySettings?.id_types?.map((t) => ({
													label: t.name || t.code || '',
													value: t.code || ''
												})) || []
											}
										/>
									</div>
								</>
							)}

							{/* Upload progress bar */}
							{photoUploadProgress > 0 && photoUploadProgress < 100 && (
								<div className="h-2 w-full overflow-hidden rounded-full bg-neutral-700">
									<div
										className="h-full rounded-full bg-blue-500 transition-all"
										style={{ width: `${photoUploadProgress}%` }}
									/>
								</div>
							)}
						</div>

						{/* Action buttons */}
						<div className="flex justify-end gap-2">
							<button
								onClick={closePhotoDialog}
								disabled={photoUpdateLoading}
								className="rounded-lg px-4 py-2 text-sm text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-neutral-200 disabled:opacity-50"
							>
								Cancelar
							</button>
							<button
								type="submit"
								onClick={handlePhotoUpdate}
								disabled={photoUpdateLoading}
								className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
							>
								{photoUpdateLoading ? <Spinner size={1} /> : null}
								Actualizar
							</button>
						</div>
					</div>
				</Dialog>
			)}

			{pendingDialog === 'recharges' && (
				<Dialog title="Intentos de Recarga" onClose={() => setPendingDialog(null)} cerrarButton={false}>
					<div className="max-h-[70vh] overflow-y-auto">
						<table className="w-full text-left text-sm">
							<thead className="sticky top-0 bg-background text-xs text-neutral-400">
								<tr>
									<th className="px-3 py-2">Fecha</th>
									<th className="px-3 py-2">Operador</th>
									<th className="px-3 py-2">Teléfono</th>
									<th className="px-3 py-2">Monto</th>
									<th className="px-3 py-2">Estado</th>
									<th className="px-3 py-2">ID</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-neutral-800/50">
								{pendingRecharges.map((pr) => (
									<tr key={pr.id} className="text-neutral-200 hover:bg-neutral-800/30">
										<td className="whitespace-nowrap px-3 py-2 text-neutral-400">
											{pr.created_at ? new Date(pr.created_at).toLocaleDateString() : '-'}
										</td>
										<td className="px-3 py-2">{pr.operator || '-'}</td>
										<td className="px-3 py-2">{pr.phone_number || '-'}</td>
										<td className="whitespace-nowrap px-3 py-2">
											{currency_code}
											{(pr.device_data?.amount_financed ?? pr.amount)?.toFixed(2)}
											{clientData?.user.country === 'venezuela' && (
												<span className="ml-1 text-xs text-neutral-500">
													(Bs {pr.amount?.toFixed(2)})
												</span>
											)}
										</td>
										<td className="px-3 py-2">
											<span
												className={`rounded px-2 py-0.5 text-xs ${
													pr.status === 'completed'
														? 'bg-green-500/10 text-green-400'
														: pr.status === 'failed'
															? 'bg-red-500/10 text-red-400'
															: 'bg-yellow-500/10 text-yellow-400'
												}`}
											>
												{ucfirst(pr.status || 'pending')}
											</span>
										</td>
										<td className="px-3 py-2">
											<div className="flex items-center gap-1">
												<span className="max-w-[80px] truncate font-mono text-xs">{pr.id}</span>
												<Copy
													onClick={() => navigator.clipboard.writeText(pr.id)}
													className="size-3 shrink-0 cursor-pointer text-neutral-500 hover:text-neutral-300"
												/>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
						{hasMore.pending_recharges && (
							<div className="mt-3 flex justify-center pb-2">
								<Button
									onClick={() => loadMorePending('pending_recharges')}
									disabled={loadingMore === 'pending_recharges'}
									variant="outline"
									className="border-neutral-700 text-sm text-neutral-400"
								>
									{loadingMore === 'pending_recharges' ? <Spinner size={1} /> : 'Cargar más'}
								</Button>
							</div>
						)}
					</div>
				</Dialog>
			)}
			{pendingDialog === 'loans' && (
				<Dialog title="Intentos de Préstamo" onClose={() => setPendingDialog(null)} cerrarButton={false}>
					<div className="max-h-[70vh] overflow-y-auto">
						<table className="w-full text-left text-sm">
							<thead className="sticky top-0 bg-background text-xs text-neutral-400">
								<tr>
									<th className="px-3 py-2">Fecha</th>
									<th className="px-3 py-2">Monto</th>
									<th className="px-3 py-2">Duración</th>
									<th className="px-3 py-2">Desembolso</th>
									<th className="px-3 py-2">Estado</th>
									<th className="px-3 py-2">Banco</th>
									<th className="px-3 py-2">Cuenta</th>
									<th className="px-3 py-2">ID</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-neutral-800/50">
								{pendingLoans.map((pl) => (
									<tr key={pl.id} className="text-neutral-200 hover:bg-neutral-800/30">
										<td className="whitespace-nowrap px-3 py-2 text-neutral-400">
											{pl.created_at ? new Date(pl.created_at).toLocaleDateString() : '-'}
										</td>
										<td className="whitespace-nowrap px-3 py-2">
											{currency_code}
											{pl.amount_usd?.toFixed(2)}
											{clientData?.user.country === 'venezuela' && (
												<span className="ml-1 text-xs text-neutral-500">
													(Bs {pl.amount_bs?.toFixed(2)})
												</span>
											)}
										</td>
										<td className="px-3 py-2">{pl.loan_duration} meses</td>
										<td className="px-3 py-2">{pl.disbursement_type || '-'}</td>
										<td className="px-3 py-2">
											<span
												className={`rounded px-2 py-0.5 text-xs ${
													pl.status === 'completed'
														? 'bg-green-500/10 text-green-400'
														: pl.status === 'failed'
															? 'bg-red-500/10 text-red-400'
															: 'bg-yellow-500/10 text-yellow-400'
												}`}
											>
												{ucfirst(pl.status || 'pending')}
											</span>
										</td>
										<td className="px-3 py-2">{pl.bank_account_data?.bank_name || '-'}</td>
										<td className="px-3 py-2">
											<span className="select-all font-mono text-xs">
												{pl.bank_account_data?.bank_account_number || '-'}
											</span>
										</td>
										<td className="px-3 py-2">
											<div className="flex items-center gap-1">
												<span className="max-w-[80px] truncate font-mono text-xs">{pl.id}</span>
												<Copy
													onClick={() => navigator.clipboard.writeText(pl.id)}
													className="size-3 shrink-0 cursor-pointer text-neutral-500 hover:text-neutral-300"
												/>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
						{hasMore.pending_loans && (
							<div className="mt-3 flex justify-center pb-2">
								<Button
									onClick={() => loadMorePending('pending_loans')}
									disabled={loadingMore === 'pending_loans'}
									variant="outline"
									className="border-neutral-700 text-sm text-neutral-400"
								>
									{loadingMore === 'pending_loans' ? <Spinner size={1} /> : 'Cargar más'}
								</Button>
							</div>
						)}
					</div>
				</Dialog>
			)}

			{confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}

			{editDialogProps && <ConfirmInputDialog {...editDialogProps} />}

			{fraudTransferOpen && (
				<Dialog
					title="Transferencia por fraude"
					onClose={closeFraudTransferDialog}
					closable={!fraudTransferLoading}
					cerrarButton={false}
					className="!w-[min(560px,90vw)]"
				>
					<form
						className="flex flex-col gap-4 pt-1"
						onSubmit={(e) => {
							e.preventDefault();
							transferFraudDevice();
						}}
					>
						<div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 px-3 py-2 text-sm text-yellow-300 light:text-yellow-700">
							Esta acción mueve el equipo, sus pagos y recordatorios al nuevo dueño, y veta al usuario original como estafador.
						</div>
						<Input
							floatingLabel="IMEI del equipo"
							placeholder="Ingrese el IMEI"
							value={fraudTransferForm.imei}
							onChange={(value) => setFraudTransferForm((prev) => ({ ...prev, imei: value }))}
							allowedChars="numbers_only"
							maxLength={15}
						/>
						<Input
							floatingLabel="Usuario original"
							placeholder="Cédula o DNI actual"
							value={fraudTransferForm.identification_number_1}
							onChange={(value) => setFraudTransferForm((prev) => ({ ...prev, identification_number_1: value }))}
							allowedChars="alphanumeric"
						/>
						<Input
							floatingLabel="Nuevo dueño"
							placeholder="Cédula o DNI del nuevo dueño"
							value={fraudTransferForm.identification_number_2}
							onChange={(value) => setFraudTransferForm((prev) => ({ ...prev, identification_number_2: value }))}
							allowedChars="alphanumeric"
						/>
						{fraudTransferError && (
							<div className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-400">
								{fraudTransferError}
							</div>
						)}
						<div className="mt-2 flex justify-end gap-3">
							<Button disabled={fraudTransferLoading} variant="secondary" onClick={closeFraudTransferDialog} className="w-fit">
								Cancelar
							</Button>
							<Button type="submit" variant="destructive" loading={fraudTransferLoading} className="w-fit">
								Transferir equipo
							</Button>
						</div>
					</form>
				</Dialog>
			)}

			{crearRecargaOpen && (
				<Dialog title="Crear Recarga" onClose={() => setCrearRecargaOpen(false)} closable={!crearRecargaLoading} className="!w-[min(500px,90vw)]">
					<form
						className="flex flex-col gap-4 pt-1"
						onSubmit={async (e) => {
							e.preventDefault();
							const result = await createMissingRecharge();
							if (result !== true) {
								setDialogMessage(result);
							}
						}}
					>
						<Input
							floatingLabel="Número de teléfono"
							placeholder="04121234567"
							value={crearRecargaForm.phone_number}
							onChange={(value) => setCrearRecargaForm((prev) => ({ ...prev, phone_number: value }))}
							allowedChars="numbers_only"
						/>
						<Input
							floatingLabel="Monto"
							placeholder="5.00"
							value={crearRecargaForm.amount}
							onChange={(value) => setCrearRecargaForm((prev) => ({ ...prev, amount: value }))}
							allowedChars="numeric"
						/>
						<Input
							floatingLabel="Fecha"
							placeholder="YYYY-MM-DD"
							value={crearRecargaForm.date}
							onChange={(value) => setCrearRecargaForm((prev) => ({ ...prev, date: value }))}
						/>
						{internalCountry.get() === 'rd' && (
							<Input
								floatingLabel="XID (Midas)"
								placeholder="abc123"
								value={crearRecargaForm.xid}
								onChange={(value) => setCrearRecargaForm((prev) => ({ ...prev, xid: value }))}
							/>
						)}
						<div className="mt-2 flex justify-end gap-3">
							<Button
								disabled={crearRecargaLoading}
								variant="secondary"
								onClick={() => setCrearRecargaOpen(false)}
								className="w-fit"
							>
								Cancelar
							</Button>
							<Button type="submit" variant="primary" loading={crearRecargaLoading} className="w-fit">
								Crear
							</Button>
						</div>
					</form>
				</Dialog>
			)}

			{twoFactorContext && (
				<Dialog title={'Verificación de seguridad'} onClose={() => setTwoFactorContext(null)} className="!w-[min(500px,90vw)]">
					<div className="space-y-3">
						<TwoFactorAuth
							country={clientData?.user?.country || internalCountry.get()}
							email={twoFactorContext.mode === 'email' ? twoFactorContext.email : undefined}
							onlyEmail={twoFactorContext.mode === 'email'}
							select_phone_numbers={
								twoFactorContext.mode === 'phone'
									? [{ country: clientData?.user?.country || '', phone_number: twoFactorContext.phone! }]
									: undefined
							}
							onSuccess={async () => {
								setTwoFactorActionLoading(true);
								const ctx = twoFactorContext;
								if (ctx?.mode === 'email' && ctx.email) {
									const result = await changeEmail(clientData!.user.email, ctx.email!);
									if (result !== true) setDialogMessage(String(result));
								} else if (ctx?.mode === 'phone' && ctx.phone) {
									const result = await changePhoneNumber(ctx.phone!);
									if (result !== true) setDialogMessage(String(result));
								}
								setTwoFactorActionLoading(false);
								setTwoFactorContext(null);
							}}
						/>
						{twoFactorActionLoading && (
							<div className="flex items-center gap-2 rounded-md border border-neutral-800 bg-neutral-900/40 px-3 py-2 text-sm">
								<Spinner size={1.25} />
								<span className="text-neutral-300">Aplicando cambio…</span>
							</div>
						)}
					</div>
				</Dialog>
			)}

			{splitCuotaDeviceID && (
				<Dialog
					title="Dividir o extender próxima cuota"
					onClose={() => setSplitCuotaDeviceID(null)}
					closable={!splitPaymentLoading}
				>
					<div className="flex flex-col gap-2">
						<Button
							onClick={async () => {
								setSplitPaymentLoading(true);
								try {
									const response = await unsafeAPI(
										'POST',
										'https://api.krece.app/split_payment/',
										{
											number_of_installments: 1,
											device_id: splitCuotaDeviceID
										},
										userData,
										internalCountry.get()
									);

									if (response?.success) {
										setDialogMessage('Se ha extendido el plazo de pago exitosamente.');
										setSplitCuotaDeviceID(null);
										searchClientData(); // Refresh data
									} else {
										setDialogMessage(response?.status || 'Error al procesar la solicitud.');
									}
								} catch (error) {
									setDialogMessage('Error: ' + (error as Error).message);
								} finally {
									setSplitPaymentLoading(false);
								}
							}}
							className="h-14 w-72 justify-start"
							variant="primary"
							disabled={splitPaymentLoading}
						>
							<div className="text-left">
								<div className="font-medium">Extender plazo de pago</div>
								<div className="text-xs text-neutral-900">Dar más tiempo para pagar la cuota vencida</div>
							</div>
						</Button>
						<Button
							onClick={async () => {
								setSplitPaymentLoading(true);
								try {
									const response = await unsafeAPI(
										'POST',
										'https://api.krece.app/split_payment/',
										{
											number_of_installments: 2,
											device_id: splitCuotaDeviceID
										},
										userData,
										internalCountry.get()
									);

									if (response?.success) {
										setDialogMessage('Se ha dividido la cuota en 2 pagos exitosamente.');
										setSplitCuotaDeviceID(null);
										searchClientData(); // Refresh data
									} else {
										setDialogMessage(response?.status || 'Error al procesar la solicitud.');
									}
								} catch (error) {
									setDialogMessage('Error: ' + (error as Error).message);
								} finally {
									setSplitPaymentLoading(false);
								}
							}}
							className="h-14 w-72 justify-start"
							variant="primary"
							disabled={splitPaymentLoading}
						>
							<div className="text-left">
								<div className="font-medium">Dividir en 2 pagos</div>
								<div className="text-xs text-neutral-900">Dividir la cuota en 2 pagos quincenales</div>
							</div>
						</Button>
						<Button
							onClick={async () => {
								setSplitPaymentLoading(true);
								try {
									const response = await unsafeAPI(
										'POST',
										'https://api.krece.app/split_payment/',
										{
											number_of_installments: 3,
											device_id: splitCuotaDeviceID
										},
										userData,
										internalCountry.get()
									);

									if (response?.success) {
										setDialogMessage('Se ha dividido la cuota en 3 pagos exitosamente.');
										setSplitCuotaDeviceID(null);
										searchClientData(); // Refresh data
									} else {
										setDialogMessage(response?.status || 'Error al procesar la solicitud.');
									}
								} catch (error) {
									setDialogMessage('Error: ' + (error as Error).message);
								} finally {
									setSplitPaymentLoading(false);
								}
							}}
							className="h-14 w-72 justify-start"
							variant="primary"
							disabled={splitPaymentLoading}
						>
							<div className="text-left">
								<div className="font-medium">Dividir en 3 pagos</div>
								<div className="text-xs text-neutral-900">Dividir la cuota en 3 pagos quincenales</div>
							</div>
						</Button>
					</div>
				</Dialog>
			)}

			<form
				className="space-y-4"
				onSubmit={(e) => {
					e.preventDefault();
					searchClientData();
				}}
			>
				<div className="space-y-0 xl:text-center">
					<h2 className="text-2xl font-semibold text-neutral-100">Administrar Clientes</h2>
					<p className="text-sm text-neutral-400">Ingrese los datos para buscar información del cliente</p>
				</div>

				<div className="flex max-w-3xl flex-col gap-4 pt-3 md:flex-row md:gap-2 xl:mx-auto">
					<Select
						containerClassName="w-full"
						options={[
							{ label: 'Cédula o DNI', value: 'identification_number' },
							{ label: 'Email', value: 'email' },
							{ label: 'Teléfono', value: 'phone_number' },
							{ label: 'IMEI', value: 'imei_no' },
							{ label: 'Device ID', value: 'nuovopay_device_id' },
							{ label: 'Referencia', value: 'reference' }
						]}
						onSelectedChange={(value) => setIdentificationType(value as string)}
						placeholder="Buscar por"
						floatingLabel={'Tipo de documento'}
						defaultValue={identificationType}
					/>

					<Input
						containerClassName="w-full"
						name="cedula"
						placeholder={'Ingresar'}
						floatingLabel={'Valor a buscar'}
						value={identificationValue}
						onChange={(value) => setIdentificationValue(value)}
						allowedChars={identificationType !== 'email' ? 'alphanumeric' : undefined}
					/>

					<div className="ml-0 flex shrink-0 justify-end gap-2 md:ml-4">
						<Button
							Icon={<SearchIcon className="size-4" />}
							loading={loading}
							variant="primary"
							className="!h-12 w-fit"
							type="submit"
						>
							Buscar
						</Button>
						{['ata', 'admin', 'manager'].includes(userData?.access_type || '') && (
							<Button
								Icon={<Ban className="size-4" />}
								variant="default"
								className="!h-12 w-fit bg-yellow-600 text-white hover:bg-yellow-700"
								type="button"
								onClick={() => {
									setEditDialogProps({
										title: 'Vetar',
										fields: [
											{
												type: 'input',
												name: 'identification_number',
												label: 'Número de identificación',
												placeholder: 'Ingrese la cédula o DNI',
												defaultValue: identificationValue
											},
											{
												type: 'select',
												name: 'reason',
												label: 'Razón del bloqueo',
												options: [
													{ label: 'Estafador', value: 'Estafador' },
													{ label: 'Otro', value: 'Otro' }
												]
											},
											{
												type: 'textarea',
												name: 'comment',
												label: 'Comentario opcional',
												placeholder: 'Agrega contexto para el equipo interno',
												required: false
											}
										],
										onConfirm: async (values) =>
											await blacklistPreemptive(
												values.identification_number as string,
												values.reason as string,
												values.comment as string
											),
										onClose: () => setEditDialogProps(null)
									});
								}}
							>
								Vetar 
							</Button>
						)}
					</div>
				</div>
			</form>

			<IdentityRecovery
				client={identityRecoveryClient}
				userData={userData}
				country={recoveryCountry}
				disabled={loading}
				onClose={() => setIdentityRecoveryClient(null)}
				onAccountRetired={(identification, authorizedEmail) => {
					if (
						clientData?.user.identification_number !== identification ||
						clientData.user.country !== recoveryCountry ||
						clientData.user.email?.trim().toLowerCase() === authorizedEmail
					) return;
					setClientData(undefined);
					setUnrelatedTransactions([]);
					setPendingRecharges([]);
					setPendingLoans([]);
					setHasMore({});
					setLivenessQrUrl(null);
					rawPaymentsRef.current = [];
					rawTransactionsRef.current = [];
				}}
			/>

			{clientData?.user?.first_name ? (
				<>
					<div className="mt-6 space-y-4">
						<div className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
							{/* Primary Information - Most Important */}
							<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
								<div className="space-y-3">
									<h3 className="text-lg font-semibold text-neutral-100">Información Personal</h3>
									<div className="space-y-2">
										<div className="flex min-h-[40px] items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
											<span className="text-sm text-neutral-400">Nombre</span>
											<div className="flex items-center gap-2">
												<span className="text-sm text-neutral-100">{clientData.user.first_name}</span>
												<button
													onClick={() => {
														setEditDialogProps({
															title: 'Editar nombre',
															fields: [
																{
																	type: 'input',
																	name: 'new_value',
																	label: 'Nuevo nombre',
																	placeholder: 'Ingrese el nuevo nombre',
																	defaultValue: clientData.user.first_name
																}
															],
															onConfirm: async (values) => {
																return changeName(String(values?.new_value || ''));
															},
															onClose: () => setEditDialogProps(null)
														});
													}}
													className="rounded-full p-1 hover:bg-neutral-700/50"
												>
													<Pencil className="h-3.5 w-3.5 text-neutral-400 hover:text-neutral-300" />
												</button>
											</div>
										</div>

										<div className="flex min-h-[40px] items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
											<span className="text-sm text-neutral-400">Apellidos</span>
											<div className="flex items-center gap-2">
												<span className="text-sm text-neutral-100">{clientData.user.last_name}</span>
												<button
													onClick={() => {
														setEditDialogProps({
															title: 'Editar apellidos',
															fields: [
																{
																	type: 'input',
																	name: 'new_value',
																	label: 'Nuevos apellidos',
																	placeholder: 'Ingrese los nuevos apellidos',
																	defaultValue: clientData.user.last_name
																}
															],
															onConfirm: async (values) => {
																return changeLastName(String(values?.new_value || ''));
															},
															onClose: () => setEditDialogProps(null)
														});
													}}
													className="rounded-full p-1 hover:bg-neutral-700/50"
												>
													<Pencil className="h-3.5 w-3.5 text-neutral-400 hover:text-neutral-300" />
												</button>
											</div>
										</div>

										<div className="flex min-h-[40px] items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
											<span className="text-sm text-neutral-400">{userData?.country === 'peru' ? 'DNI' : 'Cédula'}</span>
											<div className="flex items-center gap-2">
												<span className="text-sm text-neutral-100">{remove_prefix_from_identification(clientData.user.identification_number)}</span>
												<button
													onClick={() => {
														if (!isUserBiometricallyAuthenticated) {
															showAuthRequiredDialog();
															return;
														}
														setEditDialogProps({
															title: 'Editar Cédula o DNI',
															fields: [
																{
																	type: 'input',
																	name: 'new_value',
																	label: 'Nueva Cédula o DNI',
																	placeholder: 'Ingrese la nueva Cédula o DNI',
																	defaultValue: remove_prefix_from_identification(clientData.user.identification_number)
																}
															],
															onConfirm: async (values) => {
																return changeIdentificationNumber(String(values?.new_value || ''));
															},
															onClose: () => setEditDialogProps(null)
														});
													}}
													className="rounded-full p-1 hover:bg-neutral-700/50"
												>
													<Pencil className="h-3.5 w-3.5 text-neutral-400 hover:text-neutral-300" />
												</button>
											</div>
										</div>

										<div className="flex min-h-[40px] items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
											<span className="text-sm text-neutral-400">Email</span>
											<div className="flex items-center gap-2">
												<span className="max-w-[200px] truncate text-sm text-neutral-100">{clientData.user.email}</span>
												<button
													onClick={() => {
														if (!isUserBiometricallyAuthenticated) {
															showAuthRequiredDialog();
															return;
														}
														setEditDialogProps({
															title: 'Editar Email',
															fields: [
																{
																	type: 'input',
																	name: 'new_value',
																	label: 'Nuevo email',
																	placeholder: 'Ingrese el nuevo email',
																	defaultValue: clientData.user.email
																}
															],
															onConfirm: async (values) => {
																const newEmail = String(values?.new_value || '')
																	.trim()
																	.toLowerCase();
																if (!validate_email(newEmail)) {
																	return 'Error: El email no es válido.';
																} else if (newEmail === clientData.user.email) {
																	return 'Error: El email no puede ser el mismo.';
																}
																if (['admin', 'manager'].includes(userData?.access_type || '')) {
																	const result = await changeEmail(clientData!.user.email, newEmail);
																	if (result !== true) setDialogMessage(String(result));
																} else {
																	setTwoFactorContext({ mode: 'email', email: newEmail });
																}
																return true; // close the input dialog; 2FA dialog will open
															},
															onClose: () => setEditDialogProps(null)
														});
													}}
													className="rounded-full p-1 hover:bg-neutral-700/50"
												>
													<Pencil className="h-3.5 w-3.5 text-neutral-400 hover:text-neutral-300" />
												</button>
											</div>
										</div>

										<div className="flex min-h-[40px] items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
											<span className="text-sm text-neutral-400">Teléfono</span>
											<div className="flex items-center gap-2">
												<span className="text-sm text-neutral-100">{clientData.user.phone_number}</span>
												<button
													onClick={() => {
														if (!isUserBiometricallyAuthenticated) {
															showAuthRequiredDialog();
															return;
														}
														setEditDialogProps({
															title: 'Editar Teléfono',
															fields: [
																{
																	type: 'input',
																	name: 'new_value',
																	label: 'Nuevo teléfono',
																	placeholder: 'Ingrese el nuevo teléfono',
																	defaultValue: clientData.user.phone_number
																}
															],
															onConfirm: async (values) => {
																const newPhone = String(values?.new_value || '').trim();
																if (!validate_phone_number(newPhone || '', clientData?.user?.country || '')) {
																	return 'Error: El número de teléfono no es válido.';
																} else if (newPhone === clientData.user.phone_number) {
																	return 'Error: El número de teléfono no puede ser el mismo.';
																}
																if (['admin', 'manager'].includes(userData?.access_type || '')) {
																	const result = await changePhoneNumber(newPhone);
																	if (result !== true) setDialogMessage(String(result));
																} else {
																	setTwoFactorContext({ mode: 'phone', phone: newPhone });
																}
																return true; // close the input dialog; 2FA dialog will open
															},
															onClose: () => setEditDialogProps(null)
														});
													}}
													className="rounded-full p-1 hover:bg-neutral-700/50"
												>
													<Pencil className="h-3.5 w-3.5 text-neutral-400 hover:text-neutral-300" />
												</button>
											</div>
										</div>
									</div>
								</div>

								<div className="space-y-3">
									<h3 className="text-lg font-semibold text-neutral-100">Estado de Cuenta</h3>
									<div className="space-y-2">
										<div className="flex min-h-[40px] items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
											<span className="text-sm text-neutral-400">Estado</span>
											<div className="flex items-center gap-2">
												<span
													className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${clientData.user.blacklisted ? 'cursor-help bg-yellow-500/10 text-yellow-400' : clientData.user.is_active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
													title={clientData.user.blacklisted ? getBlacklistTooltip(clientData.user.blacklist_data) : undefined}
												>
													{clientData.user.blacklisted ? 'Vetado' : clientData.user.is_active ? 'Activo' : 'Inactivo'}
													{clientData.user.blacklisted && getBlacklistTooltip(clientData.user.blacklist_data) && <Info className="size-3" />}
												</span>
												<Button
													className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
													variant="secondary"
													onClick={() => {
														if (!isUserBiometricallyAuthenticated) {
															showAuthRequiredDialog();
															return;
														}
														setConfirmDialogProps({
															title: 'Restablecer contraseña',
															message: '¿Está seguro que desea restablecer la contraseña?',
															onConfirm: async () => resetPassword(),
															onClose: () => setConfirmDialogProps(null)
														});
													}}
												>
													<span className="flex shrink-0 items-center gap-1">
														<Lock className="size-3.5 shrink-0" />
														<span className="overflow-hidden text-ellipsis whitespace-nowrap">Restablecer contraseña</span>
													</span>
												</Button>
											</div>
										</div>

										<div className="grid grid-cols-2 gap-2">
											<div className="flex min-h-[40px] items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
												<span className="text-sm text-neutral-400">Puntos</span>
												<span className="rounded bg-green-500/10 px-2 py-1 text-xs font-medium text-green-400">
													{clientData.user.effective_points ?? 'N/A'}
												</span>
											</div>

											<div className="flex min-h-[40px] items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
												<span className="text-sm text-neutral-400">Nivel</span>
												<div className="flex items-center gap-1">
													{clientData.user?.level && (
														<span
															className="rounded px-2 py-1 text-xs font-medium"
															style={{
																backgroundColor: 'black',
																color:
																	clientData.user.level === 'Azul'
																		? '#45C4D6'
																		: clientData.user.level === 'Plata'
																			? '#C0C0C0'
																			: clientData.user.level === 'Oro'
																				? '#D4AF37'
																				: clientData.user.level === 'Platino'
																					? '#E6E8E6'
																					: ''
															}}
														>
															{clientData.user.level}
														</span>
													)}
													<Button
														className="!h-auto !w-fit !rounded !px-1 !py-1 !text-xs !font-normal"
														variant="secondary"
														onClick={() => {
															setConfirmDialogProps({
																title: 'Recalcular nivel',
																message: '¿Está seguro que desea recalcular el nivel del usuario?',
																onConfirm: async () => recalculateLevel(),
																onClose: () => setConfirmDialogProps(null)
															});
														}}
													>
														<RefreshCcw className="size-3.5 shrink-0" />
													</Button>
												</div>
											</div>
										</div>

										<div className="flex min-h-[40px] items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
											<span className="text-sm text-neutral-400">Estado de registro</span>
											<span className="text-sm text-neutral-100">{clientData.user.registration_status}</span>
										</div>

										<div className="flex min-h-[40px] items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
											<span className="text-sm text-neutral-400">Fotos</span>
											<div className="flex items-center gap-2">
												{clientData.user.photo_id_url || clientData.user.identification_id_url ? (
													<>
														{clientData.user.photo_id_url && (
															<button
																onClick={() =>
																	setPhotoDialog({
																		type: 'selfie',
																		currentUrl: clientData.user.photo_id_url
																	})
																}
																className="rounded bg-neutral-700/50 px-2 py-1 text-xs text-neutral-200 transition-colors hover:bg-neutral-700"
															>
																Ver Selfie
															</button>
														)}
														{clientData.user.identification_id_url && (
															<button
																onClick={() => {
																	setPhotoDocIdType(clientData.user.identification_type || '');
																	setPhotoDocCountry(clientData.user.country || '');
																	setPhotoDialog({
																		type: 'documento',
																		currentUrl: clientData.user.identification_id_url
																	});
																}}
																className="rounded bg-neutral-700/50 px-2 py-1 text-xs text-neutral-200 transition-colors hover:bg-neutral-700"
															>
																Ver Documento
															</button>
														)}
													</>
												) : (
													<span className="text-xs text-neutral-500">Sin fotos</span>
												)}
											</div>
										</div>
										<div className="flex min-h-[40px] items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
											<span className="text-sm text-neutral-400">Cuenta creada</span>
											<span className="text-sm text-neutral-100">
												{new Date(clientData.user.created_at).toLocaleDateString('es-ES', {
													year: 'numeric',
													month: 'short',
													day: 'numeric'
												})}
											</span>
										</div>
									</div>
								</div>
							</div>

							{/* Credit Lines - Financial Information */}
							<div className="border-t border-neutral-800 pt-4">
								<h3 className="mb-3 text-lg font-semibold text-neutral-100">Líneas de Crédito</h3>
								<div
									className={`grid grid-cols-1 gap-3 ${clientData.user.shared_credit_line_is_active ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}
								>
									<div className="rounded-lg border border-blue-500/20 bg-gradient-to-r from-blue-500/10 to-blue-600/10 p-3 light:border-blue-500/40 light:from-blue-500/20 light:to-blue-600/20 dark:border-blue-500/20 dark:from-blue-500/10 dark:to-blue-600/10">
										<div className="mb-1 flex items-center justify-between">
											<span className="text-sm font-medium text-blue-300 light:text-blue-700 dark:text-blue-300">
												Línea Celular
											</span>
											{['ata', 'admin', 'manager'].includes(userData?.access_type || '') && (
												<button
													onClick={() => {
														setEditDialogProps({
															title: 'Editar límite máximo de crédito',
															fields: [
																{
																	type: 'input',
																	name: 'new_value',
																	label: 'Límite de crédito',
																	placeholder: 'Ingrese',
																	defaultValue: clientData.user.max_loan_approved_3months?.toString() || ''
																}
															],
															onConfirm: async (values) => {
																return changeCreditLine(values?.new_value?.toString() || '', 'primary');
															},
															onClose: () => setEditDialogProps(null)
														});
													}}
													className="rounded-full p-1 hover:bg-blue-500/20 light:hover:bg-blue-500/30 dark:hover:bg-blue-500/20"
												>
													<Pencil className="h-3.5 w-3.5 text-blue-400 hover:text-blue-300 light:text-blue-600 light:hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300" />
												</button>
											)}
										</div>
										<div className="text-base font-semibold text-neutral-100 light:text-neutral-200 dark:text-neutral-100">
											{currency_code === 'S/' ? '' : currency_code}
											{(clientData.user.available_loan_approved || 0).toFixed(2)}
											<span className="text-sm font-normal text-neutral-400 light:text-neutral-600 dark:text-neutral-400">
												{' '}
												/ {currency_code === 'S/' ? '' : currency_code}
												{(clientData.user.max_loan_approved_3months || 0).toFixed(2)}
											</span>
										</div>
										<div className="mt-0.5 text-xs text-blue-300 light:text-blue-700 dark:text-blue-300">
											Disponible / Límite máximo
										</div>
									</div>

									{!clientData.user.shared_credit_line_is_active && (
										<div className="rounded-lg border border-neutral-500/20 bg-gradient-to-r from-neutral-500/10 to-neutral-600/10 p-3 light:border-neutral-500/40 light:from-neutral-500/20 light:to-neutral-600/20 dark:border-neutral-500/20 dark:from-neutral-500/10 dark:to-neutral-600/10">
											<div className="mb-1 flex items-center justify-between">
												<span className="text-sm font-medium text-neutral-300 light:text-neutral-500 dark:text-neutral-300">
													Línea Secundaria
												</span>
												{['ata', 'admin', 'manager'].includes(userData?.access_type || '') && (
													<button
														onClick={() => {
															setEditDialogProps({
																title: 'Editar límite máximo de crédito',
																fields: [
																	{
																		type: 'input',
																		name: 'new_value',
																		label: 'Límite de crédito',
																		placeholder: 'Ingrese',
																		defaultValue: clientData.user.max_loan_approved_3months_secondary?.toString() || ''
																	}
																],
																onConfirm: async (values) => {
																	return changeCreditLine(values?.new_value?.toString() || '', 'secondary');
																},
																onClose: () => setEditDialogProps(null)
															});
														}}
														className="rounded-full p-1 hover:bg-neutral-500/20 light:hover:bg-neutral-500/30 dark:hover:bg-neutral-500/20"
													>
														<Pencil className="h-3.5 w-3.5 text-neutral-400 hover:text-neutral-300 light:text-neutral-600 light:hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-300" />
													</button>
												)}
											</div>
											<div className="text-base font-semibold text-neutral-100 light:text-neutral-200 dark:text-neutral-100">
												{currency_code === 'S/' ? '' : currency_code}
												{(clientData.user.available_loan_approved_secondary || 0).toFixed(2)}
												<span className="text-sm font-normal text-neutral-400 light:text-neutral-600 dark:text-neutral-400">
													{' '}
													/ {currency_code === 'S/' ? '' : currency_code}
													{(clientData.user.max_loan_approved_3months_secondary || 0).toFixed(2)}
												</span>
											</div>
											<div className="mt-0.5 text-xs text-neutral-300 light:text-neutral-500 dark:text-neutral-300">
												Disponible / Límite máximo
											</div>
										</div>
									)}

									<div className="rounded-lg border border-emerald-500/20 bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 p-3 light:border-emerald-500/40 light:from-emerald-500/20 light:to-emerald-600/20 dark:border-emerald-500/20 dark:from-emerald-500/10 dark:to-emerald-600/10">
										<div className="mb-1">
											<span className="text-sm font-medium text-emerald-300 light:text-emerald-700 dark:text-emerald-300">
												Línea General
											</span>
											<p className="text-xs text-neutral-400">Préstamos, recargas, otros y más</p>
										</div>
										<div className="text-base font-semibold text-neutral-100 light:text-neutral-200 dark:text-neutral-100">
											{currency_code === 'S/' ? '' : currency_code}
											{clientData.user.available_personal_loan_credit_line?.toFixed(2) ?? 'N/A'}
											<span className="text-sm font-normal text-neutral-400">
												{' '}
												/ {currency_code === 'S/' ? '' : currency_code}
												{clientData.user.personal_loan_credit_line?.toFixed(2) ?? 'N/A'}
											</span>
										</div>
										<div className="mt-0.5 text-xs text-emerald-300 light:text-emerald-700 dark:text-emerald-300">
											Disponible / Límite máximo
										</div>
									</div>
								</div>
							</div>

							{/* The customer's own financing conditions (everyone sees them, admins edit) */}
							<CustomerFinancingOverrides
								user_email={clientData.user.email}
								country={internalCountry.get()}
								userData={userData}
								currency_code={currency_code}
							/>

							{/* Admin Actions */}
							<div className="border-t border-neutral-800/50 pt-4">
								<div className="mb-3">
									<h3 className="text-lg font-semibold text-neutral-100">Acciones Administrativas</h3>
								</div>

								<div className="space-y-3">
									{/* Verification Section */}
									<div className="rounded-lg border border-neutral-700/50 bg-neutral-800/30 p-4">
										<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
											<div className="flex items-center gap-3">
												<div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500/10">
													<ScanFace className="h-4 w-4 text-blue-400" />
												</div>
												<div>
													<h4 className="text-sm font-medium text-neutral-100">Verificación de Identidad</h4>
													<p className="text-xs text-neutral-400">Generar enlace biométrico</p>
													{/* Authentication Status */}
													<div className="mt-1 flex items-center gap-1.5">
														<div
															className={`h-2 w-2 rounded-full ${isUserBiometricallyAuthenticated ? 'bg-green-400' : 'bg-red-400'}`}
														/>
														<span className={`text-xs ${isUserBiometricallyAuthenticated ? 'text-green-400' : 'text-red-400'}`}>
															{isUserBiometricallyAuthenticated ? 'Autenticado' : 'No autenticado'}
														</span>
													</div>
												</div>
											</div>
											<div className="flex items-center gap-2">
												<Button
													Icon={<Search className="size-3.5" />}
													loading={livenessGenerating}
													className="h-8 bg-blue-600 px-3 text-xs text-white hover:bg-blue-700"
													onClick={async () => {
														if (isUserBiometricallyAuthenticated) {
															setDialogMessage('El usuario ya está autenticado biométricamente.');
															return;
														}

														try {
															setLivenessGenerating(true);
															setLivenessQrUrl(null);
															if (!clientData?.user?.identification_number) {
																setDialogMessage('No hay cédula/DNI del usuario.');
																return;
															}
															const userIdForQuery = clientData.user.identification_number;
															const response = (await unsafeAPI(
																'GET',
																`https://api.krece.app/authentication/liveness-detection-session/?user_identification_number=${encodeURIComponent(userIdForQuery)}`,
																null,
																userData,
																internalCountry.get(),
																false
															)) as { success?: boolean; data?: { session_id?: string; document_id?: string } } | null;
															if (response?.success && response?.data?.session_id && response?.data?.document_id) {
																const qrUrl = `${window.location.origin}/liveness-detection?liveness_session_id=${response.data.session_id}&document_id=${response.data.document_id}&user_identification_number=${encodeURIComponent(userIdForQuery)}&theme=dark&origin=admin`;
																setLivenessQrUrl(qrUrl);
																await navigator.clipboard.writeText(qrUrl);
																setDialogMessage('Enlace generado y copiado.');
															} else {
																setDialogMessage('No se pudo generar el enlace. Intente de nuevo.');
															}
														} catch (error) {
															setDialogMessage('Ocurrió un error generando el enlace.');
														} finally {
															setLivenessGenerating(false);
														}
													}}
													variant="default"
												>
													Generar
												</Button>
												{livenessQrUrl && (
													<Button
														Icon={<Copy className="size-3.5" />}
														className="h-8 bg-emerald-600 px-3 text-xs text-white hover:bg-emerald-700"
														onClick={async () => {
															try {
																await navigator.clipboard.writeText(livenessQrUrl);
																setDialogMessage('Enlace copiado.');
															} catch {
																setDialogMessage('No se pudo copiar el enlace.');
															}
														}}
														variant="default"
													>
														Copiar
													</Button>
												)}
												{clientData?.user?.last_liveness_session_photo_url && isUserBiometricallyAuthenticated && (
													<Button
														Icon={<Eye className="size-3.5" />}
														className="h-8 bg-purple-600 px-3 text-xs text-white hover:bg-purple-700"
														onClick={() => {
															window.open(clientData?.user?.last_liveness_session_photo_url, '_blank');
														}}
														variant="default"
													>
														Ver resultado
													</Button>
												)}
												{userData?.access_type === 'admin' && (
													<Button
														Icon={<ScanFace className="size-3.5" />}
														className="h-8 bg-orange-600 px-3 text-xs text-white hover:bg-orange-700"
														onClick={() => {
															setConfirmDialogProps({
																title: 'Marcar como autenticado',
																message:
																	'¿Está seguro que desea marcar este usuario como autenticado biométricamente? Esta acción debe usarse solo en casos excepcionales.',
																onConfirm: async () => {
																	try {
																		const response = (await unsafeAPI(
																			'POST',
																			'https://api.krece.app/administration/lastsuccessfulliveness/',
																			{
																				user_email: clientData!.user.email
																			},
																			userData,
																			internalCountry.get()
																		)) as { success?: boolean; status?: string };

																		if (response?.success === true) {
																			setDialogMessage('Usuario marcado como autenticado exitosamente.');
																			await searchClientData(); // Refresh data
																		} else {
																			return response?.status || 'Error al marcar como autenticado.';
																		}
																	} catch (error) {
																		return 'Error: ' + (error as Error).message;
																	}
																	return true;
																},
																onClose: () => setConfirmDialogProps(null)
															});
														}}
														variant="default"
													>
														Marcar autenticado
													</Button>
												)}
											</div>
										</div>
									</div>

									{can_recover_identity(userData, recoveryCountry) && clientData.user.country === recoveryCountry && (
										<div className="rounded-lg border border-primary-500/60 bg-primary-500/5 p-4">
											<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
												<div className="flex items-center gap-3">
													<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-500/10">
														<ShieldCheck className="h-4 w-4 text-primary-400 light:text-primary-700" />
													</div>
													<div>
														<h4 className="text-sm font-medium text-neutral-100">Recuperar identidad</h4>
														<p className="text-xs text-neutral-400">Validar al titular y retirar la cuenta reportada</p>
													</div>
												</div>
												<Button
													Icon={<ShieldCheck className="size-3.5" />}
													variant="primary"
													className="!w-auto h-8 px-3 text-xs"
													disabled={loading || !clientData.user.identification_number}
													onClick={() => setIdentityRecoveryClient({
														identification_number: clientData.user.identification_number,
														email: clientData.user.email
													})}
												>
													Recuperar identidad
												</Button>
											</div>
										</div>
									)}

									{/* Admin Actions Section */}
									{['ata', 'admin', 'manager'].includes(userData?.access_type || '') && (
										<div className="rounded-lg border border-red-500/20 bg-red-900/10 p-4">
											<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
												<div className="flex items-center gap-3">
													<div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500/10">
														<Ban className="h-4 w-4 text-red-400" />
													</div>
													<div>
														<h4 className="text-sm font-medium text-neutral-100">Acciones Críticas</h4>
														<p className="text-xs text-neutral-400">Usar con precaución</p>
													</div>
												</div>
												<div className="flex items-center gap-2">
													<Button
														Icon={<Ban className="size-3.5" />}
														onClick={() => {
															setEditDialogProps({
																title: 'Vetar cuenta',
																fields: [
																	{
																		type: 'select',
																		name: 'reason',
																		label: 'Razón del bloqueo',
																		options: [
																			{
																				label: 'Usuario pidió eliminación de cuenta',
																				value: 'Usuario pidió eliminación de cuenta'
																			},
																			{ label: 'Estafador', value: 'Estafador' },
																			{ label: 'Usuario problemático', value: 'Usuario problemático' },
																			{ label: 'Error de registro', value: 'Error de registro' },
																			{ label: 'Otro', value: 'Otro' }
																		]
																	},
																	{
																		type: 'textarea',
																		name: 'comment',
																		label: 'Comentario opcional',
																		placeholder: 'Agrega contexto para el equipo interno',
																		required: false
																	}
																],
																onConfirm: async (values) => await blacklistAccount(values.reason as string, values.comment as string),
																onClose: () => setEditDialogProps(null)
															});
														}}
														className="h-8 bg-yellow-600 px-3 text-xs text-white hover:bg-yellow-700"
														variant="default"
													>
														Vetar
													</Button>
													<Button
														Icon={<ShieldOff className="size-3.5" />}
														onClick={() => {
															setConfirmDialogProps({
																title: 'Quitar veto',
																message: '¿Estás seguro de que deseas remover este cliente de la lista de clientes vetados?',
																onConfirm: async () => await removeBlacklistAccount(),
																onClose: () => setConfirmDialogProps(null)
															});
														}}
														className="h-8 bg-green-600 px-3 text-xs text-white hover:bg-green-700"
														variant="default"
													>
														Quitar veto
													</Button>
													<Button
														Icon={<Trash className="size-3.5" />}
														onClick={() => {
															setConfirmDialogProps({
																title: 'Eliminar cuenta',
																message: `¿Estás seguro de que deseas eliminar esta cuenta?`,
																onConfirm: async () => await deleteAccount(),
																onClose: () => setConfirmDialogProps(null)
															});
														}}
														className="h-8 bg-red-600 px-3 text-xs text-white hover:bg-red-700"
														variant="default"
													>
														Eliminar
													</Button>
												</div>
											</div>
										</div>
									)}

									{canFraudTransfer && (
										<div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-4">
											<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
												<div className="flex items-center gap-3">
													<div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-500/10">
														<AlertTriangle className="h-4 w-4 text-yellow-400" />
													</div>
													<div>
														<h4 className="text-sm font-medium text-neutral-100">Transferencia por fraude</h4>
														<p className="text-xs text-neutral-400">Mueve un equipo al dueño correcto y veta al usuario original</p>
													</div>
												</div>
												<Button
													Icon={<AlertTriangle className="size-3.5" />}
													variant="destructive"
													className="h-8 !w-fit px-3 text-xs"
													onClick={openFraudTransferDialog}
												>
													Abrir transferencia
												</Button>
											</div>
										</div>
									)}

									{/* Otras Acciones Section */}
									<div className="rounded-lg border border-neutral-700/50 bg-neutral-800/30 p-4">
										<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
											<div className="flex items-center gap-3">
												<div className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-500/10">
													<Plus className="h-4 w-4 text-neutral-400" />
												</div>
												<div>
													<h4 className="text-sm font-medium text-neutral-100">Otras Acciones</h4>
													<p className="text-xs text-neutral-400">Acciones adicionales</p>
												</div>
											</div>
											<div className="flex items-center gap-2">
												<Button
													Icon={<Plus className="size-3.5" />}
													onClick={() => {
														setCrearRecargaForm({
															phone_number: clientData?.user?.phone_number || '',
															amount: '',
															date: '',
															xid: ''
														});
														setCrearRecargaOpen(true);
													}}
													className="h-8 bg-blue-600 px-3 text-xs text-white hover:bg-blue-700"
													variant="default"
												>
													Crear Recarga
												</Button>
												{clientData?.user?.country === 'peru' && (
													<Button
														Icon={
															<RefreshCcw
																className={`size-3.5 ${syncKashioLoading ? 'animate-spin' : ''}`}
															/>
														}
														onClick={syncKashio}
														disabled={syncKashioLoading}
														className="h-8 bg-amber-600 px-3 text-xs text-white hover:bg-amber-700"
														variant="default"
													>
														Sync Kashio
													</Button>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="mt-6 space-y-4">
						<div>
							<h2 className="text-2xl font-semibold text-neutral-100">Equipos</h2>
							<p className="text-sm text-neutral-400">Celulares, tablets y laptops</p>
						</div>
						{clientData?.devices?.filter((device) =>
							['phone', 'tablet', 'laptop'].includes(device?.device_type?.toLowerCase())
						)?.length > 0 ? (
							<div className="mt-6 grid gap-3">
								{clientData.devices
									.filter((device) => ['phone', 'tablet', 'laptop'].includes(device?.device_type?.toLowerCase()))
									.toSorted((a, b) => new Date(b.date_bought).getTime() - new Date(a.date_bought).getTime())
									.map((device) => (
										<details key={device.id} className="group rounded-lg border border-neutral-800 bg-neutral-900/50">
											<summary className="cursor-pointer list-none p-6">
												<div className="no-scrollbar flex items-center justify-between gap-6 overflow-x-auto">
													<div className="flex shrink-0 items-center gap-2">
														<div className="flex items-center gap-2">
															<svg
																className="h-5 w-5 text-neutral-400 transition-transform group-open:rotate-90"
																fill="none"
																viewBox="0 0 24 24"
																stroke="currentColor"
															>
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
															</svg>
															<span
																className={`rounded bg-neutral-500/10 px-2 py-1 text-xs text-neutral-500 dark:bg-neutral-500/20 dark:text-neutral-400`}
															>
																{ucfirst(device.device_type)}
															</span>
															<span
																className={`rounded px-2 py-1 text-xs ${!device.is_locked ? 'bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-400' : 'bg-red-500/10 text-red-700 dark:bg-red-500/20 dark:text-red-400'}`}
															>
																{!device.is_locked ? 'Activo' : 'Bloqueado'}
															</span>
															<h3 className="flex items-center gap-1 text-xl font-semibold text-neutral-100">
																{device.device_commercial_name
																	? device.device_commercial_name
																	: `${device.device_manufacturer} ${device.device_model}`}
																<button
																	onClick={() => {
																		setEditDialogProps({
																			title: 'Editar nombre comercial',
																			fields: [
																				{
																					type: 'input',
																					name: 'new_value',
																					label: 'Nombre comercial',
																					placeholder: 'Ingrese',
																					defaultValue:
																						device.device_commercial_name ||
																						`${device.device_manufacturer} ${device.device_model}`
																				}
																			],
																			onConfirm: async (values) => {
																				return changeCommercialName(device.id, values?.new_value?.toString() || '');
																			},
																			onClose: () => setEditDialogProps(null)
																		});
																	}}
																	className="hover:text-neutral-300"
																>
																	<Pencil className="h-4 w-4" />
																</button>
																{renderInsuranceSummaryBadge(device)}
																{renderDeletedDeviceBadge(device)}
															</h3>
														</div>
														<div className="ml-4 flex items-center gap-1">
															<span
																className={`rounded px-2 py-1 text-xs ${device.device_management_provider !== 'nuovo' ? 'bg-blue-500/10 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400' : 'bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400'}`}
															>
																{ucfirst(device.device_management_provider || '')}
															</span>
															<span className="text-neutral-400">/</span>
															<span
																className={`rounded px-2 py-1 text-xs ${device.is_released ? 'bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-400' : 'bg-yellow-500/10 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400'}`}
															>
																{device.is_released ? 'Liberado' : 'Anclado'}
															</span>
														</div>
													</div>

													<div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
														{device.is_stolen && (
															<span className="shrink-0 rounded bg-red-500/10 px-2 py-1 text-xs text-red-700 dark:bg-red-500/20 dark:text-red-400">
																Robado
															</span>
														)}
														<span
															className={`rounded bg-neutral-500/10 px-2 py-1 text-xs text-neutral-700 dark:bg-neutral-500/20 dark:text-neutral-400`}
														>
															{device.seller_store}
														</span>
														<span className="text-sm text-neutral-400">{new Date(device.date_bought).toLocaleDateString()}</span>
													</div>
												</div>
											</summary>

											<div className="px-6 pb-6">
												<div className="mb-4 border-b border-neutral-800 pb-4">
													<div className="mt-4 space-y-1.5 text-sm md:space-y-4">
														<div className="grid grid-cols-1 gap-1.5 md:grid-cols-3 md:gap-4">
															<div className="rounded bg-neutral-800/30 p-3">
																<div className="mb-1 flex items-center justify-between">
																	<p className="text-xs font-medium text-neutral-300">
																		{device.device_type?.toLowerCase() === 'laptop' ? 'Serial Number' : 'IMEI'}
																	</p>
																	{['admin', 'manager', 'ata'].includes(userData?.access_type || '') && (
																		<button
																			onClick={() => {
																				setEditDialogProps({
																					title: device.device_type?.toLowerCase() === 'laptop' ? 'Editar serial' : 'Editar IMEI',
																					fields: [
																						{
																							type: 'input',
																							name: 'new_identifier',
																							label: device.device_type?.toLowerCase() === 'laptop' ? 'Serial' : 'IMEI',
																							placeholder:
																								device.device_type?.toLowerCase() === 'laptop'
																									? 'Ingrese el serial'
																									: 'Ingrese el IMEI',
																							defaultValue:
																								device.device_type?.toLowerCase() === 'laptop'
																									? device.device_serial_no || ''
																									: device.device_imei_no || ''
																						}
																					],
																					onConfirm: async (values) => {
																						return changeDeviceIdentifier(
																							device.id,
																							values?.new_identifier?.toString() || '',
																							device.device_type || ''
																						);
																					},
																					onClose: () => setEditDialogProps(null)
																				});
																			}}
																			className="hover:text-neutral-300"
																		>
																			<Pencil className="h-4 w-4" />
																		</button>
																	)}
																</div>
																<div className="flex items-center">
																	<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																		{device.device_type?.toLowerCase() === 'laptop'
																			? device.device_serial_no || 'No registrado'
																			: device.device_imei_no || 'No registrado'}
																	</p>
																	<Copy
																		onClick={() =>
																			navigator.clipboard.writeText(
																				device.device_type?.toLowerCase() === 'laptop'
																					? device.device_serial_no || ''
																					: device.device_imei_no || ''
																			)
																		}
																		className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																	/>
																</div>
															</div>
															<div className="rounded bg-neutral-800/30 p-3">
																<p className="mb-1 text-xs font-medium text-neutral-300">ID de equipo</p>
																<div className="flex items-center">
																	<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																		{device.nuovopay_device_id}
																	</p>
																	<Copy
																		onClick={() => navigator.clipboard.writeText(String(device.nuovopay_device_id))}
																		className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																	/>
																</div>
															</div>
															<div className="rounded bg-neutral-800/30 p-3">
																<p className="mb-1 text-xs font-medium text-neutral-300">ID</p>
																<div className="flex items-center">
																	<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																		{device.id}
																	</p>
																	<Copy
																		onClick={() => navigator.clipboard.writeText(device.id)}
																		className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																	/>
																</div>
															</div>
															{device.device_serial_no && device.device_type?.toLowerCase() === 'iphone' && (
																<div className="rounded bg-neutral-800/30 p-3">
																	<p className="mb-1 text-xs font-medium text-neutral-300">Serial Number</p>
																	<div className="flex items-center">
																		<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																			{device.device_serial_no}
																		</p>
																		<Copy
																			onClick={() => navigator.clipboard.writeText(device.device_serial_no || '')}
																			className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																		/>
																	</div>
																</div>
															)}
														</div>
														<div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 md:gap-4">
															<div className="flex flex-col space-y-1.5 md:space-y-2">
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Fecha de compra</span>
																	<span className="text-neutral-100">{new Date(device.date_bought).toLocaleDateString()}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Precio</span>
																	<div className="flex items-center gap-1">
																		<span className="text-neutral-100">
																			{currency_code}
																			{device.device_price?.toFixed(2)}
																		</span>
																		{['ata', 'admin', 'manager'].includes(userData?.access_type || '') && (
																			<button
																				onClick={() => {
																					setEditDialogProps({
																						title: 'Editar precio',
																						fields: [
																							{
																								type: 'input',
																								name: 'new_value',
																								label: 'Precio de venta',
																								placeholder: 'Ingrese el precio de venta',
																								defaultValue: device.device_price?.toFixed(2)
																							}
																						],
																						onConfirm: async (values) => {
																							return changeSalePrice(device.id, values?.new_value?.toString() || '');
																						},
																						onClose: () => setEditDialogProps(null)
																					});
																				}}
																				className="hover:text-neutral-300"
																			>
																				<Pencil className="h-4 w-4" />
																			</button>
																		)}
																	</div>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Monto financiado</span>
																	<span className="text-neutral-100">
																		{currency_code}
																		{device.amount_financed?.toFixed(2)}
																	</span>
																</div>
															</div>

															<div className="flex flex-col space-y-1.5 md:space-y-2">
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Tienda</span>
																	<span className="text-neutral-100">{device.seller_store}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Local</span>
																	<span className="text-neutral-100">{device.sub_store}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Email del comprador</span>
																	<span className="ml-4 truncate text-neutral-100">{device.user}</span>
																</div>
															</div>
														</div>
													</div>
													<div className="mt-4 flex flex-wrap gap-3">
														<Button
															loading={checkingDeviceStatus}
															onClick={() => checkDeviceStatus(device.device_imei_no || '')}
															className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-blue-500/10 px-4 py-3 text-sm font-medium text-blue-400 transition-all hover:bg-blue-500/20 hover:text-blue-300 disabled:opacity-50"
															Icon={<Search className="h-4 w-4" />}
														>
															Consultar estado
														</Button>
														<Button
															onClick={() =>
																setConfirmDialogProps({
																	title: 'Cambiar fechas de pago',
																	message: '¿Está seguro que desea cambiar las fechas de pago a 15 y último?',
																	onConfirm: async () => changePaymentDate15and30(device.id),
																	onClose: () => setConfirmDialogProps(null)
																})
															}
															className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-orange-500/10 px-4 py-3 text-sm font-medium text-orange-400 transition-all hover:bg-orange-500/20 hover:text-orange-300"
															Icon={<CalendarSync className="h-4 w-4" />}
														>
															Cambiar fechas
														</Button>
														<Button
															onClick={() =>
																setConfirmDialogProps({
																	title: 'Desbloquear equipo',
																	message: '¿Está seguro que desea desbloquear este equipo?',
																	onConfirm: async () => unlockDevice(device.id),
																	onClose: () => setConfirmDialogProps(null)
																})
															}
															className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-green-500/10 px-4 py-3 text-sm font-medium text-green-400 transition-all hover:bg-green-500/20 hover:text-green-300"
															Icon={<Unlock className="h-4 w-4" />}
														>
															Desbloquear
														</Button>
														<Button
															onClick={() =>
																setEditDialogProps({
																	title: 'Liberar equipo',
																	fields: [
																		{
																			type: 'select',
																			name: 'reason',
																			label: 'Razón de la liberación',
																			options: [
																				{ label: 'Temporal', value: 'Temporal' },
																				{ label: 'Equipo pago', value: 'Equipo pago' }
																			]
																		}
																	],
																	onConfirm: async (values) =>
																		await freeDevice(device.id, values.reason as string),
																	onClose: () => setEditDialogProps(null)
																})
															}
															className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-green-500/10 px-4 py-3 text-sm font-medium text-green-400 transition-all hover:bg-green-500/20 hover:text-green-300"
															Icon={<ShieldOff className="h-4 w-4" />}
														>
															Liberar equipo
														</Button>
														<Button
															onClick={() => openStolenDeviceDialog(device)}
															className={cn(
																'flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-medium transition-all',
																device.is_stolen
																	? 'bg-green-500/10 text-green-400 hover:bg-green-500/20 hover:text-green-300'
																	: 'bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300'
															)}
															Icon={device.is_stolen ? <ShieldCheck className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
														>
															{device.is_stolen ? 'Reportar encontrado' : 'Reportar robado'}
														</Button>
													</div>
												</div>

												{renderInsuranceDetails(device)}

												<div className="mb-4 border-b border-neutral-800 pb-4">
													<div className="mb-4 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center md:gap-0">
														<div className="flex items-center gap-3">
															<h4 className="font-medium text-neutral-100">Cuotas</h4>
															<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
																{device.payments.length}
															</span>
														</div>{' '}
														<div className="flex items-center gap-2">
															{userData?.access_type === 'admin' && (
																<Button
																	onClick={() => {
																		setEditDialogProps({
																			title: 'Actualizar monto de cuotas',
																			fields: [
																				{
																					type: 'input',
																					name: 'actual_amount',
																					label: 'Nuevo monto de cuotas',
																					placeholder: 'Ingrese el monto',
																					defaultValue: device.payments?.[0]?.actualAmount?.toFixed(2) || ''
																				}
																			],
																			onConfirm: async (values) => {
																				return updateActualAmount(device.id, values?.actual_amount?.toString() || '');
																			},
																			onClose: () => setEditDialogProps(null)
																		});
																	}}
																	className="rounded-lg bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-neutral-700"
																	Icon={<Pencil className="h-4 w-4" />}
																>
																	Editar cuotas
																</Button>
															)}
															{(['admin', 'tecnoplan', 'manager'].includes(userData?.access_type || '') ||
																[
																	'ortegayoel967@gmail.com',
																	'jesusjns2045@gmail.com',
																	'cesar09405@gmail.com',
																	'cobranzard@krece.app',
																	'christian@krece.app',
																	'rodriguezdariamny@gmail.com'
																].includes(userData?.email || '')) && (
																<Button
																	onClick={() => {
																		setSplitCuotaDeviceID(device.id);
																	}}
																	className="rounded-lg bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-neutral-700"
																	Icon={<Split className="h-4 w-4 rotate-180" />}
																>
																	Dividir cuota
																</Button>
															)}
														</div>
														<div className="flex w-full flex-col items-start gap-3 rounded-lg bg-neutral-800/50 px-4 py-2 text-sm sm:flex-row sm:items-center md:w-auto">
															<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																<span className="text-neutral-400">Total pagado</span>
																<span className="font-medium text-green-400">
																	{currency_code}
																	{device.payments.reduce((sum, p) => sum + (p.amountPaid || 0), 0).toFixed(2)}
																</span>
															</div>
															<div className="hidden h-6 w-px bg-neutral-700 sm:block"></div>
															<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																<span className="text-neutral-400">Total a pagar</span>
																<span className="font-medium text-neutral-100">
																	{currency_code}
																	{device.payments.reduce((sum, p) => sum + p.actualAmount, 0).toFixed(2)}
																</span>
															</div>
														</div>
													</div>

													<div className="space-y-3">
														{device.payments
															.toSorted((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
															.map((payment) => {
																const isPastDue = new Date(payment.dueDate) < new Date() && !payment.paid;

																return (
																	<div
																		key={payment.id}
																		className="relative rounded-lg border border-neutral-800 bg-neutral-900/30 p-4"
																	>
																		<div className="mb-3 flex flex-col justify-between md:flex-row md:items-start">
																			<div>
																				<div className="mb-1 flex items-center gap-2">
																					<span className="font-medium text-neutral-100">
																						{new Date(payment.dueDate).toLocaleDateString()}
																					</span>
																					<div className="flex gap-1.5">
																						{isPastDue && (
																							<span className="rounded bg-red-500/10 px-2 py-0.5 text-xs text-red-700 dark:bg-red-500/20 dark:text-red-400">
																								Vencida
																							</span>
																						)}
																						<span
																							className={`rounded px-2 py-0.5 text-xs ${
																								payment.paid
																									? 'bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-400'
																									: 'bg-neutral-500/10 text-neutral-700 dark:bg-neutral-500/20 dark:text-neutral-400'
																							}`}
																						>
																							{payment.paid ? 'Pagado' : 'Pendiente'}
																						</span>
																					</div>
																				</div>
																				<div className="text-xs text-neutral-400">
																					ID: <span className="select-all font-mono">{payment.id}</span>
																					<Copy
																						onClick={() => navigator.clipboard.writeText(payment.id)}
																						className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
																					/>
																					{payment.confirmationNumber && (
																						<>
																							{' '}
																							• Conf: <span className="select-all font-mono">{payment.confirmationNumber}</span>
																						</>
																					)}
																				</div>
																			</div>

																			<div className="mt-3 text-left md:mt-0 md:text-right">
																				<div className="mb-1 flex items-baseline gap-2">
																					<span className="text-sm text-neutral-400">Monto:</span>
																					<span className="font-medium text-neutral-100">
																						{currency_code}
																						{payment.actualAmount?.toFixed(2)}
																					</span>
																					<span className="text-neutral-400">•</span>
																					<span className="text-sm text-neutral-400">Pendiente:</span>
																					<span className="font-medium text-neutral-100">
																						{currency_code}
																						{(payment.actualAmount - payment.amountPaid)?.toFixed(2)}
																					</span>
																				</div>
																				<div className="flex items-baseline gap-2 text-sm">
																					<span className="text-neutral-400">Pagado:</span>
																					<span className={payment.amountPaid ? 'text-green-400' : 'text-neutral-500'}>
																						{currency_code}
																						{payment.amountPaid?.toFixed(2) || 0}
																					</span>
																					{userData?.access_type === 'admin' && (
																						<button
																							onClick={() => {
																								setEditDialogProps({
																									title: 'Editar monto',
																									fields: [
																										{
																											type: 'input',
																											name: 'amount_paid',
																											label: 'Monto pagado',
																											placeholder: 'Ingrese el monto pagado',
																											defaultValue: (payment?.amountPaid || 0).toFixed(2)
																										}
																									],
																									onConfirm: async (values) => {
																										return updateAmountPaid(payment.id, values?.amount_paid?.toString() || '');
																									},
																									onClose: () => setEditDialogProps(null)
																								});
																							}}
																							className="relative right-[2px] top-[2px] hover:text-neutral-300"
																						>
																							<Pencil className="h-4 w-4" />
																						</button>
																					)}

																					<span className="text-neutral-400">•</span>
																					<span className="text-neutral-400">Confirmado:</span>
																					<span className={payment.amount_confirmed ? 'text-blue-400' : 'text-neutral-500'}>
																						{currency_code}
																						{payment.amount_confirmed?.toFixed(2) || 0}
																					</span>
																				</div>
																			</div>
																		</div>

																		<div className="flex items-center gap-2 text-xs">
																			<span className="text-neutral-400">Estado:</span>
																			<span className={`${payment.is_active ? 'text-blue-400' : 'text-neutral-500'}`}>
																				{payment.is_active ? 'Activo' : 'Inactivo'}
																			</span>
																			<span className="text-neutral-400">•</span>
																			<span className="text-neutral-400">Revisado por Krece:</span>
																			<span
																				className={`${payment.checked_by_internal_team ? 'text-green-400' : 'text-neutral-500'}`}
																			>
																				{payment.checked_by_internal_team ? 'Sí' : 'No'}
																			</span>
																		</div>
																	</div>
																);
															})}
													</div>
												</div>
												<details className="group/transactions">
													<summary className="cursor-pointer list-none">
														<div className="mb-4 flex flex-col justify-between gap-3 md:flex-row md:items-center md:gap-0">
															<div className="flex items-center gap-2">
																<svg
																	className="h-5 w-5 text-neutral-400 transition-transform group-open/transactions:rotate-90"
																	fill="none"
																	viewBox="0 0 24 24"
																	stroke="currentColor"
																>
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
																</svg>
																<div className="flex items-center gap-3">
																	<h4 className="font-medium text-neutral-100">Transacciones</h4>
																	<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
																		{device.transactions.length}
																	</span>
																</div>{' '}
															</div>
															<div className="flex w-full flex-col items-start gap-3 rounded-lg bg-neutral-800/50 px-4 py-2 text-sm sm:flex-row sm:items-center md:w-auto">
																<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																	<span className="text-neutral-400">Total transacciones</span>
																	<span className="font-medium text-neutral-100">
																		{currency_code}
																		{device.transactions.reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
																	</span>
																</div>
															</div>
														</div>
													</summary>

													<div className="space-y-3">
														{device.transactions
															.toSorted((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
															.map((transaction) => (
																<div
																	key={transaction.id}
																	className="relative rounded-lg border border-neutral-800 bg-neutral-900/30 p-4"
																>
																	<div className="mb-3 flex flex-col justify-between md:flex-row md:items-start">
																		<div>
																			<div className="mb-1 flex items-center gap-2">
																				<span className="font-medium text-neutral-100">
																					{new Date(transaction.date).toLocaleDateString()}
																				</span>
																				<span
																					className={`rounded bg-blue-500/10 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-500/20 dark:text-blue-400`}
																				>
																					Pagado a {transaction.paid_to}
																				</span>
																			</div>
																			<div className="text-xs text-neutral-400">
																				ID: <span className="select-all font-mono">{transaction.id}</span>
																				<Copy
																					onClick={() => navigator.clipboard.writeText(transaction.id)}
																					className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
																				/>
																				{transaction.related_payment && (
																					<>
																						{' '}
																						• Cuota: <span className="select-all font-mono">{transaction.related_payment}</span>
																					</>
																				)}
																			</div>
																		</div>

																		<div className="mt-3 text-left md:mt-0 md:text-right">
																			<div className="mb-1 flex items-baseline gap-2">
																				<span className="text-sm text-neutral-400">Monto:</span>
																				<span className="font-medium text-neutral-100">
																					{currency_code}
																					{transaction.amount?.toFixed(2)}
																				</span>
																			</div>
																		</div>
																	</div>

																	<div className="flex items-center gap-2 text-xs">
																		<span className="text-neutral-400">Ref. Pago:</span>
																		<span className="select-all font-mono text-neutral-100">{transaction.reference || '-'}</span>
																		<span className="text-neutral-400">•</span>
																		<span className="text-neutral-400">Ref. Cash:</span>
																		<span className="select-all font-mono text-neutral-100">
																			{transaction.confirmation_number || '-'}
																		</span>
																	</div>

																	{['admin', 'manager'].includes(userData?.access_type || '') && (
																		<div className="absolute right-3 top-3 flex gap-2 md:bottom-3 md:top-auto">
																			<button
																				onClick={() => {
																					setEditDialogProps({
																						title: 'Reembolsar transacción',
																						fields: [
																							{
																								type: 'input',
																								name: 'id_document',
																								label: 'Cédula o DNI',
																								placeholder: 'Ingrese la cédula o DNI',
																								defaultValue: ''
																							},
																							{
																								type: 'input',
																								name: 'account_number',
																								label: 'Número de cuenta',
																								placeholder: 'Ingrese el número de cuenta',
																								defaultValue: ''
																							},
																							{
																								type: 'input',
																								name: 'refund_amount',
																								label: 'Monto a reembolsar',
																								placeholder: 'Ingrese el monto',
																								defaultValue: transaction.amount?.toFixed(2) || '',
																								allowedChars: 'numeric'
																							}
																						],
																						onConfirm: async (values) => {
																							return refundTransaction(
																								transaction.id,
																								String(values?.id_document || ''),
																								String(values?.account_number || ''),
																								parseFloat(String(values?.refund_amount || '0'))
																							);
																						},
																						onClose: () => setEditDialogProps(null)
																					});
																				}}
																				className="rounded-lg bg-blue-500/20 p-1 hover:bg-blue-500/30"
																			>
																				<BanknoteArrowDown className="size-5 text-blue-500" />
																			</button>
																			<button
																				onClick={() => {
																					setConfirmDialogProps({
																						title: 'Eliminar transacción',
																						message: `¿Estás seguro de que deseas eliminar esta transacción de ${currency_code}${transaction.amount?.toFixed(2)}?`,
																						onConfirm: async () => await deleteTransaction(transaction.id),
																						onClose: () => setConfirmDialogProps(null)
																					});
																				}}
																				className="rounded-lg bg-red-500/20 p-1 hover:bg-red-500/30"
																			>
																				<Trash className="size-5 text-red-500" />
																			</button>
																		</div>
																	)}
																</div>
															))}
													</div>
												</details>
											</div>
										</details>
									))}
								{hasMore.equipos && (
									<div className="mt-3 flex justify-center">
										<Button
											onClick={() => loadMoreDevices('equipos')}
											disabled={loadingMore === 'equipos'}
											variant="outline"
											className="border-neutral-700 text-sm text-neutral-400"
										>
											{loadingMore === 'equipos' ? <Spinner size={1} /> : 'Cargar más'}
										</Button>
									</div>
								)}
							</div>
						) : (
							<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-center text-sm text-neutral-300">
								No se encontraron equipos
							</div>
						)}
					</div>
					<div className="mt-6 space-y-4">
						<div>
							<h2 className="text-2xl font-semibold text-neutral-100">iPhones</h2>
							<p className="text-sm text-neutral-400">Apple iPhones</p>
						</div>
						{clientData?.devices?.filter((device) =>
							['iphone'].includes(device?.device_type?.toLowerCase())
						)?.length > 0 ? (
							<div className="mt-6 grid gap-3">
								{clientData.devices
									.filter((device) => ['iphone'].includes(device?.device_type?.toLowerCase()))
									.toSorted((a, b) => new Date(b.date_bought).getTime() - new Date(a.date_bought).getTime())
									.map((device) => (
										<details key={device.id} className="group rounded-lg border border-neutral-800 bg-neutral-900/50">
											<summary className="cursor-pointer list-none p-6">
												<div className="no-scrollbar flex items-center justify-between gap-6 overflow-x-auto">
													<div className="flex shrink-0 items-center gap-2">
														<div className="flex items-center gap-2">
															<svg
																className="h-5 w-5 text-neutral-400 transition-transform group-open:rotate-90"
																fill="none"
																viewBox="0 0 24 24"
																stroke="currentColor"
															>
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
															</svg>
															<span
																className={`rounded bg-neutral-500/10 px-2 py-1 text-xs text-neutral-500 dark:bg-neutral-500/20 dark:text-neutral-400`}
															>
																{ucfirst(device.device_type)}
															</span>
															<span
																className={`rounded px-2 py-1 text-xs ${!device.is_locked ? 'bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-400' : 'bg-red-500/10 text-red-700 dark:bg-red-500/20 dark:text-red-400'}`}
															>
																{!device.is_locked ? 'Activo' : 'Bloqueado'}
															</span>
															<h3 className="flex items-center gap-1 text-xl font-semibold text-neutral-100">
																{device.device_commercial_name
																	? device.device_commercial_name
																	: `${device.device_manufacturer} ${device.device_model}`}
																<button
																	onClick={() => {
																		setEditDialogProps({
																			title: 'Editar nombre comercial',
																			fields: [
																				{
																					type: 'input',
																					name: 'new_value',
																					label: 'Nombre comercial',
																					placeholder: 'Ingrese',
																					defaultValue:
																						device.device_commercial_name ||
																						`${device.device_manufacturer} ${device.device_model}`
																				}
																			],
																			onConfirm: async (values) => {
																				return changeCommercialName(device.id, values?.new_value?.toString() || '');
																			},
																			onClose: () => setEditDialogProps(null)
																		});
																	}}
																	className="hover:text-neutral-300"
																>
																	<Pencil className="h-4 w-4" />
																</button>
																{renderInsuranceSummaryBadge(device)}
																{renderDeletedDeviceBadge(device)}
															</h3>
														</div>
														<div className="ml-4 flex items-center gap-1">
															<span
																className={`rounded px-2 py-1 text-xs ${device.device_management_provider !== 'nuovo' ? 'bg-blue-500/10 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400' : 'bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400'}`}
															>
																{ucfirst(device.device_management_provider || '')}
															</span>
															<span className="text-neutral-400">/</span>
															<span
																className={`rounded px-2 py-1 text-xs ${device.is_released ? 'bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-400' : 'bg-yellow-500/10 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400'}`}
															>
																{device.is_released ? 'Liberado' : 'Anclado'}
															</span>
														</div>
													</div>

													<div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
														{device.is_stolen && (
															<span className="shrink-0 rounded bg-red-500/10 px-2 py-1 text-xs text-red-700 dark:bg-red-500/20 dark:text-red-400">
																Robado
															</span>
														)}
														<span
															className={`rounded bg-neutral-500/10 px-2 py-1 text-xs text-neutral-700 dark:bg-neutral-500/20 dark:text-neutral-400`}
														>
															{device.seller_store}
														</span>
														<span className="text-sm text-neutral-400">{new Date(device.date_bought).toLocaleDateString()}</span>
													</div>
												</div>
											</summary>

											<div className="px-6 pb-6">
												<div className="mb-4 border-b border-neutral-800 pb-4">
													<div className="mt-4 space-y-1.5 text-sm md:space-y-4">
														<div className="grid grid-cols-1 gap-1.5 md:grid-cols-3 md:gap-4">
															<div className="rounded bg-neutral-800/30 p-3">
																<div className="mb-1 flex items-center justify-between">
																	<p className="text-xs font-medium text-neutral-300">
																		{device.device_type?.toLowerCase() === 'laptop' ? 'Serial Number' : 'IMEI'}
																	</p>
																	{['admin', 'manager', 'ata'].includes(userData?.access_type || '') && (
																		<button
																			onClick={() => {
																				setEditDialogProps({
																					title: device.device_type?.toLowerCase() === 'laptop' ? 'Editar serial' : 'Editar IMEI',
																					fields: [
																						{
																							type: 'input',
																							name: 'new_identifier',
																							label: device.device_type?.toLowerCase() === 'laptop' ? 'Serial' : 'IMEI',
																							placeholder:
																								device.device_type?.toLowerCase() === 'laptop'
																									? 'Ingrese el serial'
																									: 'Ingrese el IMEI',
																							defaultValue:
																								device.device_type?.toLowerCase() === 'laptop'
																									? device.device_serial_no || ''
																									: device.device_imei_no || ''
																						}
																					],
																					onConfirm: async (values) => {
																						return changeDeviceIdentifier(
																							device.id,
																							values?.new_identifier?.toString() || '',
																							device.device_type || ''
																						);
																					},
																					onClose: () => setEditDialogProps(null)
																				});
																			}}
																			className="hover:text-neutral-300"
																		>
																			<Pencil className="h-4 w-4" />
																		</button>
																	)}
																</div>
																<div className="flex items-center">
																	<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																		{device.device_type?.toLowerCase() === 'laptop'
																			? device.device_serial_no || 'No registrado'
																			: device.device_imei_no || 'No registrado'}
																	</p>
																	<Copy
																		onClick={() =>
																			navigator.clipboard.writeText(
																				device.device_type?.toLowerCase() === 'laptop'
																					? device.device_serial_no || ''
																					: device.device_imei_no || ''
																			)
																		}
																		className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																	/>
																</div>
															</div>
															<div className="rounded bg-neutral-800/30 p-3">
																<p className="mb-1 text-xs font-medium text-neutral-300">ID de equipo</p>
																<div className="flex items-center">
																	<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																		{device.nuovopay_device_id}
																	</p>
																	<Copy
																		onClick={() => navigator.clipboard.writeText(String(device.nuovopay_device_id))}
																		className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																	/>
																</div>
															</div>
															<div className="rounded bg-neutral-800/30 p-3">
																<p className="mb-1 text-xs font-medium text-neutral-300">ID</p>
																<div className="flex items-center">
																	<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																		{device.id}
																	</p>
																	<Copy
																		onClick={() => navigator.clipboard.writeText(device.id)}
																		className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																	/>
																</div>
															</div>
															{device.device_serial_no && device.device_type?.toLowerCase() === 'iphone' && (
																<div className="rounded bg-neutral-800/30 p-3">
																	<p className="mb-1 text-xs font-medium text-neutral-300">Serial Number</p>
																	<div className="flex items-center">
																		<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																			{device.device_serial_no}
																		</p>
																		<Copy
																			onClick={() => navigator.clipboard.writeText(device.device_serial_no || '')}
																			className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																		/>
																	</div>
																</div>
															)}
														</div>
														<div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 md:gap-4">
															<div className="flex flex-col space-y-1.5 md:space-y-2">
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Fecha de compra</span>
																	<span className="text-neutral-100">{new Date(device.date_bought).toLocaleDateString()}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Precio</span>
																	<div className="flex items-center gap-1">
																		<span className="text-neutral-100">
																			{currency_code}
																			{device.device_price?.toFixed(2)}
																		</span>
																		{['ata', 'admin', 'manager'].includes(userData?.access_type || '') && (
																			<button
																				onClick={() => {
																					setEditDialogProps({
																						title: 'Editar precio',
																						fields: [
																							{
																								type: 'input',
																								name: 'new_value',
																								label: 'Precio de venta',
																								placeholder: 'Ingrese el precio de venta',
																								defaultValue: device.device_price?.toFixed(2)
																							}
																						],
																						onConfirm: async (values) => {
																							return changeSalePrice(device.id, values?.new_value?.toString() || '');
																						},
																						onClose: () => setEditDialogProps(null)
																					});
																				}}
																				className="hover:text-neutral-300"
																			>
																				<Pencil className="h-4 w-4" />
																			</button>
																		)}
																	</div>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Monto financiado</span>
																	<span className="text-neutral-100">
																		{currency_code}
																		{device.amount_financed?.toFixed(2)}
																	</span>
																</div>
															</div>

															<div className="flex flex-col space-y-1.5 md:space-y-2">
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Tienda</span>
																	<span className="text-neutral-100">{device.seller_store}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Local</span>
																	<span className="text-neutral-100">{device.sub_store}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Email del comprador</span>
																	<span className="ml-4 truncate text-neutral-100">{device.user}</span>
																</div>
															</div>
														</div>
													</div>
													<div className="mt-4 flex flex-wrap gap-3">
														<Button
															loading={checkingDeviceStatus}
															onClick={() => checkDeviceStatus(device.device_imei_no || '')}
															className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-blue-500/10 px-4 py-3 text-sm font-medium text-blue-400 transition-all hover:bg-blue-500/20 hover:text-blue-300 disabled:opacity-50"
															Icon={<Search className="h-4 w-4" />}
														>
															Consultar estado
														</Button>
														<Button
															onClick={() =>
																setConfirmDialogProps({
																	title: 'Cambiar fechas de pago',
																	message: '¿Está seguro que desea cambiar las fechas de pago a 15 y último?',
																	onConfirm: async () => changePaymentDate15and30(device.id),
																	onClose: () => setConfirmDialogProps(null)
																})
															}
															className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-orange-500/10 px-4 py-3 text-sm font-medium text-orange-400 transition-all hover:bg-orange-500/20 hover:text-orange-300"
															Icon={<CalendarSync className="h-4 w-4" />}
														>
															Cambiar fechas
														</Button>
														<Button
															onClick={() =>
																setConfirmDialogProps({
																	title: 'Desbloquear equipo',
																	message: '¿Está seguro que desea desbloquear este equipo?',
																	onConfirm: async () => unlockDevice(device.id),
																	onClose: () => setConfirmDialogProps(null)
																})
															}
															className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-green-500/10 px-4 py-3 text-sm font-medium text-green-400 transition-all hover:bg-green-500/20 hover:text-green-300"
															Icon={<Unlock className="h-4 w-4" />}
														>
															Desbloquear
														</Button>
														<Button
															onClick={() =>
																setEditDialogProps({
																	title: 'Liberar equipo',
																	fields: [
																		{
																			type: 'select',
																			name: 'reason',
																			label: 'Razón de la liberación',
																			options: [
																				{ label: 'Temporal', value: 'Temporal' },
																				{ label: 'Equipo pago', value: 'Equipo pago' }
																			]
																		}
																	],
																	onConfirm: async (values) =>
																		await freeDevice(device.id, values.reason as string),
																	onClose: () => setEditDialogProps(null)
																})
															}
															className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-green-500/10 px-4 py-3 text-sm font-medium text-green-400 transition-all hover:bg-green-500/20 hover:text-green-300"
															Icon={<ShieldOff className="h-4 w-4" />}
														>
															Liberar equipo
														</Button>
														<Button
															onClick={() => openStolenDeviceDialog(device)}
															className={cn(
																'flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-medium transition-all',
																device.is_stolen
																	? 'bg-green-500/10 text-green-400 hover:bg-green-500/20 hover:text-green-300'
																	: 'bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300'
															)}
															Icon={device.is_stolen ? <ShieldCheck className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
														>
															{device.is_stolen ? 'Reportar encontrado' : 'Reportar robado'}
														</Button>
													</div>
												</div>

												{renderInsuranceDetails(device)}

												<div className="mb-4 border-b border-neutral-800 pb-4">
													<div className="mb-4 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center md:gap-0">
														<div className="flex items-center gap-3">
															<h4 className="font-medium text-neutral-100">Cuotas</h4>
															<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
																{device.payments.length}
															</span>
														</div>{' '}
														<div className="flex items-center gap-2">
															{userData?.access_type === 'admin' && (
																<Button
																	onClick={() => {
																		setEditDialogProps({
																			title: 'Actualizar monto de cuotas',
																			fields: [
																				{
																					type: 'input',
																					name: 'actual_amount',
																					label: 'Nuevo monto de cuotas',
																					placeholder: 'Ingrese el monto',
																					defaultValue: device.payments?.[0]?.actualAmount?.toFixed(2) || ''
																				}
																			],
																			onConfirm: async (values) => {
																				return updateActualAmount(device.id, values?.actual_amount?.toString() || '');
																			},
																			onClose: () => setEditDialogProps(null)
																		});
																	}}
																	className="rounded-lg bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-neutral-700"
																	Icon={<Pencil className="h-4 w-4" />}
																>
																	Editar cuotas
																</Button>
															)}
															{(['admin', 'tecnoplan', 'manager'].includes(userData?.access_type || '') ||
																[
																	'ortegayoel967@gmail.com',
																	'jesusjns2045@gmail.com',
																	'cesar09405@gmail.com',
																	'cobranzard@krece.app',
																	'christian@krece.app',
																	'rodriguezdariamny@gmail.com'
																].includes(userData?.email || '')) && (
																<Button
																	onClick={() => {
																		setSplitCuotaDeviceID(device.id);
																	}}
																	className="rounded-lg bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-neutral-700"
																	Icon={<Split className="h-4 w-4 rotate-180" />}
																>
																	Dividir cuota
																</Button>
															)}
														</div>
														<div className="flex w-full flex-col items-start gap-3 rounded-lg bg-neutral-800/50 px-4 py-2 text-sm sm:flex-row sm:items-center md:w-auto">
															<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																<span className="text-neutral-400">Total pagado</span>
																<span className="font-medium text-green-400">
																	{currency_code}
																	{device.payments.reduce((sum, p) => sum + (p.amountPaid || 0), 0).toFixed(2)}
																</span>
															</div>
															<div className="hidden h-6 w-px bg-neutral-700 sm:block"></div>
															<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																<span className="text-neutral-400">Total a pagar</span>
																<span className="font-medium text-neutral-100">
																	{currency_code}
																	{device.payments.reduce((sum, p) => sum + p.actualAmount, 0).toFixed(2)}
																</span>
															</div>
														</div>
													</div>

													<div className="space-y-3">
														{device.payments
															.toSorted((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
															.map((payment) => {
																const isPastDue = new Date(payment.dueDate) < new Date() && !payment.paid;

																return (
																	<div
																		key={payment.id}
																		className="relative rounded-lg border border-neutral-800 bg-neutral-900/30 p-4"
																	>
																		<div className="mb-3 flex flex-col justify-between md:flex-row md:items-start">
																			<div>
																				<div className="mb-1 flex items-center gap-2">
																					<span className="font-medium text-neutral-100">
																						{new Date(payment.dueDate).toLocaleDateString()}
																					</span>
																					<div className="flex gap-1.5">
																						{isPastDue && (
																							<span className="rounded bg-red-500/10 px-2 py-0.5 text-xs text-red-700 dark:bg-red-500/20 dark:text-red-400">
																								Vencida
																							</span>
																						)}
																						<span
																							className={`rounded px-2 py-0.5 text-xs ${
																								payment.paid
																									? 'bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-400'
																									: 'bg-neutral-500/10 text-neutral-700 dark:bg-neutral-500/20 dark:text-neutral-400'
																							}`}
																						>
																							{payment.paid ? 'Pagado' : 'Pendiente'}
																						</span>
																					</div>
																				</div>
																				<div className="text-xs text-neutral-400">
																					ID: <span className="select-all font-mono">{payment.id}</span>
																					<Copy
																						onClick={() => navigator.clipboard.writeText(payment.id)}
																						className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
																					/>
																					{payment.confirmationNumber && (
																						<>
																							{' '}
																							• Conf: <span className="select-all font-mono">{payment.confirmationNumber}</span>
																						</>
																					)}
																				</div>
																			</div>

																			<div className="mt-3 text-left md:mt-0 md:text-right">
																				<div className="mb-1 flex items-baseline gap-2">
																					<span className="text-sm text-neutral-400">Monto:</span>
																					<span className="font-medium text-neutral-100">
																						{currency_code}
																						{payment.actualAmount?.toFixed(2)}
																					</span>
																					<span className="text-neutral-400">•</span>
																					<span className="text-sm text-neutral-400">Pendiente:</span>
																					<span className="font-medium text-neutral-100">
																						{currency_code}
																						{(payment.actualAmount - payment.amountPaid)?.toFixed(2)}
																					</span>
																				</div>
																				<div className="flex items-baseline gap-2 text-sm">
																					<span className="text-neutral-400">Pagado:</span>
																					<span className={payment.amountPaid ? 'text-green-400' : 'text-neutral-500'}>
																						{currency_code}
																						{payment.amountPaid?.toFixed(2) || 0}
																					</span>
																					{userData?.access_type === 'admin' && (
																						<button
																							onClick={() => {
																								setEditDialogProps({
																									title: 'Editar monto',
																									fields: [
																										{
																											type: 'input',
																											name: 'amount_paid',
																											label: 'Monto pagado',
																											placeholder: 'Ingrese el monto pagado',
																											defaultValue: (payment?.amountPaid || 0).toFixed(2)
																										}
																									],
																									onConfirm: async (values) => {
																										return updateAmountPaid(payment.id, values?.amount_paid?.toString() || '');
																									},
																									onClose: () => setEditDialogProps(null)
																								});
																							}}
																							className="relative right-[2px] top-[2px] hover:text-neutral-300"
																						>
																							<Pencil className="h-4 w-4" />
																						</button>
																					)}

																					<span className="text-neutral-400">•</span>
																					<span className="text-neutral-400">Confirmado:</span>
																					<span className={payment.amount_confirmed ? 'text-blue-400' : 'text-neutral-500'}>
																						{currency_code}
																						{payment.amount_confirmed?.toFixed(2) || 0}
																					</span>
																				</div>
																			</div>
																		</div>

																		<div className="flex items-center gap-2 text-xs">
																			<span className="text-neutral-400">Estado:</span>
																			<span className={`${payment.is_active ? 'text-blue-400' : 'text-neutral-500'}`}>
																				{payment.is_active ? 'Activo' : 'Inactivo'}
																			</span>
																			<span className="text-neutral-400">•</span>
																			<span className="text-neutral-400">Revisado por Krece:</span>
																			<span
																				className={`${payment.checked_by_internal_team ? 'text-green-400' : 'text-neutral-500'}`}
																			>
																				{payment.checked_by_internal_team ? 'Sí' : 'No'}
																			</span>
																		</div>
																	</div>
																);
															})}
													</div>
												</div>
												<details className="group/transactions">
													<summary className="cursor-pointer list-none">
														<div className="mb-4 flex flex-col justify-between gap-3 md:flex-row md:items-center md:gap-0">
															<div className="flex items-center gap-2">
																<svg
																	className="h-5 w-5 text-neutral-400 transition-transform group-open/transactions:rotate-90"
																	fill="none"
																	viewBox="0 0 24 24"
																	stroke="currentColor"
																>
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
																</svg>
																<div className="flex items-center gap-3">
																	<h4 className="font-medium text-neutral-100">Transacciones</h4>
																	<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
																		{device.transactions.length}
																	</span>
																</div>{' '}
															</div>
															<div className="flex w-full flex-col items-start gap-3 rounded-lg bg-neutral-800/50 px-4 py-2 text-sm sm:flex-row sm:items-center md:w-auto">
																<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																	<span className="text-neutral-400">Total transacciones</span>
																	<span className="font-medium text-neutral-100">
																		{currency_code}
																		{device.transactions.reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
																	</span>
																</div>
															</div>
														</div>
													</summary>

													<div className="space-y-3">
														{device.transactions
															.toSorted((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
															.map((transaction) => (
																<div
																	key={transaction.id}
																	className="relative rounded-lg border border-neutral-800 bg-neutral-900/30 p-4"
																>
																	<div className="mb-3 flex flex-col justify-between md:flex-row md:items-start">
																		<div>
																			<div className="mb-1 flex items-center gap-2">
																				<span className="font-medium text-neutral-100">
																					{new Date(transaction.date).toLocaleDateString()}
																				</span>
																				<span
																					className={`rounded bg-blue-500/10 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-500/20 dark:text-blue-400`}
																				>
																					Pagado a {transaction.paid_to}
																				</span>
																			</div>
																			<div className="text-xs text-neutral-400">
																				ID: <span className="select-all font-mono">{transaction.id}</span>
																				<Copy
																					onClick={() => navigator.clipboard.writeText(transaction.id)}
																					className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
																				/>
																				{transaction.related_payment && (
																					<>
																						{' '}
																						• Cuota: <span className="select-all font-mono">{transaction.related_payment}</span>
																					</>
																				)}
																			</div>
																		</div>

																		<div className="mt-3 text-left md:mt-0 md:text-right">
																			<div className="mb-1 flex items-baseline gap-2">
																				<span className="text-sm text-neutral-400">Monto:</span>
																				<span className="font-medium text-neutral-100">
																					{currency_code}
																					{transaction.amount?.toFixed(2)}
																				</span>
																			</div>
																		</div>
																	</div>

																	<div className="flex items-center gap-2 text-xs">
																		<span className="text-neutral-400">Ref. Pago:</span>
																		<span className="select-all font-mono text-neutral-100">{transaction.reference || '-'}</span>
																		<span className="text-neutral-400">•</span>
																		<span className="text-neutral-400">Ref. Cash:</span>
																		<span className="select-all font-mono text-neutral-100">
																			{transaction.confirmation_number || '-'}
																		</span>
																	</div>

																	{['admin', 'manager'].includes(userData?.access_type || '') && (
																		<div className="absolute right-3 top-3 flex gap-2 md:bottom-3 md:top-auto">
																			<button
																				onClick={() => {
																					setEditDialogProps({
																						title: 'Reembolsar transacción',
																						fields: [
																							{
																								type: 'input',
																								name: 'id_document',
																								label: 'Cédula o DNI',
																								placeholder: 'Ingrese la cédula o DNI',
																								defaultValue: ''
																							},
																							{
																								type: 'input',
																								name: 'account_number',
																								label: 'Número de cuenta',
																								placeholder: 'Ingrese el número de cuenta',
																								defaultValue: ''
																							},
																							{
																								type: 'input',
																								name: 'refund_amount',
																								label: 'Monto a reembolsar',
																								placeholder: 'Ingrese el monto',
																								defaultValue: transaction.amount?.toFixed(2) || '',
																								allowedChars: 'numeric'
																							}
																						],
																						onConfirm: async (values) => {
																							return refundTransaction(
																								transaction.id,
																								String(values?.id_document || ''),
																								String(values?.account_number || ''),
																								parseFloat(String(values?.refund_amount || '0'))
																							);
																						},
																						onClose: () => setEditDialogProps(null)
																					});
																				}}
																				className="rounded-lg bg-blue-500/20 p-1 hover:bg-blue-500/30"
																			>
																				<BanknoteArrowDown className="size-5 text-blue-500" />
																			</button>
																			<button
																				onClick={() => {
																					setConfirmDialogProps({
																						title: 'Eliminar transacción',
																						message: `¿Estás seguro de que deseas eliminar esta transacción de ${currency_code}${transaction.amount?.toFixed(2)}?`,
																						onConfirm: async () => await deleteTransaction(transaction.id),
																						onClose: () => setConfirmDialogProps(null)
																					});
																				}}
																				className="rounded-lg bg-red-500/20 p-1 hover:bg-red-500/30"
																			>
																				<Trash className="size-5 text-red-500" />
																			</button>
																		</div>
																	)}
																</div>
															))}
													</div>
												</details>
											</div>
										</details>
									))}
								{hasMore.iphone && (
									<div className="mt-3 flex justify-center">
										<Button
											onClick={() => loadMoreDevices('iphone')}
											disabled={loadingMore === 'iphone'}
											variant="outline"
											className="border-neutral-700 text-sm text-neutral-400"
										>
											{loadingMore === 'iphone' ? <Spinner size={1} /> : 'Cargar más'}
										</Button>
									</div>
								)}
							</div>
						) : (
							<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-center text-sm text-neutral-300">
								No se encontraron iPhones
							</div>
						)}
					</div>

					<div className="mt-6 space-y-4">
						<div className="flex items-center gap-3">
							<div>
								<h2 className="text-2xl font-semibold text-neutral-100">Recargas</h2>
								<p className="text-sm text-neutral-400">Recargas de teléfonos</p>
							</div>
							{(pendingRecharges.length > 0 || hasMore.pending_recharges) && (
								<button
									onClick={() => setPendingDialog('recharges')}
									className="rounded-lg bg-neutral-800/50 px-2.5 py-1 text-xs text-neutral-300 transition-colors hover:bg-neutral-700/50"
									title="Ver intentos de recarga"
								>
									<span className="flex items-center gap-1.5">
										<Eye className="size-3.5" />
										Intentos ({pendingRecharges.length}{hasMore.pending_recharges ? '+' : ''})
									</span>
								</button>
							)}
						</div>
						{clientData?.devices?.filter((device) => ['recarga'].includes(device?.device_type?.toLowerCase()))?.length > 0 ? (
							<div className="mt-6 grid gap-3">
								{clientData.devices
									.filter((device) => ['recarga'].includes(device?.device_type?.toLowerCase()))
									.toSorted((a, b) => new Date(b.date_bought).getTime() - new Date(a.date_bought).getTime())
									.map((device) => (
										<details key={device.id} className="group rounded-lg border border-neutral-800 bg-neutral-900/50">
											<summary className="cursor-pointer list-none p-6">
												<div className="no-scrollbar flex items-center justify-between gap-6 overflow-x-auto">
													<div className="flex shrink-0 items-center gap-2">
														<div className="flex items-center gap-2">
															<svg
																className="h-5 w-5 text-neutral-400 transition-transform group-open:rotate-90"
																fill="none"
																viewBox="0 0 24 24"
																stroke="currentColor"
															>
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
															</svg>
															<h3 className="flex items-center gap-3 text-xl font-semibold text-neutral-100">
																<span>{device?.product_description || 'Recarga'}</span>
																<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
																	{currency_code}
																	{device.device_price?.toFixed(2)}
																</span>
																{renderDeletedDeviceBadge(device)}
															</h3>
														</div>
													</div>
													<div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
														<span className="text-sm text-neutral-400">{new Date(device.date_bought).toLocaleDateString()}</span>
													</div>
												</div>
											</summary>

											<div className="px-6 pb-6">
												<div className="mb-4 border-b border-neutral-800 pb-4">
													<div className="mt-4 space-y-1.5 text-sm md:space-y-4">
														<div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 md:gap-4">
															<div className="rounded bg-neutral-800/30 p-3">
																<p className="mb-1 text-xs font-medium text-neutral-300">Confirmación</p>
																<div className="flex items-center">
																	<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																		{device.device_confirmation_id}
																	</p>
																	<Copy
																		onClick={() => navigator.clipboard.writeText(String(device.device_confirmation_id))}
																		className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																	/>
																</div>
															</div>
															<div className="rounded bg-neutral-800/30 p-3">
																<p className="mb-1 text-xs font-medium text-neutral-300">ID</p>
																<div className="flex items-center">
																	<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																		{device.id}
																	</p>
																	<Copy
																		onClick={() => navigator.clipboard.writeText(device.id)}
																		className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																	/>
																</div>
															</div>
															{device.recharge_id && (
																<div className="rounded bg-neutral-800/30 p-3">
																	<p className="mb-1 text-xs font-medium text-neutral-300">ID de recarga</p>
																	<div className="flex items-center">
																		<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																			{device.recharge_id}
																		</p>
																		<Copy
																			onClick={() => navigator.clipboard.writeText(device.recharge_id || '')}
																			className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																		/>
																	</div>
																</div>
															)}
														</div>
														<div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 md:gap-4">
															<div className="flex flex-col space-y-1.5 md:space-y-2">
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Fecha de compra</span>
																	<span className="text-neutral-100">{new Date(device.date_bought).toLocaleDateString()}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Precio</span>
																	<span className="text-neutral-100">
																		{currency_code}
																		{device.device_price?.toFixed(2)}
																	</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Monto financiado</span>
																	<span className="text-neutral-100">
																		{currency_code}
																		{device.amount_financed?.toFixed(2)}
																	</span>
																</div>
															</div>

															<div className="flex flex-col space-y-1.5 md:space-y-2">
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Teléfono recargado</span>
																	<span className="text-neutral-100">{device.phone_number}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Operador</span>
																	<span className="text-neutral-100">{ucfirst(device.operator || '')}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Email del comprador</span>
																	<span className="ml-4 truncate text-neutral-100">{device.user}</span>
																</div>
															</div>
														</div>
														<div className="mt-4 flex flex-wrap gap-3">
															<Button
																onClick={() =>
																	setConfirmDialogProps({
																		title: 'Cambiar fechas de pago',
																		message: '¿Está seguro que desea cambiar las fechas de pago a 15 y último?',
																		onConfirm: async () => changePaymentDate15and30(device.id),
																		onClose: () => setConfirmDialogProps(null)
																	})
																}
																className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-orange-500/10 px-4 py-3 text-sm font-medium text-orange-400 transition-all hover:bg-orange-500/20 hover:text-orange-300"
																Icon={<CalendarSync className="h-4 w-4" />}
															>
																Cambiar fechas
															</Button>
															{['admin', 'manager'].includes(userData?.access_type || '') && (
																<Button
																	onClick={() =>
																		setConfirmDialogProps({
																			title: 'Eliminar recarga',
																			message: `¿Está seguro que desea eliminar esta recarga de ${currency_code}${device.device_price?.toFixed(2)}? Esta acción no se puede deshacer.`,
																			onConfirm: async () => await deleteLoan(device.id),
																			onClose: () => setConfirmDialogProps(null)
																		})
																	}
																	className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-red-500/10 px-4 py-3 text-sm font-medium text-red-400 transition-all hover:bg-red-500/20 hover:text-red-300"
																	Icon={<Trash className="h-4 w-4" />}
																>
																	Eliminar recarga
																</Button>
															)}
														</div>
													</div>
												</div>

												<div className="mb-4 border-b border-neutral-800 pb-4">
													<div className="mb-4 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center md:gap-0">
														<div className="flex items-center gap-3">
															<h4 className="font-medium text-neutral-100">Cuotas</h4>
															<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
																{device.payments.length}
															</span>
														</div>{' '}
														{userData?.access_type === 'admin' && (
															<Button
																onClick={() => {
																	setEditDialogProps({
																		title: 'Actualizar monto de cuotas',
																		fields: [
																			{
																				type: 'input',
																				name: 'actual_amount',
																				label: 'Nuevo monto de cuotas',
																				placeholder: 'Ingrese el monto',
																				defaultValue: device.payments?.[0]?.actualAmount?.toFixed(2) || ''
																			}
																		],
																		onConfirm: async (values) => {
																			return updateActualAmount(device.id, values?.actual_amount?.toString() || '');
																		},
																		onClose: () => setEditDialogProps(null)
																	});
																}}
																className="rounded-lg bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-neutral-700"
																Icon={<Pencil className="h-4 w-4" />}
															>
																Editar cuotas
															</Button>
														)}
														{(['admin', 'tecnoplan', 'manager'].includes(userData?.access_type || '') ||
															[
																'ortegayoel967@gmail.com',
																'jesusjns2045@gmail.com',
																'cesar09405@gmail.com',
																'cobranzard@krece.app',
																'christian@krece.app',
																'rodriguezdariamny@gmail.com'
															].includes(userData?.email || '')) && (
															<Button
																onClick={() => {
																	setSplitCuotaDeviceID(device.id);
																}}
																className="rounded-lg bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-neutral-700"
																Icon={<Split className="h-4 w-4 rotate-180" />}
															>
																Dividir cuota
															</Button>
														)}
														<div className="flex w-full flex-col items-start gap-3 rounded-lg bg-neutral-800/50 px-4 py-2 text-sm sm:flex-row sm:items-center md:w-auto">
															<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																<span className="text-neutral-400">Total pagado</span>
																<span className="font-medium text-green-400">
																	{currency_code}
																	{device.payments.reduce((sum, p) => sum + (p.amountPaid || 0), 0).toFixed(2)}
																</span>
															</div>
															<div className="hidden h-6 w-px bg-neutral-700 sm:block"></div>
															<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																<span className="text-neutral-400">Total a pagar</span>
																<span className="font-medium text-neutral-100">
																	{currency_code}
																	{device.payments.reduce((sum, p) => sum + p.actualAmount, 0).toFixed(2)}
																</span>
															</div>
														</div>
													</div>

													<div className="space-y-3">
														{device.payments
															.toSorted((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
															.map((payment) => {
																const isPastDue = new Date(payment.dueDate) < new Date() && !payment.paid;

																return (
																	<div
																		key={payment.id}
																		className="relative rounded-lg border border-neutral-800 bg-neutral-900/30 p-4"
																	>
																		<div className="mb-3 flex flex-col justify-between md:flex-row md:items-start">
																			<div>
																				<div className="mb-1 flex items-center gap-2">
																					<span className="font-medium text-neutral-100">
																						{new Date(payment.dueDate).toLocaleDateString()}
																					</span>
																					<div className="flex gap-1.5">
																						{isPastDue && (
																							<span className="rounded bg-red-500/10 px-2 py-0.5 text-xs text-red-700 dark:bg-red-500/20 dark:text-red-400">
																								Vencida
																							</span>
																						)}
																						<span
																							className={`rounded px-2 py-0.5 text-xs ${
																								payment.paid
																									? 'bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-400'
																									: 'bg-neutral-500/10 text-neutral-700 dark:bg-neutral-500/20 dark:text-neutral-400'
																							}`}
																						>
																							{payment.paid ? 'Pagado' : 'Pendiente'}
																						</span>
																					</div>
																				</div>
																				<div className="text-xs text-neutral-400">
																					ID: <span className="select-all font-mono">{payment.id}</span>
																					<Copy
																						onClick={() => navigator.clipboard.writeText(payment.id)}
																						className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
																					/>
																					{payment.confirmationNumber && (
																						<>
																							{' '}
																							• Conf: <span className="select-all font-mono">{payment.confirmationNumber}</span>
																						</>
																					)}
																				</div>
																			</div>

																			<div className="mt-3 text-left md:mt-0 md:text-right">
																				<div className="mb-1 flex items-baseline gap-2">
																					<span className="text-sm text-neutral-400">Monto:</span>
																					<span className="font-medium text-neutral-100">
																						{currency_code}
																						{payment.actualAmount?.toFixed(2)}
																					</span>
																					<span className="text-neutral-400">•</span>
																					<span className="text-sm text-neutral-400">Pendiente:</span>
																					<span className="font-medium text-neutral-100">
																						{currency_code}
																						{(payment.actualAmount - payment.amountPaid)?.toFixed(2)}
																					</span>
																				</div>
																				<div className="flex items-baseline gap-2 text-sm">
																					<span className="text-neutral-400">Pagado:</span>
																					<span className={payment.amountPaid ? 'text-green-400' : 'text-neutral-500'}>
																						{currency_code}
																						{payment.amountPaid?.toFixed(2) || 0}
																					</span>
																					{userData?.access_type === 'admin' && (
																						<button
																							onClick={() => {
																								setEditDialogProps({
																									title: 'Editar monto',
																									fields: [
																										{
																											type: 'input',
																											name: 'amount_paid',
																											label: 'Monto pagado',
																											placeholder: 'Ingrese el monto pagado',
																											defaultValue: (payment?.amountPaid || 0).toFixed(2)
																										}
																									],
																									onConfirm: async (values) => {
																										return updateAmountPaid(payment.id, values?.amount_paid?.toString() || '');
																									},
																									onClose: () => setEditDialogProps(null)
																								});
																							}}
																							className="relative right-[2px] top-[2px] hover:text-neutral-300"
																						>
																							<Pencil className="h-4 w-4" />
																						</button>
																					)}
																					<span className="text-neutral-400">•</span>
																					<span className="text-neutral-400">Confirmado:</span>
																					<span className={payment.amount_confirmed ? 'text-blue-400' : 'text-neutral-500'}>
																						{currency_code}
																						{payment.amount_confirmed?.toFixed(2) || 0}
																					</span>
																				</div>
																			</div>
																		</div>

																		<div className="flex items-center gap-2 text-xs">
																			<span className="text-neutral-400">Estado:</span>
																			<span className={`${payment.is_active ? 'text-blue-400' : 'text-neutral-500'}`}>
																				{payment.is_active ? 'Activo' : 'Inactivo'}
																			</span>
																			<span className="text-neutral-400">•</span>
																			<span className="text-neutral-400">Revisado por Krece:</span>
																			<span
																				className={`${payment.checked_by_internal_team ? 'text-green-400' : 'text-neutral-500'}`}
																			>
																				{payment.checked_by_internal_team ? 'Sí' : 'No'}
																			</span>
																		</div>
																	</div>
																);
															})}
													</div>
												</div>
												<details className="group/transactions">
													<summary className="cursor-pointer list-none">
														<div className="mb-4 flex flex-col justify-between gap-3 md:flex-row md:items-center md:gap-0">
															<div className="flex items-center gap-2">
																<svg
																	className="h-5 w-5 text-neutral-400 transition-transform group-open/transactions:rotate-90"
																	fill="none"
																	viewBox="0 0 24 24"
																	stroke="currentColor"
																>
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
																</svg>
																<div className="flex items-center gap-3">
																	<h4 className="font-medium text-neutral-100">Transacciones</h4>
																	<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
																		{device.transactions.length}
																	</span>
																</div>{' '}
															</div>
															<div className="flex w-full flex-col items-start gap-3 rounded-lg bg-neutral-800/50 px-4 py-2 text-sm sm:flex-row sm:items-center md:w-auto">
																<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																	<span className="text-neutral-400">Total transacciones</span>
																	<span className="font-medium text-neutral-100">
																		{currency_code}
																		{device.transactions.reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
																	</span>
																</div>
															</div>
														</div>
													</summary>

													<div className="space-y-3">
														{device.transactions
															.toSorted((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
															.map((transaction) => (
																<div
																	key={transaction.id}
																	className="relative rounded-lg border border-neutral-800 bg-neutral-900/30 p-4"
																>
																	<div className="mb-3 flex flex-col justify-between md:flex-row md:items-start">
																		<div>
																			<div className="mb-1 flex items-center gap-2">
																				<span className="font-medium text-neutral-100">
																					{new Date(transaction.date).toLocaleDateString()}
																				</span>
																				<span
																					className={`rounded bg-blue-500/10 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-500/20 dark:text-blue-400`}
																				>
																					Pagado a {transaction.paid_to}
																				</span>
																			</div>
																			<div className="text-xs text-neutral-400">
																				ID: <span className="select-all font-mono">{transaction.id}</span>
																				<Copy
																					onClick={() => navigator.clipboard.writeText(transaction.id)}
																					className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
																				/>
																				{transaction.related_payment && (
																					<>
																						{' '}
																						• Cuota: <span className="select-all font-mono">{transaction.related_payment}</span>
																					</>
																				)}
																			</div>
																		</div>

																		<div className="mt-3 text-left md:mt-0 md:text-right">
																			<div className="mb-1 flex items-baseline gap-2">
																				<span className="text-sm text-neutral-400">Monto:</span>
																				<span className="font-medium text-neutral-100">
																					{currency_code}
																					{transaction.amount?.toFixed(2)}
																				</span>
																			</div>
																		</div>
																	</div>

																	<div className="flex items-center gap-2 text-xs">
																		<span className="text-neutral-400">Ref. Pago:</span>
																		<span className="select-all font-mono text-neutral-100">{transaction.reference || '-'}</span>
																		<span className="text-neutral-400">•</span>
																		<span className="text-neutral-400">Ref. Cash:</span>
																		<span className="select-all font-mono text-neutral-100">
																			{transaction.confirmation_number || '-'}
																		</span>
																	</div>

																	{['admin', 'manager'].includes(userData?.access_type || '') && (
																		<div className="absolute right-3 top-3 flex gap-2 md:bottom-3 md:top-auto">
																			<button
																				onClick={() => {
																					setEditDialogProps({
																						title: 'Reembolsar transacción',
																						fields: [
																							{
																								type: 'input',
																								name: 'id_document',
																								label: 'Cédula o DNI',
																								placeholder: 'Ingrese la cédula o DNI',
																								defaultValue: ''
																							},
																							{
																								type: 'input',
																								name: 'account_number',
																								label: 'Número de cuenta',
																								placeholder: 'Ingrese el número de cuenta',
																								defaultValue: ''
																							},
																							{
																								type: 'input',
																								name: 'refund_amount',
																								label: 'Monto a reembolsar',
																								placeholder: 'Ingrese el monto',
																								defaultValue: transaction.amount?.toFixed(2) || '',
																								allowedChars: 'numeric'
																							}
																						],
																						onConfirm: async (values) => {
																							return refundTransaction(
																								transaction.id,
																								String(values?.id_document || ''),
																								String(values?.account_number || ''),
																								parseFloat(String(values?.refund_amount || '0'))
																							);
																						},
																						onClose: () => setEditDialogProps(null)
																					});
																				}}
																				className="rounded-lg bg-blue-500/20 p-1 hover:bg-blue-500/30"
																			>
																				<BanknoteArrowDown className="size-5 text-blue-500" />
																			</button>
																			<button
																				onClick={() => {
																					setConfirmDialogProps({
																						title: 'Eliminar transacción',
																						message: `¿Estás seguro de que deseas eliminar esta transacción de ${currency_code}${transaction.amount?.toFixed(2)}?`,
																						onConfirm: async () => await deleteTransaction(transaction.id),
																						onClose: () => setConfirmDialogProps(null)
																					});
																				}}
																				className="rounded-lg bg-red-500/20 p-1 hover:bg-red-500/30"
																			>
																				<Trash className="size-5 text-red-500" />
																			</button>
																		</div>
																	)}
																</div>
															))}
													</div>
												</details>
											</div>
										</details>
									))}
								{hasMore.recarga && (
									<div className="mt-3 flex justify-center">
										<Button
											onClick={() => loadMoreDevices('recarga')}
											disabled={loadingMore === 'recarga'}
											variant="outline"
											className="border-neutral-700 text-sm text-neutral-400"
										>
											{loadingMore === 'recarga' ? <Spinner size={1} /> : 'Cargar más'}
										</Button>
									</div>
								)}
							</div>
						) : (
							<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-center text-sm text-neutral-300">
								No se encontraron recargas
							</div>
						)}
					</div>

					<div className="mt-6 space-y-4">
						<div className="flex items-center gap-3">
							<div>
								<h2 className="text-2xl font-semibold text-neutral-100">Prestamos</h2>
								<p className="text-sm text-neutral-400">Prestamos personales</p>
							</div>
							{(pendingLoans.length > 0 || hasMore.pending_loans) && (
								<button
									onClick={() => setPendingDialog('loans')}
									className="rounded-lg bg-neutral-800/50 px-2.5 py-1 text-xs text-neutral-300 transition-colors hover:bg-neutral-700/50"
									title="Ver intentos de préstamo"
								>
									<span className="flex items-center gap-1.5">
										<Eye className="size-3.5" />
										Intentos ({pendingLoans.length}{hasMore.pending_loans ? '+' : ''})
									</span>
								</button>
							)}
						</div>
						{clientData?.devices?.filter((device) => ['loan'].includes(device?.device_type?.toLowerCase()))?.length > 0 ? (
							<div className="mt-6 grid gap-3">
								{clientData.devices
									.filter((device) => ['loan'].includes(device?.device_type?.toLowerCase()))
									.toSorted((a, b) => new Date(b.date_bought).getTime() - new Date(a.date_bought).getTime())
									.map((device) => (
										<details key={device.id} className="group rounded-lg border border-neutral-800 bg-neutral-900/50">
											<summary className="cursor-pointer list-none p-6">
												<div className="no-scrollbar flex items-center justify-between gap-6 overflow-x-auto">
													<div className="flex shrink-0 items-center gap-2">
														<div className="flex items-center gap-2">
															<svg
																className="h-5 w-5 text-neutral-400 transition-transform group-open:rotate-90"
																fill="none"
																viewBox="0 0 24 24"
																stroke="currentColor"
															>
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
															</svg>
															<h3 className="flex items-center gap-3 text-xl font-semibold text-neutral-100">
																<span>{'Prestamo Personal'}</span>
																<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
																	{currency_code}
																	{device.device_price?.toFixed(2)}
																</span>
																{renderDeletedDeviceBadge(device)}
															</h3>
														</div>
													</div>
													<div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
														<span className="text-sm text-neutral-400">{new Date(device.date_bought).toLocaleDateString()}</span>
													</div>
												</div>
											</summary>

											<div className="px-6 pb-6">
												<div className="mb-4 border-b border-neutral-800 pb-4">
													<div className="mt-4 space-y-1.5 text-sm md:space-y-4">
														<div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 md:gap-4">
															<div className="rounded bg-neutral-800/30 p-3">
																<p className="mb-1 text-xs font-medium text-neutral-300">ID de producto</p>
																<div className="flex items-center">
																	<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																		{device.device_confirmation_id || device.nuovopay_device_id}
																	</p>
																	<Copy
																		onClick={() =>
																			navigator.clipboard.writeText(String(device.device_confirmation_id || device.nuovopay_device_id))
																		}
																		className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																	/>
																</div>
															</div>
															<div className="rounded bg-neutral-800/30 p-3">
																<p className="mb-1 text-xs font-medium text-neutral-300">ID</p>
																<div className="flex items-center">
																	<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																		{device.id}
																	</p>
																	<Copy
																		onClick={() => navigator.clipboard.writeText(device.id)}
																		className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																	/>
																</div>
															</div>
														</div>
														<div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 md:gap-4">
															<div className="flex flex-col space-y-1.5 md:space-y-2">
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Fecha de compra</span>
																	<span className="text-neutral-100">{new Date(device.date_bought).toLocaleDateString()}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Precio</span>
																	<div className="flex items-center gap-1">
																		<span className="text-neutral-100">
																			{currency_code}
																			{device.device_price?.toFixed(2)}
																		</span>
																		{['ata', 'admin', 'manager'].includes(userData?.access_type || '') && (
																			<button
																				onClick={() => {
																					setEditDialogProps({
																						title: 'Editar precio',
																						fields: [
																							{
																								type: 'input',
																								name: 'new_value',
																								label: 'Precio de venta',
																								placeholder: 'Ingrese el precio de venta',
																								defaultValue: device.device_price?.toFixed(2)
																							}
																						],
																						onConfirm: async (values) => {
																							return changeSalePrice(device.id, values?.new_value?.toString() || '');
																						},
																						onClose: () => setEditDialogProps(null)
																					});
																				}}
																				className="hover:text-neutral-300"
																			>
																				<Pencil className="h-4 w-4" />
																			</button>
																		)}
																	</div>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Monto financiado</span>
																	<span className="text-neutral-100">
																		{currency_code}
																		{device.amount_financed?.toFixed(2)}
																	</span>
																</div>
															</div>

															<div className="flex flex-col space-y-1.5 md:space-y-2">
																{device.personal_loan_motive && (
																	<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																		<span className="text-neutral-300">Motivo del préstamo</span>
																		<span className="max-w-[200px] truncate text-neutral-100">{device.personal_loan_motive}</span>
																	</div>
																)}
																{device.payout_method && (
																	<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																		<span className="text-neutral-300">Método de pago</span>
																		<span className="text-neutral-100">
																			{device.payout_method === 'bank_transfer'
																				? 'Transferencia Bancaria'
																				: device.payout_method === 'cash_popular'
																					? 'Retiro Popular'
																					: device.payout_method}
																		</span>
																	</div>
																)}
																{device.wire_reference && (
																	<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																		<span className="text-neutral-300">Referencia de pago</span>
																		<span className="max-w-[200px] truncate text-neutral-100">{device.wire_reference}</span>
																	</div>
																)}
																{device.bank_name && (
																	<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																		<span className="text-neutral-300">Banco</span>
																		<span className="text-neutral-100">{device.bank_name}</span>
																	</div>
																)}
																{device.bank_account_number && (
																	<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																		<span className="text-neutral-300">Número de cuenta</span>
																		<span className="select-all text-neutral-100">{device.bank_account_number}</span>
																	</div>
																)}
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Email del comprador</span>
																	<span className="ml-4 truncate text-neutral-100">{device.user}</span>
																</div>
															</div>
														</div>
														<div className="mt-4 flex flex-wrap gap-3">
															<Button
																onClick={() =>
																	setConfirmDialogProps({
																		title: 'Cambiar fechas de pago',
																		message: '¿Está seguro que desea cambiar las fechas de pago a 15 y último?',
																		onConfirm: async () => changePaymentDate15and30(device.id),
																		onClose: () => setConfirmDialogProps(null)
																	})
																}
																className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-orange-500/10 px-4 py-3 text-sm font-medium text-orange-400 transition-all hover:bg-orange-500/20 hover:text-orange-300"
																Icon={<CalendarSync className="h-4 w-4" />}
															>
																Cambiar fechas
															</Button>
															{['admin', 'manager'].includes(userData?.access_type || '') && (
																<Button
																	onClick={() =>
																		setConfirmDialogProps({
																			title: 'Eliminar préstamo',
																			message: `¿Está seguro que desea eliminar este préstamo de ${currency_code}${device.device_price?.toFixed(2)}? Esta acción no se puede deshacer.`,
																			onConfirm: async () => await deleteLoan(device.id),
																			onClose: () => setConfirmDialogProps(null)
																		})
																	}
																	className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-red-500/10 px-4 py-3 text-sm font-medium text-red-400 transition-all hover:bg-red-500/20 hover:text-red-300"
																	Icon={<Trash className="h-4 w-4" />}
																>
																	Eliminar préstamo
																</Button>
															)}
														</div>
													</div>
												</div>

												<div className="mb-4 border-b border-neutral-800 pb-4">
													<div className="mb-4 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center md:gap-0">
														<div className="flex items-center gap-3">
															<h4 className="font-medium text-neutral-100">Cuotas</h4>
															<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
																{device.payments.length}
															</span>
														</div>{' '}
														{userData?.access_type === 'admin' && (
															<Button
																onClick={() => {
																	setEditDialogProps({
																		title: 'Actualizar monto de cuotas',
																		fields: [
																			{
																				type: 'input',
																				name: 'actual_amount',
																				label: 'Nuevo monto de cuotas',
																				placeholder: 'Ingrese el monto',
																				defaultValue: device.payments?.[0]?.actualAmount?.toFixed(2) || ''
																			}
																		],
																		onConfirm: async (values) => {
																			return updateActualAmount(device.id, values?.actual_amount?.toString() || '');
																		},
																		onClose: () => setEditDialogProps(null)
																	});
																}}
																className="rounded-lg bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-neutral-700"
																Icon={<Pencil className="h-4 w-4" />}
															>
																Editar cuotas
															</Button>
														)}
														{(['admin', 'tecnoplan', 'manager'].includes(userData?.access_type || '') ||
															[
																'ortegayoel967@gmail.com',
																'jesusjns2045@gmail.com',
																'cesar09405@gmail.com',
																'cobranzard@krece.app',
																'christian@krece.app',
																'rodriguezdariamny@gmail.com'
															].includes(userData?.email || '')) && (
															<Button
																onClick={() => {
																	setSplitCuotaDeviceID(device.id);
																}}
																className="rounded-lg bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-neutral-700"
																Icon={<Split className="h-4 w-4 rotate-180" />}
															>
																Dividir cuota
															</Button>
														)}
														<div className="flex w-full flex-col items-start gap-3 rounded-lg bg-neutral-800/50 px-4 py-2 text-sm sm:flex-row sm:items-center md:w-auto">
															<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																<span className="text-neutral-400">Total pagado</span>
																<span className="font-medium text-green-400">
																	{currency_code}
																	{device.payments.reduce((sum, p) => sum + (p.amountPaid || 0), 0).toFixed(2)}
																</span>
															</div>
															<div className="hidden h-6 w-px bg-neutral-700 sm:block"></div>
															<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																<span className="text-neutral-400">Total a pagar</span>
																<span className="font-medium text-neutral-100">
																	{currency_code}
																	{device.payments.reduce((sum, p) => sum + p.actualAmount, 0).toFixed(2)}
																</span>
															</div>
														</div>
													</div>

													<div className="space-y-3">
														{device.payments
															.toSorted((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
															.map((payment) => {
																const isPastDue = new Date(payment.dueDate) < new Date() && !payment.paid;

																return (
																	<div
																		key={payment.id}
																		className="relative rounded-lg border border-neutral-800 bg-neutral-900/30 p-4"
																	>
																		<div className="mb-3 flex flex-col justify-between md:flex-row md:items-start">
																			<div>
																				<div className="mb-1 flex items-center gap-2">
																					<span className="font-medium text-neutral-100">
																						{new Date(payment.dueDate).toLocaleDateString()}
																					</span>
																					<div className="flex gap-1.5">
																						{isPastDue && (
																							<span className="rounded bg-red-500/10 px-2 py-0.5 text-xs text-red-700 dark:bg-red-500/20 dark:text-red-400">
																								Vencida
																							</span>
																						)}
																						<span
																							className={`rounded px-2 py-0.5 text-xs ${
																								payment.paid
																									? 'bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-400'
																									: 'bg-neutral-500/10 text-neutral-700 dark:bg-neutral-500/20 dark:text-neutral-400'
																							}`}
																						>
																							{payment.paid ? 'Pagado' : 'Pendiente'}
																						</span>
																					</div>
																				</div>
																				<div className="text-xs text-neutral-400">
																					ID: <span className="select-all font-mono">{payment.id}</span>
																					<Copy
																						onClick={() => navigator.clipboard.writeText(payment.id)}
																						className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
																					/>
																					{payment.confirmationNumber && (
																						<>
																							{' '}
																							• Conf: <span className="select-all font-mono">{payment.confirmationNumber}</span>
																						</>
																					)}
																				</div>
																			</div>

																			<div className="mt-3 text-left md:mt-0 md:text-right">
																				<div className="mb-1 flex items-baseline gap-2">
																					<span className="text-sm text-neutral-400">Monto:</span>
																					<span className="font-medium text-neutral-100">
																						{currency_code}
																						{payment.actualAmount?.toFixed(2)}
																					</span>
																					<span className="text-neutral-400">•</span>
																					<span className="text-sm text-neutral-400">Pendiente:</span>
																					<span className="font-medium text-neutral-100">
																						{currency_code}
																						{(payment.actualAmount - payment.amountPaid)?.toFixed(2)}
																					</span>
																				</div>
																				<div className="flex items-baseline gap-2 text-sm">
																					<span className="text-neutral-400">Pagado:</span>
																					<span className={payment.amountPaid ? 'text-green-400' : 'text-neutral-500'}>
																						{currency_code}
																						{payment.amountPaid?.toFixed(2) || 0}
																					</span>
																					{userData?.access_type === 'admin' && (
																						<button
																							onClick={() => {
																								setEditDialogProps({
																									title: 'Editar monto',
																									fields: [
																										{
																											type: 'input',
																											name: 'amount_paid',
																											label: 'Monto pagado',
																											placeholder: 'Ingrese el monto pagado',
																											defaultValue: (payment?.amountPaid || 0).toFixed(2)
																										}
																									],
																									onConfirm: async (values) => {
																										return updateAmountPaid(payment.id, values?.amount_paid?.toString() || '');
																									},
																									onClose: () => setEditDialogProps(null)
																								});
																							}}
																							className="relative right-[2px] top-[2px] hover:text-neutral-300"
																						>
																							<Pencil className="h-4 w-4" />
																						</button>
																					)}
																					<span className="text-neutral-400">•</span>
																					<span className="text-neutral-400">Confirmado:</span>
																					<span className={payment.amount_confirmed ? 'text-blue-400' : 'text-neutral-500'}>
																						{currency_code}
																						{payment.amount_confirmed?.toFixed(2) || 0}
																					</span>
																				</div>
																			</div>
																		</div>

																		<div className="flex items-center gap-2 text-xs">
																			<span className="text-neutral-400">Estado:</span>
																			<span className={`${payment.is_active ? 'text-blue-400' : 'text-neutral-500'}`}>
																				{payment.is_active ? 'Activo' : 'Inactivo'}
																			</span>
																			<span className="text-neutral-400">•</span>
																			<span className="text-neutral-400">Revisado por Krece:</span>
																			<span
																				className={`${payment.checked_by_internal_team ? 'text-green-400' : 'text-neutral-500'}`}
																			>
																				{payment.checked_by_internal_team ? 'Sí' : 'No'}
																			</span>
																		</div>
																	</div>
																);
															})}
													</div>
												</div>
												<details className="group/transactions">
													<summary className="cursor-pointer list-none">
														<div className="mb-4 flex flex-col justify-between gap-3 md:flex-row md:items-center md:gap-0">
															<div className="flex items-center gap-2">
																<svg
																	className="h-5 w-5 text-neutral-400 transition-transform group-open/transactions:rotate-90"
																	fill="none"
																	viewBox="0 0 24 24"
																	stroke="currentColor"
																>
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
																</svg>
																<div className="flex items-center gap-3">
																	<h4 className="font-medium text-neutral-100">Transacciones</h4>
																	<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
																		{device.transactions.length}
																	</span>
																</div>{' '}
															</div>
															<div className="flex w-full flex-col items-start gap-3 rounded-lg bg-neutral-800/50 px-4 py-2 text-sm sm:flex-row sm:items-center md:w-auto">
																<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																	<span className="text-neutral-400">Total transacciones</span>
																	<span className="font-medium text-neutral-100">
																		{currency_code}
																		{device.transactions.reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
																	</span>
																</div>
															</div>
														</div>
													</summary>

													<div className="space-y-3">
														{device.transactions
															.toSorted((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
															.map((transaction) => (
																<div
																	key={transaction.id}
																	className="relative rounded-lg border border-neutral-800 bg-neutral-900/30 p-4"
																>
																	<div className="mb-3 flex flex-col justify-between md:flex-row md:items-start">
																		<div>
																			<div className="mb-1 flex items-center gap-2">
																				<span className="font-medium text-neutral-100">
																					{new Date(transaction.date).toLocaleDateString()}
																				</span>
																				<span
																					className={`rounded bg-blue-500/10 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-500/20 dark:text-blue-400`}
																				>
																					Pagado a {transaction.paid_to}
																				</span>
																			</div>
																			<div className="text-xs text-neutral-400">
																				ID: <span className="select-all font-mono">{transaction.id}</span>
																				<Copy
																					onClick={() => navigator.clipboard.writeText(transaction.id)}
																					className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
																				/>
																				{transaction.related_payment && (
																					<>
																						{' '}
																						• Cuota: <span className="select-all font-mono">{transaction.related_payment}</span>
																					</>
																				)}
																			</div>
																		</div>

																		<div className="mt-3 text-left md:mt-0 md:text-right">
																			<div className="mb-1 flex items-baseline gap-2">
																				<span className="text-sm text-neutral-400">Monto:</span>
																				<span className="font-medium text-neutral-100">
																					{currency_code}
																					{transaction.amount?.toFixed(2)}
																				</span>
																			</div>
																		</div>
																	</div>

																	<div className="flex items-center gap-2 text-xs">
																		<span className="text-neutral-400">Ref. Pago:</span>
																		<span className="select-all font-mono text-neutral-100">{transaction.reference || '-'}</span>
																		<span className="text-neutral-400">•</span>
																		<span className="text-neutral-400">Ref. Cash:</span>
																		<span className="select-all font-mono text-neutral-100">
																			{transaction.confirmation_number || '-'}
																		</span>
																	</div>

																	{['admin', 'manager'].includes(userData?.access_type || '') && (
																		<div className="absolute right-3 top-3 flex gap-2 md:bottom-3 md:top-auto">
																			<button
																				onClick={() => {
																					setEditDialogProps({
																						title: 'Reembolsar transacción',
																						fields: [
																							{
																								type: 'input',
																								name: 'id_document',
																								label: 'Cédula o DNI',
																								placeholder: 'Ingrese la cédula o DNI',
																								defaultValue: ''
																							},
																							{
																								type: 'input',
																								name: 'account_number',
																								label: 'Número de cuenta',
																								placeholder: 'Ingrese el número de cuenta',
																								defaultValue: ''
																							},
																							{
																								type: 'input',
																								name: 'refund_amount',
																								label: 'Monto a reembolsar',
																								placeholder: 'Ingrese el monto',
																								defaultValue: transaction.amount?.toFixed(2) || '',
																								allowedChars: 'numeric'
																							}
																						],
																						onConfirm: async (values) => {
																							return refundTransaction(
																								transaction.id,
																								String(values?.id_document || ''),
																								String(values?.account_number || ''),
																								parseFloat(String(values?.refund_amount || '0'))
																							);
																						},
																						onClose: () => setEditDialogProps(null)
																					});
																				}}
																				className="rounded-lg bg-blue-500/20 p-1 hover:bg-blue-500/30"
																			>
																				<BanknoteArrowDown className="size-5 text-blue-500" />
																			</button>
																			<button
																				onClick={() => {
																					setConfirmDialogProps({
																						title: 'Eliminar transacción',
																						message: `¿Estás seguro de que deseas eliminar esta transacción de ${currency_code}${transaction.amount?.toFixed(2)}?`,
																						onConfirm: async () => await deleteTransaction(transaction.id),
																						onClose: () => setConfirmDialogProps(null)
																					});
																				}}
																				className="rounded-lg bg-red-500/20 p-1 hover:bg-red-500/30"
																			>
																				<Trash className="size-5 text-red-500" />
																			</button>
																		</div>
																	)}
																</div>
															))}
													</div>
												</details>
											</div>
										</details>
									))}
								{hasMore.loan && (
									<div className="mt-3 flex justify-center">
										<Button
											onClick={() => loadMoreDevices('loan')}
											disabled={loadingMore === 'loan'}
											variant="outline"
											className="border-neutral-700 text-sm text-neutral-400"
										>
											{loadingMore === 'loan' ? <Spinner size={1} /> : 'Cargar más'}
										</Button>
									</div>
								)}
							</div>
						) : (
							<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-center text-sm text-neutral-300">
								No se encontraron otros productos o servicios
							</div>
						)}
					</div>

					<div className="mt-6 space-y-4">
						<div>
							<h2 className="text-2xl font-semibold text-neutral-100">Otros</h2>
							<p className="text-sm text-neutral-400">Otros productos</p>
						</div>
						{clientData?.devices?.filter((device) => ['other'].includes(device?.device_type?.toLowerCase()))?.length > 0 ? (
							<div className="mt-6 grid gap-3">
								{clientData.devices
									.filter((device) => ['other'].includes(device?.device_type?.toLowerCase()))
									.toSorted((a, b) => new Date(b.date_bought).getTime() - new Date(a.date_bought).getTime())
									.map((device) => (
										<details key={device.id} className="group rounded-lg border border-neutral-800 bg-neutral-900/50">
											<summary className="cursor-pointer list-none p-6">
												<div className="no-scrollbar flex items-center justify-between gap-6 overflow-x-auto">
													<div className="flex shrink-0 items-center gap-2">
														<div className="flex items-center gap-2">
															<svg
																className="h-5 w-5 text-neutral-400 transition-transform group-open:rotate-90"
																fill="none"
																viewBox="0 0 24 24"
																stroke="currentColor"
															>
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
															</svg>
															<h3 className="flex items-center gap-1 text-xl font-semibold text-neutral-100">
																<span>{device.device_commercial_name}</span>
																<button
																	onClick={() => {
																		setEditDialogProps({
																			title: 'Editar nombre comercial',
																			fields: [
																				{
																					type: 'input',
																					name: 'new_value',
																					label: 'Nombre comercial',
																					placeholder: 'Ingrese',
																					defaultValue:
																						device.device_commercial_name ||
																						`${device.device_manufacturer} ${device.device_model}`
																				}
																			],
																			onConfirm: async (values) => {
																				return changeCommercialName(device.id, values?.new_value?.toString() || '');
																			},
																			onClose: () => setEditDialogProps(null)
																		});
																	}}
																	className="hover:text-neutral-300"
																>
																	<Pencil className="h-4 w-4" />
																</button>
																{renderDeletedDeviceBadge(device)}
															</h3>
														</div>
													</div>
													<div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
														<span
															className={`rounded bg-neutral-500/10 px-2 py-1 text-xs text-neutral-700 dark:bg-neutral-500/20 dark:text-neutral-400`}
														>
															{device.seller_store}
														</span>
														<span className="text-sm text-neutral-400">{new Date(device.date_bought).toLocaleDateString()}</span>
													</div>
												</div>
											</summary>

											<div className="px-6 pb-6">
												<div className="mb-4 border-b border-neutral-800 pb-4">
													<div className="mt-4 space-y-1.5 text-sm md:space-y-4">
														<div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 md:gap-4">
															<div className="rounded bg-neutral-800/30 p-3">
																<p className="mb-1 text-xs font-medium text-neutral-300">ID de producto</p>
																<div className="flex items-center">
																	<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																		{device.nuovopay_device_id}
																	</p>
																	<Copy
																		onClick={() => navigator.clipboard.writeText(String(device.nuovopay_device_id))}
																		className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																	/>
																</div>
															</div>
															<div className="rounded bg-neutral-800/30 p-3">
																<p className="mb-1 text-xs font-medium text-neutral-300">ID</p>
																<div className="flex items-center">
																	<p className="w-fit select-all rounded bg-neutral-925 px-1 font-mono text-neutral-100">
																		{device.id}
																	</p>
																	<Copy
																		onClick={() => navigator.clipboard.writeText(device.id)}
																		className="ml-1.5 size-4 cursor-pointer text-xs text-neutral-400 transition-colors duration-200 hover:text-neutral-200"
																	/>
																</div>
															</div>
														</div>
														<div className="grid grid-cols-1 gap-1.5 md:grid-cols-2 md:gap-4">
															<div className="flex flex-col space-y-1.5 md:space-y-2">
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Fecha de compra</span>
																	<span className="text-neutral-100">{new Date(device.date_bought).toLocaleDateString()}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Precio</span>
																	<div className="flex items-center gap-1">
																		<span className="text-neutral-100">
																			{currency_code}
																			{device.device_price?.toFixed(2)}
																		</span>
																		{['ata', 'admin', 'manager'].includes(userData?.access_type || '') && (
																			<button
																				onClick={() => {
																					setEditDialogProps({
																						title: 'Editar precio',
																						fields: [
																							{
																								type: 'input',
																								name: 'new_value',
																								label: 'Precio de venta',
																								placeholder: 'Ingrese el precio de venta',
																								defaultValue: device.device_price?.toFixed(2)
																							}
																						],
																						onConfirm: async (values) => {
																							return changeSalePrice(device.id, values?.new_value?.toString() || '');
																						},
																						onClose: () => setEditDialogProps(null)
																					});
																				}}
																				className="hover:text-neutral-300"
																			>
																				<Pencil className="h-4 w-4" />
																			</button>
																		)}
																	</div>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Monto financiado</span>
																	<span className="text-neutral-100">
																		{currency_code}
																		{device.amount_financed?.toFixed(2)}
																	</span>
																</div>
															</div>

															<div className="flex flex-col space-y-1.5 md:space-y-2">
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Tienda</span>
																	<span className="text-neutral-100">{device.seller_store}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Local</span>
																	<span className="text-neutral-100">{device.sub_store}</span>
																</div>
																<div className="flex justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="text-neutral-300">Email del comprador</span>
																	<span className="ml-4 truncate text-neutral-100">{device.user}</span>
																</div>
															</div>
														</div>
														<div className="mt-4 flex flex-wrap gap-3">
															<Button
																onClick={() =>
																	setConfirmDialogProps({
																		title: 'Cambiar fechas de pago',
																		message: '¿Está seguro que desea cambiar las fechas de pago a 15 y último?',
																		onConfirm: async () => changePaymentDate15and30(device.id),
																		onClose: () => setConfirmDialogProps(null)
																	})
																}
																className="flex max-w-max flex-grow items-center justify-center gap-2 rounded-lg bg-orange-500/10 px-4 py-3 text-sm font-medium text-orange-400 transition-all hover:bg-orange-500/20 hover:text-orange-300"
																Icon={<CalendarSync className="h-4 w-4" />}
															>
																Cambiar fechas
															</Button>
														</div>
													</div>
												</div>

												<div className="mb-4 border-b border-neutral-800 pb-4">
													<div className="mb-4 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center md:gap-0">
														<div className="flex items-center gap-3">
															<h4 className="font-medium text-neutral-100">Cuotas</h4>
															<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
																{device.payments.length}
															</span>
														</div>{' '}
														{userData?.access_type === 'admin' && (
															<Button
																onClick={() => {
																	setEditDialogProps({
																		title: 'Actualizar monto de cuotas',
																		fields: [
																			{
																				type: 'input',
																				name: 'actual_amount',
																				label: 'Nuevo monto de cuotas',
																				placeholder: 'Ingrese el monto',
																				defaultValue: device.payments?.[0]?.actualAmount?.toFixed(2) || ''
																			}
																		],
																		onConfirm: async (values) => {
																			return updateActualAmount(device.id, values?.actual_amount?.toString() || '');
																		},
																		onClose: () => setEditDialogProps(null)
																	});
																}}
																className="rounded-lg bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-neutral-700"
																Icon={<Pencil className="h-4 w-4" />}
															>
																Editar cuotas
															</Button>
														)}
														{(['admin', 'tecnoplan', 'manager'].includes(userData?.access_type || '') ||
															[
																'ortegayoel967@gmail.com',
																'jesusjns2045@gmail.com',
																'cesar09405@gmail.com',
																'cobranzard@krece.app',
																'christian@krece.app',
																'rodriguezdariamny@gmail.com'
															].includes(userData?.email || '')) && (
															<Button
																onClick={() => {
																	setSplitCuotaDeviceID(device.id);
																}}
																className="rounded-lg bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-300 transition-colors hover:bg-neutral-700"
																Icon={<Split className="h-4 w-4 rotate-180" />}
															>
																Dividir cuota
															</Button>
														)}
														<div className="flex w-full flex-col items-start gap-3 rounded-lg bg-neutral-800/50 px-4 py-2 text-sm sm:flex-row sm:items-center md:w-auto">
															<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																<span className="text-neutral-400">Total pagado</span>
																<span className="font-medium text-green-400">
																	{currency_code}
																	{device.payments.reduce((sum, p) => sum + (p.amountPaid || 0), 0).toFixed(2)}
																</span>
															</div>
															<div className="hidden h-6 w-px bg-neutral-700 sm:block"></div>
															<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																<span className="text-neutral-400">Total a pagar</span>
																<span className="font-medium text-neutral-100">
																	{currency_code}
																	{device.payments.reduce((sum, p) => sum + p.actualAmount, 0).toFixed(2)}
																</span>
															</div>
														</div>
													</div>

													<div className="space-y-3">
														{device.payments
															.toSorted((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
															.map((payment) => {
																const isPastDue = new Date(payment.dueDate) < new Date() && !payment.paid;

																return (
																	<div
																		key={payment.id}
																		className="relative rounded-lg border border-neutral-800 bg-neutral-900/30 p-4"
																	>
																		<div className="mb-3 flex flex-col justify-between md:flex-row md:items-start">
																			<div>
																				<div className="mb-1 flex items-center gap-2">
																					<span className="font-medium text-neutral-100">
																						{new Date(payment.dueDate).toLocaleDateString()}
																					</span>
																					<div className="flex gap-1.5">
																						{isPastDue && (
																							<span className="rounded bg-red-500/10 px-2 py-0.5 text-xs text-red-700 dark:bg-red-500/20 dark:text-red-400">
																								Vencida
																							</span>
																						)}
																						<span
																							className={`rounded px-2 py-0.5 text-xs ${
																								payment.paid
																									? 'bg-green-500/10 text-green-700 dark:bg-green-500/20 dark:text-green-400'
																									: 'bg-neutral-500/10 text-neutral-700 dark:bg-neutral-500/20 dark:text-neutral-400'
																							}`}
																						>
																							{payment.paid ? 'Pagado' : 'Pendiente'}
																						</span>
																					</div>
																				</div>
																				<div className="text-xs text-neutral-400">
																					ID: <span className="select-all font-mono">{payment.id}</span>
																					<Copy
																						onClick={() => navigator.clipboard.writeText(payment.id)}
																						className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
																					/>
																					{payment.confirmationNumber && (
																						<>
																							{' '}
																							• Conf: <span className="select-all font-mono">{payment.confirmationNumber}</span>
																						</>
																					)}
																				</div>
																			</div>

																			<div className="mt-3 text-left md:mt-0 md:text-right">
																				<div className="mb-1 flex items-baseline gap-2">
																					<span className="text-sm text-neutral-400">Monto:</span>
																					<span className="font-medium text-neutral-100">
																						{currency_code}
																						{payment.actualAmount?.toFixed(2)}
																					</span>
																					<span className="text-neutral-400">•</span>
																					<span className="text-sm text-neutral-400">Pendiente:</span>
																					<span className="font-medium text-neutral-100">
																						{currency_code}
																						{(payment.actualAmount - payment.amountPaid)?.toFixed(2)}
																					</span>
																				</div>
																				<div className="flex items-baseline gap-2 text-sm">
																					<span className="text-neutral-400">Pagado:</span>
																					<span className={payment.amountPaid ? 'text-green-400' : 'text-neutral-500'}>
																						{currency_code}
																						{payment.amountPaid?.toFixed(2) || 0}
																					</span>
																					{userData?.access_type === 'admin' && (
																						<button
																							onClick={() => {
																								setEditDialogProps({
																									title: 'Editar monto',
																									fields: [
																										{
																											type: 'input',
																											name: 'amount_paid',
																											label: 'Monto pagado',
																											placeholder: 'Ingrese el monto pagado',
																											defaultValue: (payment?.amountPaid || 0).toFixed(2)
																										}
																									],
																									onConfirm: async (values) => {
																										return updateAmountPaid(payment.id, values?.amount_paid?.toString() || '');
																									},
																									onClose: () => setEditDialogProps(null)
																								});
																							}}
																							className="relative right-[2px] top-[2px] hover:text-neutral-300"
																						>
																							<Pencil className="h-4 w-4" />
																						</button>
																					)}
																					<span className="text-neutral-400">•</span>
																					<span className="text-neutral-400">Confirmado:</span>
																					<span className={payment.amount_confirmed ? 'text-blue-400' : 'text-neutral-500'}>
																						{currency_code}
																						{payment.amount_confirmed?.toFixed(2) || 0}
																					</span>
																				</div>
																			</div>
																		</div>

																		<div className="flex items-center gap-2 text-xs">
																			<span className="text-neutral-400">Estado:</span>
																			<span className={`${payment.is_active ? 'text-blue-400' : 'text-neutral-500'}`}>
																				{payment.is_active ? 'Activo' : 'Inactivo'}
																			</span>
																			<span className="text-neutral-400">•</span>
																			<span className="text-neutral-400">Revisado por Krece:</span>
																			<span
																				className={`${payment.checked_by_internal_team ? 'text-green-400' : 'text-neutral-500'}`}
																			>
																				{payment.checked_by_internal_team ? 'Sí' : 'No'}
																			</span>
																		</div>
																	</div>
																);
															})}
													</div>
												</div>
												<details className="group/transactions">
													<summary className="cursor-pointer list-none">
														<div className="mb-4 flex flex-col justify-between gap-3 md:flex-row md:items-center md:gap-0">
															<div className="flex items-center gap-2">
																<svg
																	className="h-5 w-5 text-neutral-400 transition-transform group-open/transactions:rotate-90"
																	fill="none"
																	viewBox="0 0 24 24"
																	stroke="currentColor"
																>
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
																</svg>
																<div className="flex items-center gap-3">
																	<h4 className="font-medium text-neutral-100">Transacciones</h4>
																	<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
																		{device.transactions.length}
																	</span>
																</div>{' '}
															</div>
															<div className="flex w-full flex-col items-start gap-3 rounded-lg bg-neutral-800/50 px-4 py-2 text-sm sm:flex-row sm:items-center md:w-auto">
																<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
																	<span className="text-neutral-400">Total transacciones</span>
																	<span className="font-medium text-neutral-100">
																		{currency_code}
																		{device.transactions.reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
																	</span>
																</div>
															</div>
														</div>
													</summary>

													<div className="space-y-3">
														{device.transactions
															.toSorted((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
															.map((transaction) => (
																<div
																	key={transaction.id}
																	className="relative rounded-lg border border-neutral-800 bg-neutral-900/30 p-4"
																>
																	<div className="mb-3 flex flex-col justify-between md:flex-row md:items-start">
																		<div>
																			<div className="mb-1 flex items-center gap-2">
																				<span className="font-medium text-neutral-100">
																					{new Date(transaction.date).toLocaleDateString()}
																				</span>
																				<span
																					className={`rounded bg-blue-500/10 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-500/20 dark:text-blue-400`}
																				>
																					Pagado a {transaction.paid_to}
																				</span>
																			</div>
																			<div className="text-xs text-neutral-400">
																				ID: <span className="select-all font-mono">{transaction.id}</span>
																				<Copy
																					onClick={() => navigator.clipboard.writeText(transaction.id)}
																					className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
																				/>
																				{transaction.related_payment && (
																					<>
																						{' '}
																						• Cuota: <span className="select-all font-mono">{transaction.related_payment}</span>
																					</>
																				)}
																			</div>
																		</div>

																		<div className="mt-3 text-left md:mt-0 md:text-right">
																			<div className="mb-1 flex items-baseline gap-2">
																				<span className="text-sm text-neutral-400">Monto:</span>
																				<span className="font-medium text-neutral-100">
																					{currency_code}
																					{transaction.amount?.toFixed(2)}
																				</span>
																			</div>
																		</div>
																	</div>

																	<div className="flex items-center gap-2 text-xs">
																		<span className="text-neutral-400">Ref. Pago:</span>
																		<span className="select-all font-mono text-neutral-100">{transaction.reference || '-'}</span>
																		<span className="text-neutral-400">•</span>
																		<span className="text-neutral-400">Ref. Cash:</span>
																		<span className="select-all font-mono text-neutral-100">
																			{transaction.confirmation_number || '-'}
																		</span>
																	</div>

																	{['admin', 'manager'].includes(userData?.access_type || '') && (
																		<div className="absolute right-3 top-3 flex gap-2 md:bottom-3 md:top-auto">
																			<button
																				onClick={() => {
																					setEditDialogProps({
																						title: 'Reembolsar transacción',
																						fields: [
																							{
																								type: 'input',
																								name: 'id_document',
																								label: 'Cédula o DNI',
																								placeholder: 'Ingrese la cédula o DNI',
																								defaultValue: ''
																							},
																							{
																								type: 'input',
																								name: 'account_number',
																								label: 'Número de cuenta',
																								placeholder: 'Ingrese el número de cuenta',
																								defaultValue: ''
																							},
																							{
																								type: 'input',
																								name: 'refund_amount',
																								label: 'Monto a reembolsar',
																								placeholder: 'Ingrese el monto',
																								defaultValue: transaction.amount?.toFixed(2) || '',
																								allowedChars: 'numeric'
																							}
																						],
																						onConfirm: async (values) => {
																							return refundTransaction(
																								transaction.id,
																								String(values?.id_document || ''),
																								String(values?.account_number || ''),
																								parseFloat(String(values?.refund_amount || '0'))
																							);
																						},
																						onClose: () => setEditDialogProps(null)
																					});
																				}}
																				className="rounded-lg bg-blue-500/20 p-1 hover:bg-blue-500/30"
																			>
																				<BanknoteArrowDown className="size-5 text-blue-500" />
																			</button>
																			<button
																				onClick={() => {
																					setConfirmDialogProps({
																						title: 'Eliminar transacción',
																						message: `¿Estás seguro de que deseas eliminar esta transacción de ${currency_code}${transaction.amount?.toFixed(2)}?`,
																						onConfirm: async () => await deleteTransaction(transaction.id),
																						onClose: () => setConfirmDialogProps(null)
																					});
																				}}
																				className="rounded-lg bg-red-500/20 p-1 hover:bg-red-500/30"
																			>
																				<Trash className="size-5 text-red-500" />
																			</button>
																		</div>
																	)}
																</div>
															))}
													</div>
												</details>
											</div>
										</details>
									))}
								{hasMore.other && (
									<div className="mt-3 flex justify-center">
										<Button
											onClick={() => loadMoreDevices('other')}
											disabled={loadingMore === 'other'}
											variant="outline"
											className="border-neutral-700 text-sm text-neutral-400"
										>
											{loadingMore === 'other' ? <Spinner size={1} /> : 'Cargar más'}
										</Button>
									</div>
								)}
							</div>
						) : (
							<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-center text-sm text-neutral-300">
								No se encontraron otros productos o servicios
							</div>
						)}
					</div>

						{unrelatedTransactions.length > 0 && (
						<div className="mt-6">
							<details className="group/unrelated">
								<summary className="cursor-pointer list-none">
									<div className="mb-4 flex flex-col justify-between gap-3 md:flex-row md:items-center md:gap-0">
										<div className="flex items-center gap-2">
											<svg
												className="h-5 w-5 text-neutral-400 transition-transform group-open/unrelated:rotate-90"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
											>
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
											</svg>
											<div className="flex items-center gap-3">
												<h2 className="text-2xl font-semibold text-neutral-100">Transacciones sin correspondiente</h2>
												<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
													{unrelatedTransactions.length}
												</span>
											</div>
										</div>
										<div className="flex w-full flex-col items-start gap-3 rounded-lg bg-neutral-800/50 px-4 py-2 text-sm sm:flex-row sm:items-center md:w-auto">
											<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
												<span className="text-neutral-400">Total transacciones</span>
												<span className="font-medium text-neutral-100">
													{currency_code}
													{unrelatedTransactions.reduce((sum, t) => sum + (t.amount || 0), 0).toFixed(2)}
												</span>
											</div>
										</div>
									</div>
								</summary>

								<div className="space-y-3">
									{unrelatedTransactions.map((transaction) => (
										<div
											key={transaction.id}
											className="relative rounded-lg border border-neutral-800 bg-neutral-900/30 p-4"
										>
											<div className="mb-3 flex flex-col justify-between md:flex-row md:items-start">
												<div>
													<div className="mb-1 flex items-center gap-2">
														<span className="font-medium text-neutral-100">
															{new Date(transaction.date).toLocaleDateString()}
														</span>
														<span
															className="rounded bg-blue-500/10 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
														>
															Pagado a {transaction.paid_to}
														</span>
													</div>
													<div className="text-xs text-neutral-400">
														ID: <span className="select-all font-mono">{transaction.id}</span>
														<Copy
															onClick={() => navigator.clipboard.writeText(transaction.id)}
															className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
														/>
														{transaction.related_payment && (
															<>
																{' '}
																• Cuota: <span className="select-all font-mono">{transaction.related_payment}</span>
															</>
														)}
														<>
															{' '}
															• Email: <span className="select-all font-mono">{transaction.user}</span>
														</>
													</div>
												</div>

												<div className="mt-3 text-left md:mt-0 md:text-right">
													<div className="mb-1 flex items-baseline gap-2">
														<span className="text-sm text-neutral-400">Monto:</span>
														<span className="font-medium text-neutral-100">
															{currency_code}
															{transaction.amount?.toFixed(2)}
														</span>
													</div>
												</div>
											</div>

											<div className="flex items-center gap-2 text-xs">
												<span className="text-neutral-400">Ref. Pago:</span>
												<span className="select-all font-mono text-neutral-100">{transaction.reference || '-'}</span>
												<span className="text-neutral-400">•</span>
												<span className="text-neutral-400">Ref. Cash:</span>
												<span className="select-all font-mono text-neutral-100">
													{transaction.confirmation_number || '-'}
												</span>
											</div>

											{['admin', 'manager'].includes(userData?.access_type || '') && (
												<div className="absolute right-3 top-3 flex gap-2 md:bottom-3 md:top-auto">
													<button
														onClick={() => {
															setConfirmDialogProps({
																title: 'Eliminar transacción',
																message: `¿Estás seguro de que deseas eliminar esta transacción de ${transaction.amount?.toFixed(2)}?`,
																onConfirm: async () => await deleteTransaction(transaction.id),
																onClose: () => setConfirmDialogProps(null)
															});
														}}
														className="rounded-lg bg-red-500/20 p-1 hover:bg-red-500/30"
													>
														<Trash className="size-5 text-red-500" />
													</button>
												</div>
											)}
										</div>
									))}
								</div>
							</details>
						</div>
					)}
				</>
			) : identificationType === 'reference' && unrelatedTransactions.length > 0 ? (
				<div className="mt-6">
					<details className="group/unrelated">
						<summary className="cursor-pointer list-none">
							<div className="mb-4 flex flex-col justify-between gap-3 md:flex-row md:items-center md:gap-0">
								<div className="flex items-center gap-2">
									<svg
										className="h-5 w-5 text-neutral-400 transition-transform group-open/unrelated:rotate-90"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
									</svg>
									<div className="flex items-center gap-3">
										<h2 className="text-2xl font-semibold text-neutral-100">Transacciones sin correspondiente</h2>
										<span className="rounded-md bg-neutral-800/50 px-2 py-1 text-base font-medium text-neutral-300">
											{unrelatedTransactions.length}
										</span>
									</div>
								</div>
								<div className="flex w-full flex-col items-start gap-3 rounded-lg bg-neutral-800/50 px-4 py-2 text-sm sm:flex-row sm:items-center md:w-auto">
									<div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
										<span className="text-neutral-400">Total transacciones</span>
										<span className="font-medium text-neutral-100">
											{currency_code}
											{unrelatedTransactions.reduce((sum, t) => sum + (t.amount || 0), 0).toFixed(2)}
										</span>
									</div>
								</div>
							</div>
						</summary>

						<div className="space-y-3">
							{unrelatedTransactions.map((transaction) => (
								<div
									key={transaction.id}
									className="relative rounded-lg border border-neutral-800 bg-neutral-900/30 p-4"
								>
									<div className="mb-3 flex flex-col justify-between md:flex-row md:items-start">
										<div>
											<div className="mb-1 flex items-center gap-2">
												<span className="font-medium text-neutral-100">
													{new Date(transaction.date).toLocaleDateString()}
												</span>
												<span
													className="rounded bg-blue-500/10 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
												>
													Pagado a {transaction.paid_to}
												</span>
											</div>
											<div className="text-xs text-neutral-400">
												{transaction.user && (
													<>
														Email: <span className="select-all font-mono">{transaction.user}</span>
														<Copy
															onClick={() => navigator.clipboard.writeText(transaction.user)}
															className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
														/>
														{' • '}
													</>
												)}
												ID: <span className="select-all font-mono">{transaction.id}</span>
												<Copy
													onClick={() => navigator.clipboard.writeText(transaction.id)}
													className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
												/>
												{transaction.related_payment && (
													<>
														{' '}
														• Cuota: <span className="select-all font-mono">{transaction.related_payment}</span>
														<Copy
															onClick={() => navigator.clipboard.writeText(transaction.related_payment!)}
															className="ml-1 inline size-3 cursor-pointer text-xs text-neutral-300 transition-colors duration-200 hover:text-neutral-200"
														/>
													</>
												)}
											</div>
										</div>

										<div className="mt-3 text-left md:mt-0 md:text-right">
											<div className="mb-1 flex items-baseline gap-2">
												<span className="text-sm text-neutral-400">Monto:</span>
												<span className="font-medium text-neutral-100">
													{currency_code}
													{transaction.amount?.toFixed(2)}
												</span>
											</div>
										</div>
									</div>

									<div className="flex items-center gap-2 text-xs">
										<span className="text-neutral-400">Ref. Pago:</span>
										<span className="select-all font-mono text-neutral-100">{transaction.reference || '-'}</span>
										<span className="text-neutral-400">•</span>
										<span className="text-neutral-400">Ref. Cash:</span>
										<span className="select-all font-mono text-neutral-100">{transaction.confirmation_number || '-'}</span>
									</div>

									{['admin', 'manager'].includes(userData?.access_type || '') && (
										<div className="absolute right-3 top-3 flex gap-2 md:bottom-3 md:top-auto">
											<button
												onClick={() => {
													setEditDialogProps({
														title: 'Reembolsar transacción',
														fields: [
															{
																type: 'input',
																name: 'id_document',
																label: 'Cédula o DNI',
																placeholder: 'Ingrese la cédula o DNI',
																defaultValue: ''
															},
															{
																type: 'input',
																name: 'account_number',
																label: 'Número de cuenta',
																placeholder: 'Ingrese el número de cuenta',
																defaultValue: ''
															},
															{
																type: 'input',
																name: 'refund_amount',
																label: 'Monto a reembolsar',
																placeholder: 'Ingrese el monto',
																defaultValue: transaction.amount?.toFixed(2) || '',
																allowedChars: 'numeric'
															}
														],
														onConfirm: async (values) => {
															return refundTransaction(
																transaction.id,
																String(values?.id_document || ''),
																String(values?.account_number || ''),
																parseFloat(String(values?.refund_amount || '0'))
															);
														},
														onClose: () => setEditDialogProps(null)
													});
												}}
												className="rounded-lg bg-blue-500/20 p-1 hover:bg-blue-500/30"
											>
												<BanknoteArrowDown className="size-5 text-blue-500" />
											</button>
											<button
												onClick={() => {
													setConfirmDialogProps({
														title: 'Eliminar transacción',
														message: `¿Estás seguro de que deseas eliminar esta transacción de ${currency_code}${transaction.amount?.toFixed(2)}?`,
														onConfirm: async () => await deleteTransaction(transaction.id),
														onClose: () => setConfirmDialogProps(null)
													});
												}}
												className="rounded-lg bg-red-500/20 p-1 hover:bg-red-500/30"
											>
												<Trash className="size-5 text-red-500" />
											</button>
										</div>
									)}
								</div>
							))}
						</div>
					</details>
				</div>
			) : (
				clientData === null &&
				!loading && (
					<div className="mt-6 rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-center text-sm text-neutral-300">
						No se encontró información
					</div>
				)
			)}
		</div>
	);
}
